import React, { useState, useEffect, useRef } from 'react';
import { 
  Heart, MessageCircle, User, Plus, Search, Sparkles, X, Settings, BadgeCheck, 
  ChevronLeft, MapPin, Send, LogOut, Home, UploadCloud, Hash, Wand2, Loader2, 
  MessageSquare, CheckCircle2, ChevronRight, LayoutDashboard, ShieldCheck, 
  PlaySquare, Flag, ShieldPlus, UserCheck, Eye, EyeOff, Bookmark, Lock, Ban, 
  UserMinus, UserPlus, Megaphone, Trash2, Volume2, VolumeX, CheckSquare, Square, FileText
} from 'lucide-react';

// 导入我们刚才创建的组件库
import {
  DEFAULT_COVER, DEFAULT_AVATAR, OFFICIAL_ADMIN_PHONE,
  isSuperAdminProfile, isAdminProfile,
  hasUxHintSeen, markUxHintSeen, UX_HINT_KEYS,
  privacyPolicyContent, userAgreementContent, platformConventionContent,
  needsConventionAcceptance, saveConventionAcceptance, clearConventionSessionAcceptance,
  PLATFORM_CONVENTION_VERSION,
  ConfirmModal, LegalDocument, AuthModal, PublishModal, DraftBoxPanel,
  ReportModal, FeedbackModal, AdminModal, SearchModal,
  EmailNotificationModal, SettingsModal, CommentModal, AdoptChatModal,
  PlatformConventionAcceptModal, SiteMaintenanceScreen, SiteLegalFooter,
  WorkInteractionsModal,
  isPostVideoMedia,
  resolvePostMediaUrl
} from './Components.js';

import {
  MobileBottomNav,
  MobileUserSheet,
  FeedCaption,
  FeedVideo,
  FeedLoadingState,
  ProfileTabGroups,
  ProfileWorkTile,
  SyncStatusBanner,
  AdoptFilterBar
} from './MobileUI.js';

import cloudbase from '@cloudbase/js-sdk';
import {
  AUDIT_ACTIONS,
  filterActiveFeedItems,
  buildContentSnapshot,
  writeAuditLog,
  softDeleteContentInCloud,
  restoreContentInCloud,
  saveLegalConsent,
  isContentDeleted,
  needsLegalConsentRenewal
} from './compliance.js';
import {
  scanPublishFormCompliance,
  scanCommentCompliance,
  scanPrivateMessageCompliance,
  scanFeedbackCompliance,
  scanReportCompliance,
  complianceBlockMessage
} from './cybersecurityCompliance.js';
import {
  loadPublishDrafts,
  loadPublishDraftsLocal,
  savePublishDraftsLocal,
  migrateLegacyPublishDraft,
  upsertPublishDraft,
  deletePublishDrafts,
  renamePublishDraft,
  formDataFromPost,
  suggestDraftName,
  validateDraftForPublish
} from './publishDrafts.js';
import {
  fetchCloudPublishDrafts,
  upsertCloudPublishDraft,
  deleteCloudPublishDrafts,
  mergePublishDraftLists,
  syncAllDraftsToCloud
} from './publishDraftsCloud.js';
import {
  fetchUserFromCloudByPhone,
  fetchUserFromCloudByPhoneWithRetry,
  mergeCloudProfileIntoAppProfile,
  primaryActorKey,
  extractModerationFields,
  profileMatchesUserKey
} from './userCloudSync.js';
import {
  DEFAULT_MAINTENANCE_CONFIG,
  normalizeMaintenanceConfig,
  fetchCloudMaintenanceConfig,
  saveCloudMaintenanceConfig,
  isMaintenanceActive,
  isMaintenanceBypassProfile,
  shouldBlockForMaintenance,
  getMaintenanceBlockToast,
  markMaintenanceAdminSession,
  clearMaintenanceAdminSession,
  hasMaintenanceAdminSession,
  pickMaintenanceConfig,
  fetchServerMaintenanceConfig,
  saveServerMaintenanceConfig
} from './siteMaintenance.js';
import {
  resolveProfileLocationFields,
  isNearbyAdoptMatch,
  sortAdoptsByLocation,
  applyAuthLocationToProfile,
  formatProfileLocationDisplay,
  detectUserLocation,
  buildLocationProfile,
  setAutoLocationConsent,
  getGeolocationErrorMessage,
  UNKNOWN_LOCATION
} from './geoUtils.js';
import {
  OFFICIAL_UID,
  UID_PATTERN,
  normalizeUidInput,
  resolveUidForProfile,
  ensurePermanentUid,
  fetchUserFromCloudByUid
} from './uidUtils.js';
import {
  mergeSafeUserPayload,
  mergeSafePostPatch,
  appendLegacyUids,
  profilesDiffer
} from './dataSafety.js';
import {
  fetchProfileFromServerApi,
  syncProfileToServerApi,
  mergeServerProfileIntoAppProfile,
  buildContentKey,
  mergeFeedItemsByContentKeys
} from './profileApiSync.js';
import { getWorkInteractionCounts } from './workInteractions.js';

// 云开发：使用 npm 打包 SDK，避免依赖 imgcache.qq.com 被广告拦截导致 window.tcb 不存在
const CLOUD_ENV_ID = 'yugui-prod-d7gr1n9ludf874d95';
/** 与控制台「环境地域」一致，如 ap-guangzhou；留空则 SDK 默认 ap-shanghai（勿填错，否则会 network request error） */
const CLOUD_REGION = (process.env.REACT_APP_CLOUD_REGION || '').trim();
let app = null;
let db = null;

const getCloudbaseInitConfig = () => {
  const cfg = { env: CLOUD_ENV_ID, timeout: 25000 };
  if (CLOUD_REGION) cfg.region = CLOUD_REGION;
  return cfg;
};

/** 默认启用浏览器云 SDK；仅当构建变量要求关闭时禁用（例如改走纯自建 API） */
const isClientCloudbaseAllowed = () => {
  if (typeof window === 'undefined') return false;
  const flag = (process.env.REACT_APP_DISABLE_CLIENT_CLOUDBASE || '').trim().toLowerCase();
  if (flag === '1' || flag === 'true' || flag === 'yes') return false;
  return true;
};

const RAW_API_BASE = (process.env.REACT_APP_API_BASE || 'http://124.223.10.142:3001/api').trim();

const getApiBase = () => {
  // On HTTPS pages, use same-origin API path to avoid mixed-content blocks.
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    if (RAW_API_BASE.startsWith('http://')) return '/api';
  }
  // Local dev: always hit backend on 3001 (not the React dev server port).
  if (
    typeof window !== 'undefined' &&
    /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)
  ) {
    return 'http://localhost:3001/api';
  }
  return RAW_API_BASE;
};

const API_BASE = getApiBase();

const DEV_SMS_CODE = '123456';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getApiBaseCandidates = () => {
  const candidates = [];
  const isLocal =
    typeof window !== 'undefined' &&
    /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);

  if (isLocal) {
    candidates.push('http://localhost:3001/api');
    candidates.push('/api');
  }

  const primaryBase = getApiBase();
  if (primaryBase) candidates.push(primaryBase);

  if (typeof window !== 'undefined' && window.location?.origin && !isLocal) {
    candidates.push('/api');
    candidates.push(`${window.location.origin}/api`);
  }

  if (RAW_API_BASE) candidates.push(RAW_API_BASE);

  return [...new Set(candidates)];
};

const RETRYABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);
const BASE_TIMEOUT_MS = 10000;
const MAX_RETRY = 2;
const PROFILE_CACHE_KEY = 'yugui_user_profiles';
const PROFILE_SYNC_AT_PREFIX = 'yugui_profile_last_sync_';
const PROFILE_SYNC_TTL_MS = 5 * 60 * 1000;
const OFFLINE_QUEUE_KEY = 'yugui_offline_queue_v1';
const OFFLINE_TASK_MAX_RETRY = 6;
const OFFLINE_RETRY_BASE_MS = 3000;

const readProfileCache = () => {
  try {
    return JSON.parse(localStorage.getItem(PROFILE_CACHE_KEY) || '{}');
  } catch (error) {
    return {};
  }
};

const getCachedProfileByPhone = (phone) => {
  if (!phone) return null;
  const cache = readProfileCache();
  return cache[phone] || null;
};

const saveCachedProfileByPhone = (phone, profilePatch) => {
  if (!phone || !profilePatch) return;
  const cache = readProfileCache();
  cache[phone] = {
    ...(cache[phone] || {}),
    ...profilePatch,
    updatedAt: Date.now()
  };
  localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(cache));
};

const getProfileSyncAt = (phone) => {
  if (!phone) return 0;
  const value = Number(localStorage.getItem(`${PROFILE_SYNC_AT_PREFIX}${phone}`) || 0);
  return Number.isFinite(value) ? value : 0;
};

const markProfileSynced = (phone) => {
  if (!phone) return;
  localStorage.setItem(`${PROFILE_SYNC_AT_PREFIX}${phone}`, String(Date.now()));
};

const PROFILE_SYNC_FIELDS = [
  'name',
  'bio',
  'avatar',
  'coverImg',
  'uid',
  'followingList',
  'city',
  'location',
  'province'
];

const publisherKey = (petOrPost) =>
  String(petOrPost?.userId ?? petOrPost?.uid ?? petOrPost?.phone ?? '');

const viewerKey = (profile) => String(profile?.id ?? profile?.uid ?? profile?.phone ?? '');

/** 领养私信线程：同一宠物、发布者与咨询者唯一一条会话 */
const makeAdoptThreadId = (pet, profile) => {
  const pid = String(pet?.id ?? pet?._id ?? '');
  const owner = publisherKey(pet);
  const self = viewerKey(profile);
  if (!pid || !owner || !self) return '';
  const [a, b] = [owner, self].sort();
  return `${pid}|${a}|${b}`;
};

const otherPartyFromThreadId = (threadId, selfKey) => {
  const parts = String(threadId || '').split('|');
  if (parts.length < 3) return '';
  const [, k1, k2] = parts;
  if (k1 === selfKey) return k2;
  if (k2 === selfKey) return k1;
  return '';
};

/** 父级 overflow:hidden 时，嵌套滚动区常无法响应鼠标滚轮 */
const bindWheelScroll = (element, { snapSlides = false } = {}) => {
  if (!element) return () => {};
  let snapLock = false;
  const onWheel = (e) => {
    if (element.scrollHeight <= element.clientHeight + 2) return;

    if (snapSlides) {
      if (snapLock) {
        e.preventDefault();
        return;
      }
      if (Math.abs(e.deltaY) < 6) return;
      e.preventDefault();
      const slideH = element.clientHeight || 1;
      const idx = Math.round(element.scrollTop / slideH);
      const maxIdx = Math.max(0, Math.ceil(element.scrollHeight / slideH) - 1);
      const nextIdx = e.deltaY > 0 ? Math.min(idx + 1, maxIdx) : Math.max(idx - 1, 0);
      snapLock = true;
      element.scrollTo({ top: nextIdx * slideH, behavior: 'smooth' });
      window.setTimeout(() => {
        snapLock = false;
      }, 380);
      return;
    }

    const atTop = element.scrollTop <= 0;
    const atBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 2;
    const goingDown = e.deltaY > 0;
    const goingUp = e.deltaY < 0;
    if ((goingDown && atBottom) || (goingUp && atTop)) return;
    e.preventDefault();
    element.scrollTop += e.deltaY;
  };
  element.addEventListener('wheel', onWheel, { passive: false });
  return () => element.removeEventListener('wheel', onWheel);
};

/** 删除作品时写入草稿箱。返回是否未保存媒体 */
const savePublishDraftFromPost = (post) => {
  const { formData, droppedMedia } = formDataFromPost(post);
  try {
    const { droppedMedia: storeDrop } = upsertPublishDraft({
      name: suggestDraftName(formData),
      formData
    });
    return { droppedMedia: droppedMedia || storeDrop, error: false };
  } catch (e) {
    console.warn('草稿写入失败', e);
    return { droppedMedia: false, error: true };
  }
};

/** 写入云库前再压一档，避免 data URL 过大触发 network / 413 类错误 */
const shrinkDataUrlForCloud = (dataUrl) => new Promise((resolve) => {
  if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image')) {
    resolve(dataUrl);
    return;
  }
  if (dataUrl.length < 220000) {
    resolve(dataUrl);
    return;
  }
  const img = new Image();
  img.onload = () => {
    try {
      const maxSide = 480;
      let { width, height } = img;
      const scale = Math.min(1, maxSide / Math.max(width, height, 1));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.42));
    } catch (e) {
      resolve(dataUrl);
    }
  };
  img.onerror = () => resolve(dataUrl);
  img.src = dataUrl;
});

try {
  if (typeof window !== 'undefined' && isClientCloudbaseAllowed()) {
    app = cloudbase.init(getCloudbaseInitConfig());
    db = app.database();
  }
} catch (error) {
  console.warn('云开发初始化失败:', error);
}

// 发布前若 db 仍为空则再初始化一次（极少数首屏竞态）
const tryInitCloudDb = () => {
  if (db && app) return { ok: true };
  if (typeof window === 'undefined') {
    return { ok: false, reason: '当前环境不支持云开发' };
  }
  if (!isClientCloudbaseAllowed()) {
    return { ok: false, reason: '' };
  }
  try {
    if (!app) {
      app = cloudbase.init(getCloudbaseInitConfig());
    }
    db = app.database();
    return { ok: true };
  } catch (error) {
    console.warn('云开发延迟初始化失败:', error);
    return { ok: false, reason: error?.message || '云开发初始化失败' };
  }
};

// 云数据库写入前尽量匿名登录（否则 add 会因未登录失败，只能走连不上的 /api）
const ensureCloudAnonymousAuth = async () => {
  if (!app?.auth) return;
  try {
    const auth = app.auth();
    if (typeof auth.hasLoginState === 'function' && auth.hasLoginState()) return;
    if (typeof auth.getLoginState === 'function') {
      try {
        const st = await auth.getLoginState();
        if (st) return;
      } catch (e) {
        /* ignore */
      }
    }
    if (typeof auth.signInAnonymously === 'function') {
      await auth.signInAnonymously();
    }
  } catch (error) {
    console.warn('云匿名登录跳过或失败:', error);
  }
};

const ensureCloudDbReady = async () => {
  tryInitCloudDb();
  if (!db) return false;
  await ensureCloudAnonymousAuth();
  return true;
};

const apiRequest = async (endpoint, method = 'GET', data = null) => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { success: false, message: '当前设备离线，请检查网络连接' };
  }

  const endpointPath = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const baseCandidates = getApiBaseCandidates();
  const maxAttempt = method === 'GET' ? MAX_RETRY + 1 : MAX_RETRY;
  let lastFailureMessage = '连接服务失败，请稍后重试';

  for (const base of baseCandidates) {
    for (let attempt = 1; attempt <= maxAttempt; attempt += 1) {
      const controller = new AbortController();
      const bodyBytes = data ? JSON.stringify(data).length : 0;
      const timeoutMs =
        method === 'POST' && bodyBytes > 800_000
          ? 120000
          : BASE_TIMEOUT_MS + (attempt - 1) * 2000;
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      try {
        const options = {
          method,
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal
        };
        if (data) options.body = JSON.stringify(data);

        const requestUrl = `${base}${endpointPath}`;
        const response = await fetch(requestUrl, options);

        let result = null;
        try {
          result = await response.json();
        } catch (parseError) {
          result = null;
        }

        if (response.ok) {
          return result || { success: true };
        }

        const serverMessage = result?.message;
        lastFailureMessage =
          serverMessage ||
          (response.status >= 500
            ? '服务器繁忙，请稍后重试'
            : response.status === 413
              ? '视频或图片太大，请换更短/更小的文件后重试'
              : response.status === 404
                ? '服务不可用，请联系管理员'
                : '请求失败，请稍后重试');
        if (!serverMessage) {
          lastFailureMessage = `${lastFailureMessage} (HTTP ${response.status})`;
        }

        const canRetry = RETRYABLE_STATUS.has(response.status) && attempt < maxAttempt;
        if (!canRetry) {
          break;
        }
        await sleep(300 * attempt);
      } catch (error) {
        console.error('API请求失败:', error);
        if (error?.name === 'AbortError') {
          lastFailureMessage = `请求超时，请检查网络后重试 (${endpointPath})`;
        } else {
          lastFailureMessage = `连接服务失败，请稍后重试 (${endpointPath})`;
        }

        const canRetry = attempt < maxAttempt;
        if (!canRetry) {
          break;
        }
        await sleep(300 * attempt);
      } finally {
        clearTimeout(timeoutId);
      }
    }
  }

  return { success: false, message: lastFailureMessage };
};

const parseFeedItems = (result) => {
  const rawItems =
    result?.posts ||
    result?.items ||
    result?.data ||
    result?.list ||
    [];

  if (!Array.isArray(rawItems)) return [];
  return rawItems.map((item) => ({
    ...item,
    content: item.content || item.description || '',
    name: item.name || item.petName || '',
    id: item.id || item._id || `${item.type || 'post'}_${item.createdAt || Date.now()}`
  }));
};

const normalizeAndMergeFeeds = (postResult, petResult) => {
  const postItems = parseFeedItems(postResult).map((item) => ({
    ...item,
    type: item.type || 'post'
  }));
  const petItems = parseFeedItems(petResult).map((item) => ({
    ...item,
    type: 'adopt'
  }));

  const merged = [...postItems, ...petItems];
  const dedupMap = new Map();
  merged.forEach((item) => {
    dedupMap.set(String(item.id), item);
  });
  return filterActiveFeedItems(
    Array.from(dedupMap.values()).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
  );
};

/** 合并多路 feed（云库 + 服务器 API），优先保留带媒体的条目 */
const mergeFeedLists = (...lists) => {
  const map = new Map();
  lists.flat().forEach((item) => {
    if (!item?.id) return;
    const key = String(item.id);
    const existing = map.get(key);
    if (!existing) {
      map.set(key, item);
      return;
    }
    const hasMedia = (row) => !!(row?.img || row?.image || row?.media || row?.cover);
    const pick =
      hasMedia(existing) && !hasMedia(item)
        ? existing
        : hasMedia(item) && !hasMedia(existing)
          ? item
          : (item.createdAt || 0) >= (existing.createdAt || 0)
            ? item
            : existing;
    map.set(key, pick);
  });
  return filterActiveFeedItems(
    Array.from(map.values()).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
  );
};

const toAppProfile = (serverUser, phone, isAdmin, locationInfo, locationAgreed, cachedProfile = {}) => {
  const locationFields = resolveProfileLocationFields(
    locationInfo,
    locationAgreed,
    serverUser,
    cachedProfile
  );

  return {
    id: serverUser?.id || serverUser?._id || cachedProfile.id || null,
    name: serverUser?.name || cachedProfile.name || (isAdmin ? "予归官方" : `用户_${phone.slice(-4)}`),
    phone,
    uid: resolveUidForProfile({ isAdmin, serverUser, cachedProfile }) || (isAdmin ? OFFICIAL_UID : ''),
    bio: serverUser?.bio || cachedProfile.bio || (isAdmin ? "官方账号，发布平台公告与规则" : "用领养代替购买，给生命一个家"),
    avatar: serverUser?.avatar || cachedProfile.avatar || (isAdmin ? "https://images.unsplash.com/photo-1450778869180-41d0601e046e?auto=format&fit=crop&w=100&q=80" : `https://api.dicebear.com/7.x/avataaars/svg?seed=${phone}`),
    coverImg: serverUser?.coverImg || cachedProfile.coverImg || DEFAULT_COVER,
    location: locationFields.location,
    city: locationFields.city,
    province: locationFields.province,
    locationData: locationFields.locationData,
    locationSource: locationFields.locationSource,
    locationAgreed: locationFields.locationAgreed,
    role: isAdmin ? "super_admin" : (serverUser?.role || "user"),
    isVerified: isAdmin || serverUser?.isVerified === 1 || serverUser?.isVerified === true,
    followingList: Array.isArray(serverUser?.followingList)
      ? serverUser.followingList
      : (typeof serverUser?.followingList === 'string'
        ? (() => {
            try { return JSON.parse(serverUser.followingList); } catch { return []; }
          })()
        : (cachedProfile.followingList || [])),
    followers: serverUser?.followers || cachedProfile.followers || 0,
    createdAt: serverUser?.createdAt || cachedProfile.createdAt || Date.now()
  };
};

const getServerUserFromResult = (result) => {
  if (!result?.success) return null;
  return result.user || result.data || result.profile || null;
};

const fetchUserFromApiByPhone = async (phone) => fetchProfileFromServerApi(apiRequest, phone);

const sortAnnouncements = (list) =>
  [...(list || [])].sort((a, b) => {
    const withdrawnDiff = (a.withdrawn ? 1 : 0) - (b.withdrawn ? 1 : 0);
    if (withdrawnDiff !== 0) return withdrawnDiff;
    const pinDiff = (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
    if (pinDiff !== 0) return pinDiff;
    return (b.pinnedAt || b.createdAt || 0) - (a.pinnedAt || a.createdAt || 0);
  });

const filterPublicAnnouncements = (list) =>
  (list || []).filter((ann) => !ann.withdrawn && !ann.archived);

const App = () => {
  // 全局状态
  const [currentView, setCurrentView] = useState('home'); 
  const [showSplash, setShowSplash] = useState(true);
  
  // 弹窗状态
  const [toastMsg, setToastMsg] = useState('');
  const [toastType, setToastType] = useState('success');
  const [showMobileUserMenu, setShowMobileUserMenu] = useState(false);
  const [expandedFeedPostId, setExpandedFeedPostId] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [viewingUserTab, setViewingUserTab] = useState('works');
  const [feedsLoading, setFeedsLoading] = useState(true);
  const [feedsLoadError, setFeedsLoadError] = useState('');
  const [adoptFilterType, setAdoptFilterType] = useState('all');
  const [adoptFilterLocation, setAdoptFilterLocation] = useState('all');
  const [auditLogs, setAuditLogs] = useState([]);
  const [adminAllContent, setAdminAllContent] = useState([]);
  const [banRequests, setBanRequests] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [workInteractionPost, setWorkInteractionPost] = useState(null);
  const [feedReturnView, setFeedReturnView] = useState(null);
  const [discoverFocusPostId, setDiscoverFocusPostId] = useState(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [pendingDeletePost, setPendingDeletePost] = useState(null);
  const [adoptChatPet, setAdoptChatPet] = useState(null);
  const [adoptChatThreadId, setAdoptChatThreadId] = useState(null);
  const [adoptChatMessages, setAdoptChatMessages] = useState([]);
  const [adoptChatThreadChoices, setAdoptChatThreadChoices] = useState([]);
  const [videoSoundPostId, setVideoSoundPostId] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showUserAgreement, setShowUserAgreement] = useState(false);
  const [showPlatformConvention, setShowPlatformConvention] = useState(false);
  const [showConventionAcceptModal, setShowConventionAcceptModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  
  // 搜索状态
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTab, setSearchTab] = useState('works');
  const [searchResults, setSearchResults] = useState({ works: [], adopts: [], users: [], documents: [] });
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);

  // 用户与数据状态
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userProfile, setUserProfile] = useState(null); 
  const [posts, setPosts] = useState([]);
  const [adoptList, setAdoptList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [reports, setReports] = useState([]);
  const [feedbackList, setFeedbackList] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [siteMaintenance, setSiteMaintenance] = useState({ ...DEFAULT_MAINTENANCE_CONFIG });
  const [maintenanceReady, setMaintenanceReady] = useState(false);
  const [notifications, setNotifications] = useState([]); // 通知数据
  const [profileTab, setProfileTab] = useState('works');
  /** 个人主页「我的作品 / 我的领养」多选 id */
  const [profileBatchIds, setProfileBatchIds] = useState([]);
  const [profileBatchMode, setProfileBatchMode] = useState(false);
  const [publishDrafts, setPublishDrafts] = useState([]);
  const [publishEditingDraftId, setPublishEditingDraftId] = useState(null);
  const [draftBatchMode, setDraftBatchMode] = useState(false);
  const [draftBatchIds, setDraftBatchIds] = useState([]);
  const [showProfileBatchHint, setShowProfileBatchHint] = useState(false);
  const [syncQueue, setSyncQueue] = useState([]);
  const [isSyncingQueue, setIsSyncingQueue] = useState(false);
  const userProfilePhoneRef = useRef(null);
  const wasBannedRef = useRef(false);

  useEffect(() => {
    userProfilePhoneRef.current = userProfile?.phone || null;
  }, [userProfile?.phone]);

  useEffect(() => {
    setViewingUserTab('works');
  }, [viewingUser?.uid, viewingUser?.id, viewingUser?.phone]);

  useEffect(() => {
    if (userProfile?.isBanned) {
      wasBannedRef.current = true;
      return;
    }
    if (wasBannedRef.current && userProfile && !userProfile.isBanned) {
      wasBannedRef.current = false;
      showToast('您的账号已解封，欢迎回来！');
    }
  }, [userProfile?.isBanned, userProfile?.phone]);

  const publicAnnouncements = filterPublicAnnouncements(announcements);
  const showToast = (msg, type, durationMs = 3000) => {
    const text = typeof msg === 'string' ? msg : '操作成功';
    let resolved = type;
    if (!resolved) {
      if (/失败|错误|请先|不能|无效|超时|离线|无法|暂不支持|异常/.test(text)) resolved = 'error';
      else if (/稍后|待同步|部分成功|可能需|正在|请稍候|请勿关闭/.test(text)) resolved = 'warning';
      else resolved = 'success';
    }
    setToastType(resolved);
    setToastMsg(text);
    setTimeout(() => setToastMsg(''), durationMs);
  };

  const closeAllOverlays = () => {
    setShowSettingsModal(false);
    setShowEmailModal(false);
    setShowPublishModal(false);
    setShowReportModal(false);
    setShowFeedbackModal(false);
    setShowCommentModal(false);
    setShowPrivacyPolicy(false);
    setShowUserAgreement(false);
    setShowPlatformConvention(false);
    setShowSearchResults(false);
    setShowSearch(false);
    setIsSearching(false);
    setShowMobileUserMenu(false);
    setAdoptChatPet(null);
    setAdoptChatThreadId(null);
    setAdoptChatMessages([]);
    setAdoptChatThreadChoices([]);
    setSelectedPost(null);
    setReportTarget(null);
    setWorkInteractionPost(null);
    setPendingDeletePost(null);
  };

  const openSearchBar = () => {
    closeAllOverlays();
    setSearchTab(
      currentView === 'adopt' ? 'adopt' : currentView === 'profile' || currentView === 'user' ? 'users' : 'works'
    );
    setShowSearch(true);
  };

  const openEmailModal = () => {
    closeAllOverlays();
    setShowEmailModal(true);
  };

  const openSettingsModal = () => {
    closeAllOverlays();
    setShowSettingsModal(true);
  };

  const openPlatformConvention = () => {
    closeAllOverlays();
    setShowPlatformConvention(true);
  };

  const openPrivacyPolicy = () => {
    closeAllOverlays();
    setShowPrivacyPolicy(true);
  };

  const openUserAgreement = () => {
    closeAllOverlays();
    setShowUserAgreement(true);
  };

  const openFeedbackModal = () => {
    closeAllOverlays();
    setShowFeedbackModal(true);
  };

  const openReportModal = (target) => {
    closeAllOverlays();
    setReportTarget(target);
    setShowReportModal(true);
  };

  const openCommentModal = (post) => {
    closeAllOverlays();
    setSelectedPost(post);
    setShowCommentModal(true);
  };

  const resetModalsForNav = () => {
    closeAllOverlays();
  };

  const openAuthModal = () => {
    closeAllOverlays();
    setShowAuthModal(true);
  };

  const openMobileUserMenu = () => {
    closeAllOverlays();
    setShowMobileUserMenu(true);
  };

  const navigateTo = (view, opts = {}) => {
    if (view === 'admin') {
      if (!isLoggedIn || !isAdminProfile(userProfile)) {
        showToast('无管理权限');
        return;
      }
    }
    resetModalsForNav();
    if (view !== 'user' && !opts.preserveViewingUser) setViewingUser(null);
    setCurrentView(view);
  };

  const openAdminConsole = () => {
    if (!isLoggedIn) {
      openAuthModal();
      return;
    }
    navigateTo('admin');
  };

  /** 切换主页面时自动关闭全屏弹层，无需手动点关闭 */
  useEffect(() => {
    if (currentView === 'admin') return;
    closeAllOverlays();
  }, [currentView]);

  const resolveAuthorAvatar = (post) => {
    if (
      post &&
      userProfile &&
      (isMine(post.userId) ||
        (post.phone && String(post.phone) === String(userProfile.phone)) ||
        (post.uid && String(post.uid) === String(userProfile.uid)))
    ) {
      return userProfile.avatar || post.avatar || DEFAULT_AVATAR;
    }
    if (post?.avatar) return post.avatar;
    const uid = post?.userId;
    const matched = usersList.find(
      (u) =>
        String(u.id) === String(uid) ||
        String(u.uid) === String(uid) ||
        String(u.phone) === String(uid) ||
        (post?.phone && String(u.phone) === String(post.phone)) ||
        (post?.uid && String(u.uid) === String(post.uid))
    );
    return matched?.avatar || DEFAULT_AVATAR;
  };

  const formatFeedAuthorLabel = (post) => {
    const name = String(post?.user || '').trim();
    const uid = String(post?.uid || '').trim();
    if (name && name.length >= 2 && !/^\d+$/.test(name)) return name;
    if (uid) return uid;
    return name || '用户';
  };

  const openAuthorProfile = (post) => {
    const uid = post?.userId;
    if (isMine(uid)) {
      setViewingUser(null);
      navigateTo('profile');
      return;
    }
    const matched = usersList.find(
      (u) => String(u.id) === String(uid) || String(u.uid) === String(uid) || String(u.phone) === String(uid)
    );
    if (matched) {
      setViewingUser(matched);
      navigateTo('user');
      return;
    }
    setViewingUser({
      id: uid,
      uid: post?.uid || uid,
      name: post?.user || '用户',
      avatar: DEFAULT_AVATAR,
      bio: '该用户暂未完善资料',
      location: '未知位置',
      followers: 0,
      followingList: []
    });
    navigateTo('user');
  };

  const retryFeedsLoad = async () => {
    setFeedsLoadError('');
    setFeedsLoading(true);
    try {
      if (db) {
        const [pr, petr] = await Promise.all([
          db.collection('posts').limit(200).get(),
          db.collection('pets').limit(200).get()
        ]);
        const mapRow = (doc, forceType) => ({
          ...doc,
          id: doc._id,
          type: forceType || doc.type || 'post',
          content: doc.content || doc.description || ''
        });
        setPosts(filterActiveFeedItems((pr.data || []).map((d) => mapRow(d, 'post'))));
        setAdoptList(filterActiveFeedItems((petr.data || []).map((d) => mapRow(d, 'adopt'))));
      } else {
        const [postResult, petResult] = await Promise.all([
          apiRequest('/posts', 'GET'),
          apiRequest('/pets', 'GET')
        ]);
        const items = normalizeAndMergeFeeds(
          postResult?.success ? postResult : { posts: [] },
          petResult?.success ? petResult : { posts: [] }
        );
        setPosts(items.filter((item) => (item.type || 'post') !== 'adopt'));
        setAdoptList(items.filter((item) => item.type === 'adopt'));
      }
    } catch (e) {
      setFeedsLoadError('网络异常，请检查连接后重试');
    } finally {
      setFeedsLoading(false);
    }
  };

  const performLogout = (toastMessage = '已安全退出') => {
    clearConventionSessionAcceptance();
    clearMaintenanceAdminSession();
    localStorage.removeItem('yugui_user');
    setIsLoggedIn(false);
    setUserProfile(null);
    navigateTo('home');
    if (toastMessage) showToast(toastMessage);
  };

  const promptConventionIfNeeded = (phone) => {
    if (phone && (needsConventionAcceptance(phone) || needsLegalConsentRenewal(phone))) {
      setShowConventionAcceptModal(true);
    }
  };

  const beginAuthenticatedSession = (phone) => {
    clearConventionSessionAcceptance();
    promptConventionIfNeeded(phone);
  };

  const recordContentBlocked = async (compliance, actionLabel) => {
    await recordAudit({
      action: AUDIT_ACTIONS.CONTENT_BLOCKED,
      summary: `${actionLabel}：${compliance.code || '违规'}`,
      snapshot: {
        article: compliance.article || '',
        field: compliance.field || '',
        message: (compliance.message || '').slice(0, 300)
      }
    });
  };

  const recordAudit = async (entry) => {
    await writeAuditLog(db, ensureCloudAnonymousAuth, {
      actorId: userProfile?.id || userProfile?.uid || entry?.actorId || '',
      actorPhone: userProfile?.phone || entry?.actorPhone || '',
      actorName: userProfile?.name || entry?.actorName || '',
      ...entry
    });
  };

  const recordAdminAudit = async (entry) => {
    const actorRole = isSuperAdminProfile(userProfile) ? 'super_admin' : 'admin';
    const logRow = {
      id: `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      createdAt: Date.now(),
      actorId: userProfile?.id || userProfile?.uid || '',
      actorPhone: userProfile?.phone || '',
      actorName: userProfile?.name || '',
      ...entry,
      meta: {
        ...(entry.meta || {}),
        actorRole,
        actorUid: userProfile?.uid || ''
      }
    };
    await recordAudit(logRow);
    setAuditLogs((prev) => [logRow, ...prev].slice(0, 120));
  };

  const expireTemporaryAdmins = (list) => {
    const now = Date.now();
    return (list || []).map((u) => {
      if (u.role !== 'admin' || !u.adminUntil) return u;
      if (Number(u.adminUntil) > now) return u;
      return {
        ...u,
        role: 'user',
        adminUntil: null,
        adminDays: null,
        adminGrantedBy: null,
        adminGrantedByName: null
      };
    });
  };

  const handleConventionAccept = () => {
    if (userProfile?.phone) {
      saveConventionAcceptance(userProfile.phone);
      saveLegalConsent(userProfile.phone, { conventionVersion: PLATFORM_CONVENTION_VERSION });
      recordAudit({
        action: AUDIT_ACTIONS.CONVENTION_ACCEPT,
        actorId: userProfile.id || userProfile.uid,
        actorPhone: userProfile.phone,
        actorName: userProfile.name,
        summary: `确认平台公约 v${PLATFORM_CONVENTION_VERSION}`
      });
      recordAudit({
        action: AUDIT_ACTIONS.LEGAL_CONSENT,
        actorId: userProfile.id || userProfile.uid,
        actorPhone: userProfile.phone,
        actorName: userProfile.name,
        summary: '确认隐私政策与用户协议'
      });
    }
    setShowConventionAcceptModal(false);
  };

  const requireAuth = (action) => {
    if (!isLoggedIn || !userProfile) {
      openAuthModal();
      return false;
    }
    if (userProfile.isBanned) {
      showToast('账号已被限制使用。如有疑问请通过「官方反馈」联系平台', 'warning');
      return false;
    }
    if (needsConventionAcceptance(userProfile.phone) || needsLegalConsentRenewal(userProfile.phone)) {
      setShowConventionAcceptModal(true);
      return false;
    }
    if (action) action();
    return true;
  };

  const saveQueueToStorage = (queue) => {
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
  };

  const pushOfflineTask = (task) => {
    if (!task) return;
    setSyncQueue((prev) => {
      const next = [
        ...prev,
        {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          createdAt: Date.now(),
          retryCount: 0,
          nextRetryAt: Date.now(),
          ...task
        }
      ];
      saveQueueToStorage(next);
      return next;
    });
  };

  const loadQueueFromStorage = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed;
    } catch (error) {
      return [];
    }
  };

  const syncProfileToCloudDb = async (profile) => {
    if (!profile?.phone) return false;
    if (!(await ensureCloudDbReady())) return false;
    try {
      const queryRes = await db.collection('users').where({ phone: profile.phone }).get();
      const existingCloud = queryRes?.data?.[0] || {};
      const payload = mergeSafeUserPayload(existingCloud, profile);

      if (queryRes?.data?.length) {
        await db.collection('users').doc(queryRes.data[0]._id).update(payload);
      } else {
        await db.collection('users').add({
          ...payload,
          createdAt: payload.createdAt || Date.now()
        });
      }
      markProfileSynced(profile.phone);
      return true;
    } catch (error) {
      console.warn('用户资料同步到云数据库失败:', error);
      return false;
    }
  };

  const refreshPublishDraftsFromCloud = async (phone = userProfile?.phone) => {
    migrateLegacyPublishDraft();
    let local = loadPublishDraftsLocal();
    if (db && phone) {
      try {
        await ensureCloudAnonymousAuth();
        const cloud = await fetchCloudPublishDrafts(db, ensureCloudAnonymousAuth, phone);
        const merged = mergePublishDraftLists(local, cloud);
        savePublishDraftsLocal(merged);
        await syncAllDraftsToCloud(db, ensureCloudAnonymousAuth, phone, merged);
        setPublishDrafts(merged);
        return merged;
      } catch (error) {
        console.warn('草稿云同步失败:', error);
      }
    }
    setPublishDrafts(local);
    return local;
  };

  const persistPublishDraft = async (payload) => {
    const result = upsertPublishDraft(payload);
    if (userProfile?.phone && db) {
      await upsertCloudPublishDraft(db, ensureCloudAnonymousAuth, userProfile.phone, result.draft);
    }
    setPublishDrafts(loadPublishDraftsLocal());
    return result;
  };

  const removePublishDrafts = async (ids) => {
    const removed = deletePublishDrafts(ids);
    if (userProfile?.phone && db) {
      await deleteCloudPublishDrafts(db, ensureCloudAnonymousAuth, userProfile.phone, ids);
    }
    setPublishDrafts(loadPublishDraftsLocal());
    return removed;
  };

  const renamePublishDraftEntry = async (id, name) => {
    const row = renamePublishDraft(id, name);
    if (row && userProfile?.phone && db) {
      await upsertCloudPublishDraft(db, ensureCloudAnonymousAuth, userProfile.phone, row);
    }
    setPublishDrafts(loadPublishDraftsLocal());
    return row;
  };

  const syncUserDataFromCloud = async (phone, baseProfile) => {
    if (!phone || !baseProfile) return baseProfile;
    let profile = baseProfile;
    if (await ensureCloudDbReady()) {
      const cloudUser = await fetchUserFromCloudByPhoneWithRetry(
        db,
        ensureCloudAnonymousAuth,
        phone
      );
      if (cloudUser) {
        profile = mergeCloudProfileIntoAppProfile(baseProfile, cloudUser);
      }
    }
    const apiUser = await fetchProfileFromServerApi(apiRequest, phone);
    if (apiUser) profile = mergeServerProfileIntoAppProfile(profile, apiUser);
    setUserProfile(profile);
    localStorage.setItem('yugui_user', JSON.stringify(profile));
    saveCachedProfileByPhone(phone, profile);
    await refreshPublishDraftsFromCloud(phone);
    return profile;
  };

  const resolveLoginProfile = async ({
    phone,
    serverUser,
    locationInfo,
    locationAgreed,
    cachedProfile: cachedOverride,
    applySession = true
  }) => {
    const isAdmin = phone === OFFICIAL_ADMIN_PHONE;
    const cachedProfile = cachedOverride || getCachedProfileByPhone(phone) || {};
    const apiUser = (await fetchProfileFromServerApi(apiRequest, phone)) || serverUser;
    await ensureCloudDbReady();
    let profile = toAppProfile(apiUser, phone, isAdmin, locationInfo, locationAgreed, cachedProfile);
    profile = mergeServerProfileIntoAppProfile(profile, apiUser);
    if (db) {
      const cloudUser = await fetchUserFromCloudByPhoneWithRetry(
        db,
        ensureCloudAnonymousAuth,
        phone
      );
      if (cloudUser) profile = mergeCloudProfileIntoAppProfile(profile, cloudUser);
    }
    profile = applyAuthLocationToProfile(profile, locationInfo, locationAgreed);
    profile = await finalizeProfileUid(profile);
    if (!applySession) return profile;
    return syncUserDataFromCloud(phone, profile);
  };

  const applySessionProfile = (phone, profile) => {
    setUserProfile(profile);
    localStorage.setItem('yugui_user', JSON.stringify(profile));
    saveCachedProfileByPhone(phone, profile);
    return profile;
  };

  const persistUserPatchToServer = async (patch, baseProfile = userProfile) => {
    if (!baseProfile?.phone) return null;
    const apiUser = await syncProfileToServerApi(apiRequest, baseProfile, patch);
    if (!apiUser) return null;
    const merged = mergeServerProfileIntoAppProfile(baseProfile, apiUser);
    applySessionProfile(baseProfile.phone, merged);
    return merged;
  };

  const rejectIfMaintenanceBlocksProfile = async (profile) => {
    const cloudCfg = db
      ? await fetchCloudMaintenanceConfig(db, ensureCloudAnonymousAuth)
      : siteMaintenance;
    const serverCfg = await fetchServerMaintenanceConfig(apiRequest);
    const cfg = pickMaintenanceConfig(cloudCfg, serverCfg);
    setSiteMaintenance(cfg);
    if (!isMaintenanceActive(cfg)) return false;
    if (!isMaintenanceBypassProfile(profile)) {
      showToast(getMaintenanceBlockToast(cfg));
      return true;
    }
    markMaintenanceAdminSession(profile.phone);
    return false;
  };

  const loadMergedMaintenanceConfig = async () => {
    const cloudCfg = db
      ? await fetchCloudMaintenanceConfig(db, ensureCloudAnonymousAuth)
      : { ...DEFAULT_MAINTENANCE_CONFIG };
    const serverCfg = await fetchServerMaintenanceConfig(apiRequest);
    return pickMaintenanceConfig(cloudCfg, serverCfg);
  };

  const enforceMaintenanceLockout = (cfg, profile, { toast = true } = {}) => {
    if (!isMaintenanceActive(cfg)) return;
    if (profile && !isMaintenanceBypassProfile(profile)) {
      localStorage.removeItem('yugui_user');
      setIsLoggedIn(false);
      setUserProfile(null);
      closeAllOverlays();
      setShowAuthModal(false);
      if (toast) showToast(getMaintenanceBlockToast(cfg));
      return;
    }
    if (
      profile &&
      isMaintenanceBypassProfile(profile) &&
      !hasMaintenanceAdminSession(profile.phone)
    ) {
      setIsLoggedIn(false);
      setUserProfile(null);
      closeAllOverlays();
      setShowAuthModal(false);
    }
  };

  const syncPostPatch = async (item, patch, contentType = 'post') => {
    if (!item) return false;
    const itemId = item.id || item._id;
    const base = contentType === 'adopt' ? 'pets' : 'posts';
    const idKey = contentType === 'adopt' ? 'petId' : 'postId';
    const endpoints = itemId
      ? [`/${base}/${itemId}`, `/${base}/update`, `/${base}/interact`]
      : [`/${base}/update`, `/${base}/interact`];

    for (const endpoint of endpoints) {
      const method = endpoint.includes(`/${base}/`) && !endpoint.endsWith('/update') && !endpoint.endsWith('/interact') ? 'PATCH' : 'POST';
      const payload = { id: itemId, [idKey]: itemId, ...patch };
      const result = await apiRequest(endpoint, method, payload);
      if (result?.success) return true;
    }

    if (!db || !itemId) return false;
    try {
      const existingRes = await db.collection(base).doc(itemId).get();
      const payload = mergeSafePostPatch(existingRes?.data || {}, patch);
      await db.collection(base).doc(itemId).update(payload);
      return true;
    } catch (error) {
      console.warn(`${base} 云端同步失败:`, error);
      return false;
    }
  };

  const updateUserRoleInCloud = async (userRow, role, extra = {}) => {
    if (!db || !userRow) return false;
    try {
      await ensureCloudAnonymousAuth();
      const docId = String(userRow.id ?? userRow._id ?? '');
      if (!docId) return false;
      const patch = {
        role,
        updatedAt: Date.now()
      };
      if (role === 'admin') {
        patch.adminUntil = extra.adminUntil ?? null;
        patch.adminDays = extra.adminDays ?? null;
        patch.adminGrantedBy = extra.adminGrantedBy ?? '';
        patch.adminGrantedByName = extra.adminGrantedByName ?? '';
      } else if (role === 'user') {
        patch.adminUntil = null;
        patch.adminDays = null;
        patch.adminGrantedBy = null;
        patch.adminGrantedByName = null;
      }
      await db.collection('users').doc(docId).update(patch);
      return true;
    } catch (error) {
      console.warn('users 角色云同步失败:', error);
      return false;
    }
  };

  const getBanDurationLabel = (days) => {
    if (days === -1) return '永久';
    if (days === 1) return '1 天';
    if (days === 7) return '7 天';
    if (days === 30) return '30 天';
    return `${days} 天`;
  };

  const matchUserRow = (data) => {
    const uid = String(data.userId || data.id || '');
    if (!uid) return null;
    return usersList.find(
      (u) =>
        String(u.id) === uid ||
        String(u.uid) === uid ||
        String(u.userId) === uid ||
        String(u._id) === uid ||
        (data.phone && String(u.phone) === String(data.phone))
    );
  };

  const applyBanPatchToUsersList = (userId, patch) => {
    const uid = String(userId || '');
    if (!uid) return;
    setUsersList((prev) =>
      prev.map((u) => {
        const hit =
          String(u.id) === uid ||
          String(u.uid) === uid ||
          String(u.userId) === uid ||
          String(u._id) === uid ||
          (patch.phone && String(u.phone) === String(patch.phone));
        return hit ? { ...u, ...patch } : u;
      })
    );
    setUserProfile((prev) => {
      if (!profileMatchesUserKey(prev, uid) && !(patch.phone && prev?.phone === patch.phone)) return prev;
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem('yugui_user', JSON.stringify(next));
        saveCachedProfileByPhone(next.phone, next);
      } catch (_) {}
      return next;
    });
  };

  const syncSelfProfileFromCloudRow = (row) => {
    const phone = userProfilePhoneRef.current;
    if (!phone || !row || String(row.phone) !== String(phone)) return;
    const moderation = extractModerationFields(row);
    setUserProfile((prev) => {
      if (!prev || String(prev.phone) !== String(phone)) return prev;
      const next = mergeCloudProfileIntoAppProfile(prev, { ...row, ...moderation });
      const profileChanged = profilesDiffer(prev, next, PROFILE_SYNC_FIELDS);
      const moderationChanged =
        !!prev.isBanned !== !!next.isBanned ||
        (prev.banUntil || null) !== (next.banUntil || null) ||
        prev.role !== next.role;
      if (!profileChanged && !moderationChanged) return prev;
      try {
        localStorage.setItem('yugui_user', JSON.stringify(next));
        saveCachedProfileByPhone(next.phone, next);
      } catch (_) {}
      return next;
    });
    setUsersList((prev) =>
      prev.map((u) =>
        String(u.phone) === String(phone)
          ? {
              ...u,
              name: row.name || u.name,
              bio: row.bio ?? u.bio,
              avatar: row.avatar || u.avatar,
              coverImg: row.coverImg || u.coverImg,
              uid: row.uid || u.uid
            }
          : u
      )
    );
  };

  const updateUserBanInCloud = async (userRow, { banned, days }, actor = {}) => {
    if (!db || !userRow) return false;
    try {
      await ensureCloudAnonymousAuth();
      const docId = String(userRow.id ?? userRow._id ?? userRow.userId ?? '');
      if (!docId) return false;
      const isPermanent = days === -1;
      const patch = banned
        ? {
            isBanned: true,
            banDays: days,
            banUntil: isPermanent ? null : Date.now() + days * 86400000,
            bannedAt: Date.now(),
            bannedBy: actor.phone || '',
            bannedByName: actor.name || '',
            bannedByRole: actor.role || '',
            updatedAt: Date.now()
          }
        : {
            isBanned: false,
            banDays: 0,
            banUntil: null,
            bannedAt: null,
            bannedBy: '',
            bannedByName: '',
            bannedByRole: '',
            updatedAt: Date.now()
          };
      await db.collection('users').doc(docId).update(patch);
      return true;
    } catch (error) {
      console.warn('users 封禁云同步失败:', error);
      return false;
    }
  };

  const syncPostInteraction = async (item, interactionPayload, contentType = 'post') => {
    if (!item || !interactionPayload) return false;
    const itemId = item.id || item._id;
    if (!itemId) return false;
    const base = contentType === 'adopt' ? 'pets' : 'posts';
    const idKey = contentType === 'adopt' ? 'petId' : 'postId';
    const interactionEndpoints = [
      `/${base}/${itemId}/interact`,
      `/${base}/interact`,
      `/${base}/${itemId}/${interactionPayload.action || 'action'}`
    ];
    for (const endpoint of interactionEndpoints) {
      const payload = { id: itemId, [idKey]: itemId, ...interactionPayload };
      const result = await apiRequest(endpoint, 'POST', payload);
      if (result?.success) return true;
    }
    return false;
  };

  const uploadMediaToCloud = async (rawFile) => {
    if (!rawFile || !app?.uploadFile) return null;
    const ext = (rawFile.name?.split('.').pop() || 'bin').toLowerCase();
    const cloudPath = `uploads/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
    try {
      const uploadRes = await app.uploadFile({
        cloudPath,
        filePath: rawFile
      });
      return uploadRes?.fileID || uploadRes?.fileId || null;
    } catch (error) {
      console.warn('媒体上传云存储失败:', error);
      return null;
    }
  };

  const fileToDataUrl = async (rawFile) => {
    if (!rawFile) return null;
    return new Promise((resolve) => {
      try {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(rawFile);
      } catch (error) {
        resolve(null);
      }
    });
  };

  const syncNotificationToCloud = async (notification) => {
    if (!db || !notification) return false;
    try {
      await db.collection('notifications').add(notification);
      return true;
    } catch (error) {
      console.warn('通知同步失败:', error);
      return false;
    }
  };

  const syncFollowState = async (nextFollowingList, targetUserId, isFollowing) => {
    const normalizedTargetId = String(targetUserId || '');
    const followPayload = {
      userId: userProfile?.id,
      phone: userProfile?.phone,
      targetUserId,
      action: isFollowing ? 'unfollow' : 'follow'
    };
    const followEndpoints = ['/user/follow', '/users/follow', '/social/follow'];
    let followSynced = false;
    for (const endpoint of followEndpoints) {
      const result = await apiRequest(endpoint, 'POST', followPayload);
      if (result?.success) {
        followSynced = true;
        break;
      }
    }

    const updatedProfile = {
      ...userProfile,
      followingList: nextFollowingList
    };
    localStorage.setItem('yugui_user', JSON.stringify(updatedProfile));
    saveCachedProfileByPhone(updatedProfile.phone, { followingList: updatedProfile.followingList });
    await syncProfileToCloudDb(updatedProfile);
    await persistUserPatchToServer({ followingList: nextFollowingList }, updatedProfile);

    if (db && normalizedTargetId) {
      try {
        const targetQuery = await db.collection('users').where({ uid: normalizedTargetId }).get();
        const targetByUid = targetQuery?.data?.[0];
        const matchedTarget = targetByUid || usersList.find(u =>
          String(u.id) === normalizedTargetId || String(u.uid) === normalizedTargetId
        );
        if (matchedTarget?.id) {
          const currentFollowers = Number(matchedTarget.followers || 0);
          const nextFollowers = isFollowing ? Math.max(0, currentFollowers - 1) : currentFollowers + 1;
          await db.collection('users').doc(matchedTarget.id).update({
            followers: nextFollowers,
            updatedAt: Date.now()
          });
        }
      } catch (error) {
        console.warn('粉丝数同步失败:', error);
      }
    }
    return followSynced;
  };

  const executeSyncTask = async (task) => {
    if (!task?.type) return true;
    if (task.type === 'postPatch') {
      return syncPostPatch(task.item, task.patch, task.contentType || 'post');
    }
    if (task.type === 'postInteraction') {
      return syncPostInteraction(task.item, task.interactionPayload, task.contentType || 'post');
    }
    if (task.type === 'notification') {
      return syncNotificationToCloud(task.notification);
    }
    if (task.type === 'follow') {
      await syncFollowState(task.nextFollowingList || [], task.targetUserId, !!task.isFollowing);
      return true;
    }
    if (task.type === 'publish') {
      const endpoint = task.contentType === 'adopt' ? '/pets' : '/posts';
      let result = await apiRequest(endpoint, 'POST', task.postData);
      if (!result?.success && task.contentType === 'adopt') {
        result = await apiRequest('/posts', 'POST', task.postData);
      }
      if (result?.success) return true;
      if (db) {
        try {
          const targetCollection = task.contentType === 'adopt' ? 'pets' : 'posts';
          await db.collection(targetCollection).add(task.postData);
          return true;
        } catch (error) {
          return false;
        }
      }
      return false;
    }
    return true;
  };

  const flushOfflineQueue = async (force = false) => {
    if (isSyncingQueue || !syncQueue.length) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    setIsSyncingQueue(true);
    const now = Date.now();
    let workingQueue = [...syncQueue];
    const pending = [];

    for (const task of workingQueue) {
      const nextRetryAt = Number(task.nextRetryAt || 0);
      if (!force && nextRetryAt > now) {
        pending.push(task);
        continue;
      }
      const ok = await executeSyncTask(task);
      if (!ok) {
        const nextRetryCount = Number(task.retryCount || 0) + 1;
        if (nextRetryCount > OFFLINE_TASK_MAX_RETRY) {
          continue;
        }
        const backoffMs = OFFLINE_RETRY_BASE_MS * Math.pow(2, Math.max(0, nextRetryCount - 1));
        pending.push({
          ...task,
          retryCount: nextRetryCount,
          nextRetryAt: Date.now() + backoffMs
        });
      }
    }

    setSyncQueue(pending);
    saveQueueToStorage(pending);
    setIsSyncingQueue(false);
  };

  useEffect(() => {
    setSyncQueue(loadQueueFromStorage());
  }, []);

  useEffect(() => {
    const onOnline = () => flushOfflineQueue();
    if (typeof window !== 'undefined') {
      window.addEventListener('online', onOnline);
    }
    const timer = setInterval(() => {
      flushOfflineQueue();
    }, 15000);
    return () => {
      clearInterval(timer);
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onOnline);
      }
    };
  }, [syncQueue, isSyncingQueue]);

  useEffect(() => {
    setProfileBatchIds([]);
    setProfileBatchMode(false);
  }, [profileTab]);

  const refreshPublishDrafts = () => refreshPublishDraftsFromCloud(userProfile?.phone);

  useEffect(() => {
    refreshPublishDraftsFromCloud();
  }, []);

  useEffect(() => {
    if (currentView === 'profile' && profileTab === 'drafts') {
      refreshPublishDrafts();
    }
  }, [currentView, profileTab]);

  useEffect(() => {
    if (profileTab !== 'drafts') {
      setDraftBatchMode(false);
      setDraftBatchIds([]);
    }
  }, [profileTab]);

  // 启动时拉取维护配置；维护期间禁止自动恢复登录，管理员须在本会话重新验证
  useEffect(() => {
    const bootstrapSession = async () => {
      const forceReady = setTimeout(() => setMaintenanceReady(true), 8000);
      try {
        await ensureCloudDbReady();

        const normalizedCfg = await loadMergedMaintenanceConfig();
        setSiteMaintenance(normalizedCfg);
        setMaintenanceReady(true);

        if (isMaintenanceActive(normalizedCfg)) {
          clearMaintenanceAdminSession();
          setIsLoggedIn(false);
          setUserProfile(null);
          return;
        }

        let localProfile = null;
        try {
          localProfile = JSON.parse(localStorage.getItem('yugui_user') || 'null');
        } catch (error) {
          localProfile = null;
        }

        if (!localProfile?.phone) return;

      const isAdmin = localProfile.phone === OFFICIAL_ADMIN_PHONE;
      const cachedProfile = getCachedProfileByPhone(localProfile.phone) || {};
      const localMergedProfile = toAppProfile(
        localProfile,
        localProfile.phone,
        isAdmin,
        {
          city: localProfile.city,
          province: localProfile.province,
          locationData: localProfile.locationData,
          locationSource: localProfile.locationSource,
          locationAgreed: localProfile.locationAgreed
        },
        !!(localProfile.city || localProfile.locationData),
        {
          ...cachedProfile,
          ...localProfile
        }
      );
      setUserProfile(localMergedProfile);
      setIsLoggedIn(true);

      let profile = localMergedProfile;
      const cloudUser = db
        ? await fetchUserFromCloudByPhoneWithRetry(db, ensureCloudAnonymousAuth, localProfile.phone)
        : null;
      if (cloudUser) {
        profile = mergeCloudProfileIntoAppProfile(profile, cloudUser);
      }

      profile = applyAuthLocationToProfile(profile, profile, profile.locationAgreed);
      profile = await finalizeProfileUid(profile);

      const apiUser = await fetchProfileFromServerApi(apiRequest, localProfile.phone);
      if (apiUser) profile = mergeServerProfileIntoAppProfile(profile, apiUser);

      const lastSyncedAt = getProfileSyncAt(localProfile.phone);
      const shouldRefreshFromCloud = Date.now() - lastSyncedAt > PROFILE_SYNC_TTL_MS;
      if (shouldRefreshFromCloud) {
        const serverUser = await fetchUserFromApiByPhone(localProfile.phone);
        if (serverUser) {
          profile = toAppProfile(
            serverUser,
            localProfile.phone,
            isAdmin,
            {
              city: profile.city,
              province: profile.province,
              locationData: profile.locationData,
              locationSource: profile.locationSource,
              locationAgreed: profile.locationAgreed
            },
            !!(profile.city || profile.locationData),
            { ...cachedProfile, ...profile }
          );
          const freshCloudUser = db
            ? await fetchUserFromCloudByPhoneWithRetry(db, ensureCloudAnonymousAuth, localProfile.phone)
            : null;
          if (freshCloudUser) profile = mergeCloudProfileIntoAppProfile(profile, freshCloudUser);
        }
      }

      profile = await syncUserDataFromCloud(profile.phone, profile);
      localStorage.setItem('yugui_user', JSON.stringify(profile));
      saveCachedProfileByPhone(profile.phone, profile);
      setUserProfile(profile);
      await syncProfileToCloudDb(profile);
      await refreshPublishDraftsFromCloud(profile.phone);
      markProfileSynced(profile.phone);
      if (profile.locationAgreed) {
        refreshAutoLocationIfAllowed(profile);
      }
      } catch (error) {
        console.warn('启动初始化失败:', error);
        setMaintenanceReady(true);
      } finally {
        clearTimeout(forceReady);
      }
    };

    bootstrapSession();
  }, []);

  /** 维护模式开启/变更时，立即清退普通用户；管理员须本会话重新验证 */
  useEffect(() => {
    if (!maintenanceReady) return;
    enforceMaintenanceLockout(siteMaintenance, userProfile);
  }, [
    maintenanceReady,
    siteMaintenance.enabled,
    siteMaintenance.startAt,
    siteMaintenance.endAt,
    siteMaintenance.updatedAt,
    userProfile?.phone,
    userProfile?.role,
    isLoggedIn
  ]);

  /** 每 3 秒同步云端 + 服务端维护状态，确保一键开启后全员即时生效 */
  useEffect(() => {
    if (!maintenanceReady) return undefined;
    let cancelled = false;
    const tick = async () => {
      const cfg = await loadMergedMaintenanceConfig();
      if (cancelled) return;
      setSiteMaintenance((prev) => {
        if (
          prev.enabled === cfg.enabled &&
          prev.startAt === cfg.startAt &&
          prev.endAt === cfg.endAt &&
          prev.updatedAt === cfg.updatedAt
        ) {
          return prev;
        }
        return cfg;
      });
    };
    tick();
    const timer = setInterval(tick, 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [maintenanceReady]);

  // 资料/封禁状态兜底轮询（云监听异常时仍能较快同步）
  useEffect(() => {
    if (!userProfile?.phone) return undefined;
    const pullSelfProfileFromCloud = async () => {
      if (!(await ensureCloudDbReady())) return;
      const cloudUser = await fetchUserFromCloudByPhoneWithRetry(
        db,
        ensureCloudAnonymousAuth,
        userProfile.phone
      );
      if (cloudUser) syncSelfProfileFromCloudRow(cloudUser);
    };
    pullSelfProfileFromCloud();
    const timer = setInterval(pullSelfProfileFromCloud, 8000);
    return () => clearInterval(timer);
  }, [userProfile?.phone]);

  // 打开「我的」页时再从云端/服务器拉一次，确保换设备后资料与操作一致
  useEffect(() => {
    if (currentView !== 'profile' || !userProfile?.phone) return undefined;
    let cancelled = false;
    (async () => {
      const refreshed = await syncUserDataFromCloud(userProfile.phone, userProfile);
      if (!cancelled) applySessionProfile(userProfile.phone, refreshed);
    })();
    return () => {
      cancelled = true;
    };
  }, [currentView, userProfile?.phone]);

  useEffect(() => {
    if (currentView !== 'profile' || profileTab !== 'drafts' || !userProfile?.phone) return undefined;
    refreshPublishDraftsFromCloud(userProfile.phone);
  }, [currentView, profileTab, userProfile?.phone]);

  // 云数据库实时监听
  useEffect(() => {
    let pollingTimer = null;

    const refreshFeedsFromApi = async () => {
      const [postResult, petResult] = await Promise.all([
        apiRequest('/posts', 'GET'),
        apiRequest('/pets', 'GET')
      ]);

      const canUsePosts = !!postResult?.success;
      const canUsePets = !!petResult?.success;
      if (!canUsePosts && !canUsePets) return;

      const items = normalizeAndMergeFeeds(
        canUsePosts ? postResult : { posts: [] },
        canUsePets ? petResult : { posts: [] }
      );
      const apiPosts = items.filter((item) => (item.type || 'post') !== 'adopt');
      const apiAdopts = items.filter((item) => item.type === 'adopt');
      if (db) {
        setPosts((prev) => mergeFeedLists(prev, apiPosts));
        setAdoptList((prev) => mergeFeedLists(prev, apiAdopts));
      } else {
        setPosts(apiPosts);
        setAdoptList(apiAdopts);
      }
    };

    // watch 首包可能较晚或失败，首屏先拉一次云库，避免发现页长时间空白
    const bootstrapCloudFeeds = async () => {
      setFeedsLoading(true);
      setFeedsLoadError('');
      try {
        const [pr, petr] = await Promise.all([
          db.collection('posts').limit(200).get(),
          db.collection('pets').limit(200).get()
        ]);
        const mapRow = (doc, forceType) => ({
          ...doc,
          id: doc._id,
          type: forceType || doc.type || 'post',
          content: doc.content || doc.description || ''
        });
        const rowsP = filterActiveFeedItems((pr.data || []).map((d) => mapRow(d, 'post'))).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        const rowsPet = filterActiveFeedItems((petr.data || []).map((d) => mapRow(d, 'adopt'))).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setPosts(rowsP);
        setAdoptList(rowsPet);
      } catch (e) {
        console.warn('云库首屏 posts/pets 拉取失败:', e);
        setFeedsLoadError('内容加载失败，请检查网络');
        await refreshFeedsFromApi();
      } finally {
        setFeedsLoading(false);
      }
    };

    // 无云库时：全部由独立后端 API + 本地 SQLite 文件数据库提供
    if (!db) {
      const refreshPlatformFromApi = async () => {
        const [usersRes, annRes, reportsRes, feedbacksRes, notifRes, maintRes] =
          await Promise.all([
            apiRequest('/users', 'GET'),
            apiRequest('/announcements', 'GET'),
            apiRequest('/reports', 'GET'),
            apiRequest('/feedbacks', 'GET'),
            apiRequest('/notifications', 'GET'),
            fetchServerMaintenanceConfig(apiRequest)
          ]);
        if (usersRes?.success && Array.isArray(usersRes.users)) {
          setUsersList(expireTemporaryAdmins(usersRes.users));
        }
        if (annRes?.success && annRes.announcements) {
          setAnnouncements(sortAnnouncements(annRes.announcements));
        }
        if (reportsRes?.success && Array.isArray(reportsRes.reports)) {
          setReports([...reportsRes.reports].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
        }
        if (feedbacksRes?.success && Array.isArray(feedbacksRes.feedbacks)) {
          setFeedbackList(
            [...feedbacksRes.feedbacks].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
          );
        }
        if (notifRes?.success && Array.isArray(notifRes.notifications)) {
          setNotifications(
            [...notifRes.notifications].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
          );
        }
        if (maintRes) setSiteMaintenance(maintRes);
      };

      refreshFeedsFromApi().finally(() => setFeedsLoading(false));
      refreshPlatformFromApi();
      pollingTimer = setInterval(() => {
        refreshFeedsFromApi();
        refreshPlatformFromApi();
      }, 15000);
    }

    if (!db) {
      return () => {
        if (pollingTimer) clearInterval(pollingTimer);
      };
    }

    bootstrapCloudFeeds();
    refreshFeedsFromApi();
    pollingTimer = setInterval(refreshFeedsFromApi, 15000);

    // 帖子监听
    const postWatcher = db.collection('posts').watch({
      onChange: (snapshot) => {
        const cloudPosts = filterActiveFeedItems(
          snapshot.docs.map((d) => ({ ...d.data(), id: d.id, type: 'post' }))
        );
        setPosts((prev) => mergeFeedLists(prev, cloudPosts));
        setFeedsLoading(false);
        setFeedsLoadError('');
      },
      onError: (err) => {
        console.error('posts监听失败:', err);
        setFeedsLoadError('实时更新连接异常');
        setFeedsLoading(false);
      }
    });
    // 领养信息监听
    const petWatcher = db.collection('pets').watch({
      onChange: (snapshot) => {
        const cloudAdopts = filterActiveFeedItems(
          snapshot.docs.map((d) => ({ ...d.data(), id: d.id, type: 'adopt' }))
        );
        setAdoptList((prev) => mergeFeedLists(prev, cloudAdopts));
        setFeedsLoading(false);
      },
      onError: (err) => console.error('pets监听失败:', err)
    });
    // 用户列表监听 — 同步封禁/解封到当前登录用户
    const userWatcher = db.collection('users').watch({
      onChange: (snapshot) => {
        const users = expireTemporaryAdmins(snapshot.docs.map((d) => ({ ...d.data(), id: d.id })));
        setUsersList(users);
        const phone = userProfilePhoneRef.current;
        if (!phone) return;
        const selfRow = users.find((u) => String(u.phone) === String(phone));
        if (selfRow) syncSelfProfileFromCloudRow(selfRow);
      },
      onError: (err) => console.error('users监听失败:', err)
    });
    // 举报监听
    const reportWatcher = db.collection('reports').watch({
      onChange: (snapshot) => setReports(snapshot.docs.map(d => ({ ...d.data(), id: d.id })).sort((a,b) => b.createdAt - a.createdAt)),
      onError: (err) => console.error('reports监听失败:', err)
    });
    // 反馈监听
    const feedbackWatcher = db.collection('feedbacks').watch({
      onChange: (snapshot) => setFeedbackList(snapshot.docs.map(d => ({ ...d.data(), id: d.id })).sort((a,b) => b.createdAt - a.createdAt)),
      onError: (err) => console.error('feedback监听失败:', err)
    });
    // 公告监听 — 云端为准，撤回后立即全站生效
    const loadAnnouncements = async () => {
      if (db) return;
      const result = await apiRequest('/announcements', 'GET');
      if (result.success && result.announcements) {
        setAnnouncements(sortAnnouncements(result.announcements));
      }
    };
    loadAnnouncements();
    const annWatcher = db.collection('announcements').watch({
      onChange: (snapshot) =>
        setAnnouncements(
          sortAnnouncements(snapshot.docs.map((d) => ({ ...d.data(), id: d.id })))
        ),
      onError: (err) => console.error('announcements监听失败:', err)
    });

    const loadSiteMaintenance = async () => {
      const cfg = await fetchCloudMaintenanceConfig(db, ensureCloudAnonymousAuth);
      setSiteMaintenance(cfg);
    };
    loadSiteMaintenance();
    const maintenanceWatcher = db.collection('site_settings').watch({
      onChange: (snapshot) => {
        const hit = snapshot.docs.find(
          (d) => d.id === 'maintenance' || d.data()?.key === 'maintenance'
        );
        if (hit) {
          setSiteMaintenance(normalizeMaintenanceConfig({ ...hit.data(), _id: hit.id }));
        }
      },
      onError: (err) => console.error('site_settings监听失败:', err)
    });
    
    // 通知监听
    const notificationWatcher = db.collection('notifications').watch({
      onChange: (snapshot) => setNotifications(snapshot.docs.map(d => ({ ...d.data(), id: d.id })).sort((a,b) => b.createdAt - a.createdAt)),
      onError: (err) => console.error('notifications监听失败:', err)
    });

    return () => {
      if (pollingTimer) clearInterval(pollingTimer);
      postWatcher.close();
      petWatcher.close();
      userWatcher.close();
      reportWatcher.close();
      feedbackWatcher.close();
      annWatcher.close();
      maintenanceWatcher.close();
      notificationWatcher.close();
    };
  }, []);

  // 启动页倒计时
  useEffect(() => {
    if(showSplash) {
      const timer = setTimeout(() => setShowSplash(false), 5000); // 增加到5秒，确保用户能看到
      return () => clearTimeout(timer);
    }
  }, [showSplash]);

  // 启动页结束后，已登录但未确认公约/法律文件的用户须补确认
  useEffect(() => {
    if (showSplash || !isLoggedIn || !userProfile?.phone) return;
    if (needsConventionAcceptance(userProfile.phone) || needsLegalConsentRenewal(userProfile.phone)) {
      setShowConventionAcceptModal(true);
    }
  }, [showSplash, isLoggedIn, userProfile?.phone]);

  useEffect(() => {
    if (currentView !== 'profile' || !isLoggedIn) {
      setShowProfileBatchHint(false);
      return;
    }
    if (
      (profileTab === 'works' || profileTab === 'adopts') &&
      !hasUxHintSeen(UX_HINT_KEYS.profileBatch)
    ) {
      setShowProfileBatchHint(true);
    } else {
      setShowProfileBatchHint(false);
    }
  }, [currentView, profileTab, isLoggedIn]);

  // 发现页视频：仅播放当前可见条目；重登后须重新绑定 observer
  useEffect(() => {
    if (currentView !== 'home' || isSearching) return undefined;
    const viewport = document.querySelector('.feed-viewport');
    if (!viewport) return undefined;

    const tryPlayVisible = (video) => {
      const rect = video.getBoundingClientRect();
      const rootRect = viewport.getBoundingClientRect();
      const visibleH = Math.min(rect.bottom, rootRect.bottom) - Math.max(rect.top, rootRect.top);
      const ratio = rect.height > 0 ? visibleH / rect.height : 0;
      if (ratio >= 0.45) {
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      }
    };

    const videos = viewport.querySelectorAll('video[data-feed-video]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.45) {
            const p = video.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { root: viewport, threshold: [0, 0.45, 0.75, 1] }
    );
    videos.forEach((v) => {
      observer.observe(v);
      tryPlayVisible(v);
    });
    return () => observer.disconnect();
  }, [currentView, isSearching, isLoggedIn, posts, discoverFocusPostId]);

  // 桌面端鼠标滚轮：嵌套滚动区在 overflow:hidden 布局下需手动转发
  useEffect(() => {
    const seen = new Set();
    const panels = [];
    const add = (el) => {
      if (!el || seen.has(el)) return;
      seen.add(el);
      panels.push(el);
    };
    document.querySelectorAll('.feed-viewport, .yugui-scroll-panel, main .overflow-y-auto, main .overflow-y-scroll').forEach(add);
    const cleanups = panels.map((el) =>
      bindWheelScroll(el, { snapSlides: el.classList.contains('feed-viewport') })
    );
    return () => cleanups.forEach((off) => off());
  }, [currentView, isSearching, profileTab, posts.length, adoptList.length, feedsLoading]);

  // 管理后台：加载审计日志
  useEffect(() => {
    if (currentView !== 'admin') return;
    const loadAuditLogs = async () => {
      let rows = [];
      try {
        const res = await apiRequest('/admin/audit-logs?limit=120');
        if (res?.success && Array.isArray(res.logs)) {
          rows = res.logs.map((d) => ({ ...d, id: d.id || d._id }));
        }
      } catch (e) {
        console.warn('服务端 audit_logs 读取失败:', e);
      }
      if (db) {
        try {
          await ensureCloudAnonymousAuth();
          const r = await db.collection('audit_logs').limit(120).get();
          const cloudRows = (r.data || []).map((d) => ({ ...d, id: d._id || d.id }));
          const seen = new Set(rows.map((log) => `${log.action}|${log.createdAt}|${log.actorPhone}`));
          cloudRows.forEach((log) => {
            const key = `${log.action}|${log.createdAt}|${log.actorPhone}`;
            if (!seen.has(key)) {
              seen.add(key);
              rows.push(log);
            }
          });
        } catch (e) {
          console.warn('audit_logs 云库读取失败:', e);
        }
      }
      if (!rows.length) {
        try {
          const parsed = JSON.parse(localStorage.getItem('yugui_audit_log_buffer') || '[]');
          rows = Array.isArray(parsed) ? parsed : [];
        } catch (_) {
          rows = [];
        }
      }
      rows.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setAuditLogs(rows.slice(0, 120));
    };
    loadAuditLogs();

    const loadAdminContent = async () => {
      let rows = [...posts, ...adoptList];
      if (db) {
        try {
          await ensureCloudAnonymousAuth();
          const [postRes, petRes] = await Promise.all([
            db.collection('posts').limit(300).get(),
            db.collection('pets').limit(300).get()
          ]);
          const postRows = (postRes.data || []).map((d) => ({
            ...d,
            id: d._id || d.id,
            type: 'post'
          }));
          const petRows = (petRes.data || []).map((d) => ({
            ...d,
            id: d._id || d.id,
            type: 'adopt'
          }));
          rows = [...postRows, ...petRows];
        } catch (e) {
          console.warn('管理后台内容拉取失败:', e);
        }
      }
      rows.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setAdminAllContent(rows);
    };
    loadAdminContent();

    const loadBanRequests = async () => {
      if (!db || !isAdminProfile(userProfile)) return;
      try {
        await ensureCloudAnonymousAuth();
        const r = await db.collection('ban_requests').limit(100).get();
        const rows = (r.data || [])
          .map((d) => ({ ...d, id: d._id || d.id }))
          .filter((item) => item.status === 'pending')
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setBanRequests(rows);
      } catch (e) {
        console.warn('ban_requests 读取失败:', e);
        setBanRequests([]);
      }
    };
    loadBanRequests();
  }, [currentView, userProfile?.role, userProfile?.phone]);

  // 核心业务逻辑
  const patchFeedItem = (postId, patch) => {
    const sid = String(postId);
    setPosts((prev) =>
      prev.map((item) => (String(item.id) === sid ? { ...item, ...patch } : item))
    );
    setAdoptList((prev) =>
      prev.map((item) => (String(item.id) === sid ? { ...item, ...patch } : item))
    );
  };

  const handleLike = async (post, e) => {
    e?.stopPropagation();
    if (!requireAuth()) return;

    const actorId = primaryActorKey(userProfile) || String(userProfile?.id || userProfile?.uid || userProfile?.phone || '');
    const prevLikedBy = Array.isArray(post.likedBy) ? post.likedBy.map((id) => String(id)) : [];
    const hadLiked = prevLikedBy.includes(actorId);
    const nextLikedBy = hadLiked ? prevLikedBy.filter((id) => id !== actorId) : [...prevLikedBy, actorId];
    const nextLikes = nextLikedBy.length;

    patchFeedItem(post.id, { likedBy: nextLikedBy, likes: nextLikes });

    const likeNotification = {
      type: 'like',
      title: '作品被点赞',
      content: `${userProfile?.name || '用户'} 点赞了你的作品`,
      targetUserId: post.userId,
      sourceUserId: userProfile?.id,
      postId: post.id,
      createdAt: Date.now(),
      read: false
    };

    if (!hadLiked && String(post.userId || '') !== actorId) {
      setNotifications(prev => [likeNotification, ...prev]);
      const notifySynced = await syncNotificationToCloud(likeNotification);
      if (!notifySynced) {
        pushOfflineTask({ type: 'notification', notification: likeNotification });
      }
    }

    const interactSynced = await syncPostInteraction(post, {
      action: hadLiked ? 'unlike' : 'like',
      actorId
    }, post.type === 'adopt' ? 'adopt' : 'post');
    const synced = interactSynced || await syncPostPatch(post, {
      likedBy: nextLikedBy,
      likes: nextLikes
    }, post.type === 'adopt' ? 'adopt' : 'post');
    if (!synced) {
      pushOfflineTask({
        type: 'postPatch',
        item: post,
        patch: { likedBy: nextLikedBy, likes: nextLikes },
        contentType: post.type === 'adopt' ? 'adopt' : 'post'
      });
    }

    const contentKey = buildContentKey(post);
    if (contentKey) {
      const prevKeys = Array.isArray(userProfile?.likedContentKeys) ? userProfile.likedContentKeys : [];
      const nextKeys = hadLiked
        ? prevKeys.filter((key) => String(key) !== contentKey)
        : [...new Set([...prevKeys.map(String), contentKey])];
      await persistUserPatchToServer({ likedContentKeys: nextKeys });
    }

    showToast(
      hadLiked
        ? (synced ? "已取消点赞" : "已取消点赞，云端同步稍后完成")
        : (synced ? "点赞成功！" : "点赞成功，云端同步稍后完成")
    );
  };

  const handleBookmark = async (post, e) => {
    e?.stopPropagation();
    if (!requireAuth()) return;

    const actorId = primaryActorKey(userProfile) || String(userProfile?.id || userProfile?.uid || userProfile?.phone || '');
    const prevBookmarkedBy = Array.isArray(post.bookmarkedBy) ? post.bookmarkedBy.map((id) => String(id)) : [];
    const hadBookmarked = prevBookmarkedBy.includes(actorId);
    const nextBookmarkedBy = hadBookmarked
      ? prevBookmarkedBy.filter((id) => id !== actorId)
      : [...prevBookmarkedBy, actorId];

    patchFeedItem(post.id, { bookmarkedBy: nextBookmarkedBy });

    const bookmarkNotification = {
      type: 'bookmark',
      title: '作品被收藏',
      content: `${userProfile?.name || '用户'} 收藏了你的作品`,
      targetUserId: post.userId,
      sourceUserId: userProfile?.id,
      postId: post.id,
      createdAt: Date.now(),
      read: false
    };

    if (!hadBookmarked && String(post.userId || '') !== actorId) {
      setNotifications(prev => [bookmarkNotification, ...prev]);
      const notifySynced = await syncNotificationToCloud(bookmarkNotification);
      if (!notifySynced) {
        pushOfflineTask({ type: 'notification', notification: bookmarkNotification });
      }
    }

    const interactSynced = await syncPostInteraction(post, {
      action: hadBookmarked ? 'unbookmark' : 'bookmark',
      actorId
    }, post.type === 'adopt' ? 'adopt' : 'post');
    const synced = interactSynced || await syncPostPatch(post, {
      bookmarkedBy: nextBookmarkedBy
    }, post.type === 'adopt' ? 'adopt' : 'post');
    if (!synced) {
      pushOfflineTask({
        type: 'postPatch',
        item: post,
        patch: { bookmarkedBy: nextBookmarkedBy },
        contentType: post.type === 'adopt' ? 'adopt' : 'post'
      });
    }

    const contentKey = buildContentKey(post);
    if (contentKey) {
      const prevKeys = Array.isArray(userProfile?.bookmarkedContentKeys)
        ? userProfile.bookmarkedContentKeys
        : [];
      const nextKeys = hadBookmarked
        ? prevKeys.filter((key) => String(key) !== contentKey)
        : [...new Set([...prevKeys.map(String), contentKey])];
      await persistUserPatchToServer({ bookmarkedContentKeys: nextKeys });
    }

    showToast(
      hadBookmarked
        ? (synced ? "已取消收藏" : "已取消收藏，云端同步稍后完成")
        : (synced ? "收藏成功！" : "收藏成功，云端同步稍后完成")
    );
  };

  const handleFollow = async (targetUserId, e) => {
    e?.stopPropagation();
    if (!requireAuth()) return;
    const actorIds = [userProfile?.id, userProfile?.uid, userProfile?.phone].filter(Boolean).map((id) => String(id));
    const normalizedTargetId = String(targetUserId || '');
    if (!normalizedTargetId) return;
    if (actorIds.includes(normalizedTargetId)) return showToast("不能关注自己");

    const currentFollowing = Array.isArray(userProfile?.followingList)
      ? userProfile.followingList.map((id) => String(id))
      : [];
    const isFollowing = currentFollowing.includes(normalizedTargetId);
    const nextFollowingList = isFollowing
      ? currentFollowing.filter((id) => id !== normalizedTargetId)
      : [...currentFollowing, normalizedTargetId];

    setUserProfile(prev => ({
      ...prev,
      followingList: nextFollowingList
    }));

    if (isFollowing) {
      const followSynced = await syncFollowState(nextFollowingList, normalizedTargetId, true);
      if (!followSynced) {
        pushOfflineTask({
          type: 'follow',
          nextFollowingList,
          targetUserId: normalizedTargetId,
          isFollowing: true
        });
      }
      showToast("取消关注成功！");
    } else {
      const followNotification = {
        type: 'follow',
        title: '被关注',
        content: `${userProfile?.name || '用户'} 关注了你`,
        targetUserId: normalizedTargetId,
        sourceUserId: userProfile?.id,
        createdAt: Date.now(),
        read: false
      };
      setNotifications(prev => [followNotification, ...prev]);
      const notifySynced = await syncNotificationToCloud(followNotification);
      if (!notifySynced) {
        pushOfflineTask({ type: 'notification', notification: followNotification });
      }
      const followSynced = await syncFollowState(nextFollowingList, normalizedTargetId, false);
      if (!followSynced) {
        pushOfflineTask({
          type: 'follow',
          nextFollowingList,
          targetUserId: normalizedTargetId,
          isFollowing: false
        });
      }
      showToast("关注成功！");
    }
  };

  // 更新用户资料
  const persistLocationProfile = async (locationPatch, toastMessage) => {
    if (!userProfile) return false;
    const merged = applyAuthLocationToProfile(
      { ...userProfile, ...locationPatch },
      locationPatch,
      locationPatch.locationAgreed ?? userProfile.locationAgreed
    );
    setUserProfile(merged);
    localStorage.setItem('yugui_user', JSON.stringify(merged));
    saveCachedProfileByPhone(merged.phone, merged);
    await syncProfileToCloudDb(merged);
    await persistUserPatchToServer(
      {
        location: merged.location,
        city: merged.city,
        province: merged.province,
        locationSource: merged.locationSource,
        locationAgreed: merged.locationAgreed
      },
      merged
    );
    if (toastMessage) showToast(toastMessage);
    return true;
  };

  const handleToggleLocationAutoDetect = async (enabled) => {
    const next = setAutoLocationConsent(userProfile, enabled);
    await persistLocationProfile(
      next,
      enabled ? '已开启随时自动定位' : '已关闭自动定位，当前城市仍保留在云端'
    );
  };

  const handleRefreshLocation = async () => {
    if (!userProfile) return;
    try {
      const detected = await detectUserLocation();
      const patch = buildLocationProfile({
        ...detected,
        locationAgreed: userProfile.locationAgreed !== false ? true : false
      });
      await persistLocationProfile(patch, `已更新为 ${patch.location}`);
    } catch (error) {
      showToast(getGeolocationErrorMessage(error), 'warning');
    }
  };

  const refreshAutoLocationIfAllowed = async (profile) => {
    if (!profile?.locationAgreed || !profile?.phone) return profile;
    try {
      const detected = await detectUserLocation();
      const patch = buildLocationProfile({ ...detected, locationAgreed: true });
      const merged = applyAuthLocationToProfile(profile, patch, true);
      localStorage.setItem('yugui_user', JSON.stringify(merged));
      saveCachedProfileByPhone(merged.phone, merged);
      await syncProfileToCloudDb(merged);
      setUserProfile(merged);
      return merged;
    } catch (error) {
      console.warn('后台自动定位失败:', error);
      return profile;
    }
  };

  const handleUpdateProfile = async (updatedProfile) => {
    const profileUpdatedAt = Date.now();
    const newProfile = {
      ...userProfile,
      ...updatedProfile,
      profileUpdatedAt
    };
    if (
      updatedProfile.city ||
      updatedProfile.location ||
      updatedProfile.locationAgreed != null
    ) {
      Object.assign(
        newProfile,
        applyAuthLocationToProfile(newProfile, updatedProfile, updatedProfile.locationAgreed)
      );
    }
    setUserProfile(newProfile);
    localStorage.setItem('yugui_user', JSON.stringify(newProfile));
    saveCachedProfileByPhone(newProfile.phone, newProfile);

    let synced = false;
    const apiUser = await syncProfileToServerApi(apiRequest, newProfile, updatedProfile);
    if (apiUser) {
      Object.assign(newProfile, mergeServerProfileIntoAppProfile(newProfile, apiUser));
      synced = true;
    }
    for (let attempt = 0; attempt < 3 && !synced; attempt += 1) {
      synced = await syncProfileToCloudDb(newProfile);
      if (!synced) await sleep(400 * (attempt + 1));
    }
    if (apiUser) await syncProfileToCloudDb(newProfile);
    const refreshed = await syncUserDataFromCloud(newProfile.phone, newProfile);
    applySessionProfile(refreshed.phone, refreshed);
    setUsersList((prev) =>
      prev.map((u) =>
        String(u.phone) === String(refreshed.phone)
          ? {
              ...u,
              name: refreshed.name,
              bio: refreshed.bio,
              avatar: refreshed.avatar,
              coverImg: refreshed.coverImg,
              uid: refreshed.uid
            }
          : u
      )
    );

    if (!apiUser && !synced) {
      showToast('资料已保存，云端同步稍后自动重试', 'warning');
    }
  };

  const lastCommentSubmitRef = useRef({ key: '', at: 0 });

  // 添加评论（防抖重入：连点只生效一次）
  const handleAddComment = async (postId, comment) => {
    const text = (comment?.content || '').trim();
    const compliance = scanCommentCompliance(text);
    if (!compliance.ok) {
      showToast(complianceBlockMessage(compliance), 'warning');
      await recordContentBlocked(compliance, '拦截评论');
      return false;
    }

    const dedupeKey = `${postId}|${text}|${comment?.userId}`;
    const now = Date.now();
    if (lastCommentSubmitRef.current.key === dedupeKey && now - lastCommentSubmitRef.current.at < 4000) {
      return true;
    }
    lastCommentSubmitRef.current = { key: dedupeKey, at: now };

    let matchedPost =
      posts.find((post) => String(post.id) === String(postId)) ||
      adoptList.find((post) => String(post.id) === String(postId)) ||
      null;

    if (matchedPost) {
      const nextComments = [...(matchedPost.comments || []), comment];
      patchFeedItem(postId, {
        comments: nextComments,
        commentsCount: nextComments.length
      });
    } else {
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          matchedPost = post;
          return {
            ...post,
            comments: [...(post.comments || []), comment],
            commentsCount: (post.commentsCount || 0) + 1
          };
        }
        return post;
      }));
    }

    setSelectedPost((prev) => {
      if (!prev || String(prev.id) !== String(postId)) return prev;
      return {
        ...prev,
        comments: [...(prev.comments || []), comment],
        commentsCount: (prev.commentsCount || 0) + 1
      };
    });

    if (!matchedPost) return true;
    const nextComments = [...(matchedPost.comments || []), comment];
    const interactSynced = await syncPostInteraction(matchedPost, {
      action: 'comment',
      comment
    }, matchedPost.type === 'adopt' ? 'adopt' : 'post');
    const synced = interactSynced || await syncPostPatch(matchedPost, {
      comments: nextComments,
      commentsCount: nextComments.length
    }, matchedPost.type === 'adopt' ? 'adopt' : 'post');
    if (!synced) {
      pushOfflineTask({
        type: 'postPatch',
        item: matchedPost,
        patch: { comments: nextComments, commentsCount: nextComments.length },
        contentType: matchedPost.type === 'adopt' ? 'adopt' : 'post'
      });
      showToast('评论已发布，云端同步稍后完成');
    }
    await recordAudit({
      action: AUDIT_ACTIONS.COMMENT,
      targetType: matchedPost.type === 'adopt' ? 'adopt' : 'post',
      targetId: postId,
      summary: '发表评论',
      snapshot: { contentPreview: (comment?.content || '').slice(0, 200) }
    });
    return synced;
  };

  // 处理搜索输入变化
  const handleSearchInputChange = async (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (value.trim()) {
      const results = await filterSearchResults(value);
      setSearchResults(results);
      setShowSearchResults(true);
    } else {
      setSearchResults({ works: [], adopts: [], users: [], documents: [] });
      setShowSearchResults(false);
    }
  };

  // 处理搜索
  const handleSearch = async () => {
    if (!searchQuery.trim()) return showToast('请输入搜索关键词');
    const results = await filterSearchResults(searchQuery);
    setSearchResults(results);
    setIsSearching(true);
    setShowSearch(true);
  };

  // 关闭搜索
  const handleCloseSearch = () => {
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults({ works: [], adopts: [], users: [], documents: [] });
    setShowSearchResults(false);
    setIsSearching(false);
    setSearchTab('works');
  };

  const openSearchDocument = (docId) => {
    closeAllOverlays();
    setSearchQuery('');
    setSearchResults({ works: [], adopts: [], users: [], documents: [] });
    setSearchTab('works');
    if (docId === 'convention') openPlatformConvention();
    else if (docId === 'privacy') openPrivacyPolicy();
    else if (docId === 'agreement') openUserAgreement();
  };

  const handlePublish = async (formData, type, opts = {}) => {
    const silent = !!opts.silent;
    try {
    if (!userProfile) {
      if (!silent) showToast('请先登录后再发布', 'error');
      return false;
    }

    if (!silent && (formData?.rawFile || formData?.isVideo)) {
      showToast('正在处理视频，大约需要 10–60 秒，请勿关闭页面…', 'warning', 90000);
    } else if (!silent) {
      showToast('正在发布…', 'warning', 15000);
    }

    const compliance = scanPublishFormCompliance(formData);
    if (!compliance.ok) {
      if (!silent) showToast(complianceBlockMessage(compliance), 'warning');
      await recordContentBlocked(compliance, '拦截发布');
      return false;
    }

    const publisherId = userProfile.id || userProfile.uid || userProfile.phone;
    let mediaUrl = formData.img || formData.images?.[0] || formData.image;
    if (formData.rawFile) {
      // 直接将本地文件转为可发布地址，避免云存储权限问题导致发布中断。
      const fallbackDataUrl = await fileToDataUrl(formData.rawFile);
      if (fallbackDataUrl) {
        mediaUrl = fallbackDataUrl;
      } else {
        if (!silent) showToast("媒体处理失败，请重新选择文件后发布");
        return false;
      }
    }
    if (typeof mediaUrl === 'string' && mediaUrl.startsWith('blob:')) {
      if (!silent) showToast("媒体地址无效，请重新上传后发布");
      return false;
    }
    if (!mediaUrl) {
      if (!silent) showToast("发布失败：请先上传照片或视频！");
      return false;
    }
    const isVideoMedia =
      !!formData.isVideo ||
      (typeof mediaUrl === 'string' && mediaUrl.startsWith('data:video'));
    if (!isVideoMedia && typeof mediaUrl === 'string' && mediaUrl.startsWith('data:') && mediaUrl.length > 650000) {
      if (!silent) showToast("图片体积仍然偏大，请换一张更小的图片后重试");
      return false;
    }
    if (isVideoMedia && typeof mediaUrl === 'string' && mediaUrl.length > 12_000_000) {
      if (!silent) showToast("视频编码后体积过大，请缩短时长或选用更低分辨率后再试");
      return false;
    }

    mediaUrl = await shrinkDataUrlForCloud(mediaUrl);

    // 云端单条文档只保留一份图片字段，避免同一张 base64 重复 5 倍导致超限失败
    const cloudPostData = {
      userId: publisherId,
      user: userProfile.name,
      uid: userProfile.uid,
      phone: userProfile.phone,
      avatar: userProfile.avatar || '',
      type: type === 'adopt' ? 'adopt' : 'post',
      title: formData.title || '',
      content: formData.content,
      img: mediaUrl,
      isVideo: isVideoMedia,
      name: formData.name || '',
      petType: formData.petType,
      age: formData.age,
      gender: formData.gender,
      health: formData.health,
      tags: formData.tags,
      visibility: formData.visibility || 'public',
      ...(type === 'adopt' && userProfile.city
        ? {
            city: userProfile.city,
            province: userProfile.province || '',
            location: userProfile.location,
            userLocation: userProfile.city,
            locationData: userProfile.locationData || null
          }
        : {}),
      createdAt: Date.now()
    };
    // 兼容后端 API 的冗余字段（仅用于 HTTP，不写入云库单条）
    const postData = {
      ...cloudPostData,
      image: mediaUrl,
      imageUrl: mediaUrl,
      cover: mediaUrl,
      media: mediaUrl,
      description: formData.content,
      status: 'published'
    };

    const targetCollection = type === 'adopt' ? 'pets' : 'posts';
    let publishedDocId = '';
    let cloudOk = false;
    let apiOk = false;
    let cloudErrorMsg = '';
    let apiErrorMsg = '';

    const cloudInit = tryInitCloudDb();
    const cloudSkippedReason = cloudInit.ok ? '' : (cloudInit.reason || '');

    if (db) {
      try {
        await ensureCloudAnonymousAuth();
        const addResult = await db.collection(targetCollection).add(cloudPostData);
        const newId =
          addResult?.id ||
          addResult?._id ||
          (typeof addResult === 'string' ? addResult : null) ||
          `${targetCollection}_${Date.now()}`;
        publishedDocId = String(newId);
        cloudOk = true;
      } catch (cloudError) {
        console.error('云数据库发布失败:', cloudError);
        cloudErrorMsg =
          cloudError?.message ||
          cloudError?.errMsg ||
          (typeof cloudError === 'string' ? cloudError : '') ||
          '云数据库写入失败';
        if (/network/i.test(String(cloudErrorMsg))) {
          cloudErrorMsg +=
            '。请到控制台核对：1) Web 安全域名已添加当前站点（保存后约 10 分钟生效）；2) 匿名登录已开；3) posts/pets 可写；4) 环境地域与 REACT_APP_CLOUD_REGION 一致（不设则默认上海）';
        }
      }
    }

    // 始终写入服务器文件库，避免仅入云库后刷新丢失
    const endpoint = type === 'adopt' ? '/pets' : '/posts';
    const apiPayload = {
      ...postData,
      ...(publishedDocId ? { id: publishedDocId } : {})
    };
    let apiResult = await apiRequest(endpoint, 'POST', apiPayload);
    if (!apiResult?.success && type === 'adopt') {
      apiResult = await apiRequest('/posts', 'POST', apiPayload);
    }
    if (apiResult?.success) {
      apiOk = true;
      publishedDocId = String(
        apiResult.id ||
          apiResult.data?.id ||
          apiResult.post?.id ||
          publishedDocId ||
          `${targetCollection}_${Date.now()}`
      );
    } else {
      apiErrorMsg = apiResult?.message || '';
    }

    const result = {
      success: cloudOk || apiOk,
      message: cloudOk && apiOk
        ? '已发布'
        : cloudOk
          ? '已发布到云数据库（服务器同步失败，刷新后可能不可见）'
          : apiOk
            ? '已发布到服务器'
            : ''
    };
    
    if (result.success) {
      if (!silent) {
        if (cloudOk && !apiOk) {
          showToast('发布成功，但服务器未同步，请部署最新后端后重新发布', 'warning');
        } else {
          showToast('发布成功！');
        }
      }
      const createdItem = {
        ...cloudPostData,
        id: publishedDocId,
        type: type === 'adopt' ? 'adopt' : 'post'
      };
      if (type === 'adopt') {
        setAdoptList((prev) => [
          createdItem,
          ...prev.filter((p) => String(p.id) !== String(createdItem.id))
        ]);
      } else {
        setPosts((prev) => [
          createdItem,
          ...prev.filter((p) => String(p.id) !== String(createdItem.id))
        ]);
      }
      await recordAudit({
        action: AUDIT_ACTIONS.PUBLISH,
        targetType: type === 'adopt' ? 'adopt' : 'post',
        targetId: publishedDocId || String(cloudPostData.createdAt),
        summary: type === 'adopt' ? `发布领养：${formData.name}` : `发布动态：${formData.title || '无标题'}`,
        snapshot: buildContentSnapshot({
          ...cloudPostData,
          id: publishedDocId,
          type: type === 'adopt' ? 'adopt' : 'post'
        })
      });
      if (opts.draftId) {
        await removePublishDrafts([opts.draftId]);
      }
      setPublishEditingDraftId(null);
      if (!silent) {
        setShowPublishModal(false);
        setCurrentView('home');
        setIsSearching(false);
        setShowSearch(false);
      }
      return true;
    } else {
      const apiMsg = apiErrorMsg || '';
      const apiHint =
        apiMsg &&
        (apiMsg.includes('连接服务失败') || apiMsg.includes('请求超时'))
          ? `${apiMsg}（请确认服务器已配置 Nginx /api 反向代理且后端在运行）`
          : apiMsg;
      const failReason =
        cloudErrorMsg ||
        cloudSkippedReason ||
        apiHint ||
        '发布失败，请稍后重试';
      showToast(`发布失败：${failReason}`);
    }
    return false;
    } catch (error) {
      console.error('发布异常:', error);
      if (!silent) showToast('发布异常，请稍后重试', 'error');
      return false;
    }
  };

  const finalizeProfileUid = async (profile) => {
    if (!profile?.phone) return profile;
    const isAdmin = profile.phone === OFFICIAL_ADMIN_PHONE;
    try {
      const uid = await ensurePermanentUid(
        db,
        ensureCloudAnonymousAuth,
        profile.phone,
        isAdmin,
        profile.uid
      );
      if (!uid || uid === profile.uid) return profile;
      const legacyUids = appendLegacyUids(
        profile.legacyUids,
        profile.uid && profile.uid !== uid ? profile.uid : null
      );
      const next = { ...profile, uid, legacyUids };
      localStorage.setItem('yugui_user', JSON.stringify(next));
      saveCachedProfileByPhone(next.phone, next);
      await syncProfileToCloudDb(next);
      return next;
    } catch (error) {
      console.warn('UID 固化失败:', error);
      return profile;
    }
  };

  const openUserByProfile = (targetUser) => {
    if (!targetUser) return;
    const matched =
      usersList.find((user) =>
        [user.id, user.uid, user.phone, user._id]
          .filter(Boolean)
          .some((value) =>
            [targetUser.id, targetUser.uid, targetUser.phone, targetUser._id]
              .filter(Boolean)
              .some((key) => String(key) === String(value))
          )
      ) || targetUser;
    if (
      userProfile &&
      [matched.id, matched.uid, matched.phone, matched._id]
        .filter(Boolean)
        .some((key) => profileMatchesUserKey(userProfile, key))
    ) {
      setViewingUser(null);
      navigateTo('profile');
      return;
    }
    setViewingUser(matched);
    setWorkInteractionPost(null);
    navigateTo('user');
  };

  const filterSearchResults = async (query) => {
    const trimmed = query.trim();
    if (!trimmed) return { works: [], adopts: [], users: [], documents: [] };
    const qLower = trimmed.toLowerCase();
    const qUpper = normalizeUidInput(trimmed);
    const exactUidSearch = UID_PATTERN.test(qUpper);

    const matchText = (value) => value != null && String(value).toLowerCase().includes(qLower);
    const matchTags = (tags) =>
      Array.isArray(tags) && tags.some((tag) => String(tag).toLowerCase().includes(qLower));

    const searchableDocs = [
      { id: 'convention', title: '予归平台公约', hint: '平台行为准则与领养规范', keywords: ['平台公约', '公约', '规范', '机构规范', '领养规范', '机构'] },
      { id: 'privacy', title: '予归隐私保护政策', hint: '个人信息处理说明', keywords: ['隐私', '隐私政策'] },
      { id: 'agreement', title: '予归用户服务与免责协议', hint: '服务条款与免责声明', keywords: ['用户协议', '免责', '服务协议', '协议'] },
    ];
    const filteredDocuments = searchableDocs.filter((doc) => {
      if (doc.title.includes(trimmed)) return true;
      return doc.keywords.some(
        (keyword) =>
          keyword.includes(trimmed) ||
          trimmed.includes(keyword) ||
          keyword.toLowerCase().includes(qLower) ||
          qLower.includes(keyword.toLowerCase())
      );
    });

    const activeWorks = filterActiveFeedItems(posts.filter((item) => item.type !== 'adopt'));
    const activeAdopts = filterActiveFeedItems(adoptList.filter((item) => item.type === 'adopt'));

    const filteredWorks = activeWorks.filter((post) =>
      matchText(post.title) ||
      matchText(post.content) ||
      matchText(post.user) ||
      matchTags(post.tags)
    );

    const filteredAdopts = activeAdopts.filter((pet) =>
      matchText(pet.name) ||
      matchText(pet.title) ||
      matchText(pet.content) ||
      matchText(pet.description) ||
      matchText(pet.petType) ||
      matchText(pet.age) ||
      matchText(pet.gender) ||
      matchText(pet.health) ||
      matchText(pet.city) ||
      matchText(pet.province) ||
      matchText(pet.location) ||
      matchText(pet.userLocation) ||
      matchTags(pet.tags)
    );

    let filteredUsers = usersList.filter((user) => {
      if (exactUidSearch) return normalizeUidInput(user.uid) === qUpper;
      const nameMatch = matchText(user.name);
      const uidMatch = normalizeUidInput(user.uid).includes(qUpper);
      return nameMatch || uidMatch;
    });

    if (exactUidSearch && filteredUsers.length === 0 && db) {
      const cloudUser = await fetchUserFromCloudByUid(db, ensureCloudAnonymousAuth, qUpper);
      if (cloudUser) {
        filteredUsers = [{ ...cloudUser, id: cloudUser._cloudId || cloudUser.id || cloudUser._id }];
      }
    }

    return { works: filteredWorks, adopts: filteredAdopts, users: filteredUsers, documents: filteredDocuments };
  };

  const searchTabMeta = {
    works: { label: '作品', placeholder: '搜索作品标题 / 内容 / 标签' },
    adopt: { label: '领养', placeholder: '搜索宠物名 / 品种 / 城市 / 健康状况' },
    users: { label: '用户', placeholder: '搜索昵称 / 8位用户ID' },
  };

  const getSearchEmptyHint = (tab) => {
    if (tab === 'works') return '试试作品标题、正文关键词或标签';
    if (tab === 'adopt') return '试试宠物名字、品种、城市或健康状况';
    return '试试用户昵称或 8 位用户 ID';
  };

  const isValidPhone = (phone) => /^1[3-9]\d{9}$/.test(String(phone || '').replace(/\D/g, ''));

  const handleSendSmsCode = async (phone, purpose) => {
    const result = await apiRequest('/sms/send', 'POST', { phone, purpose });
    if (!result?.success) {
      showToast(result?.message || '验证码发送失败');
      return false;
    }
    showToast(result.message || `验证码已发送（内测固定码 ${DEV_SMS_CODE}）`);
    return true;
  };

  const handleChangePassword = async ({ code, newPassword }) => {
    const phone = userProfile?.phone;
    if (!phone) throw new Error('请先登录');

    const result = await apiRequest('/user/change-password', 'POST', {
      phone,
      code,
      newPassword
    });

    if (!result?.success) {
      throw new Error(result?.message || '密码修改失败');
    }

    try {
      const saved = localStorage.getItem('yugui_saved_account');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.phone === phone) {
          localStorage.setItem(
            'yugui_saved_account',
            JSON.stringify({ ...parsed, pwd: newPassword })
          );
        }
      }
    } catch (_) {
      /* ignore */
    }
  };

  const handleCloudAuth = async (phone, mode, password, code, loginMethod, setModeFunc, locationInfo, locationAgreed) => {
    console.log('Auth attempt:', { phone, mode, loginMethod, locationAgreed, locationInfo });

    const activeMaintenanceCfg = db
      ? normalizeMaintenanceConfig(await fetchCloudMaintenanceConfig(db, ensureCloudAnonymousAuth))
      : siteMaintenance;
    if (db) setSiteMaintenance(activeMaintenanceCfg);

    if (mode === 'register') {
      if (isMaintenanceActive(activeMaintenanceCfg)) {
        return showToast('网站维护中，暂停新用户注册');
      }
      if (!isValidPhone(phone)) return showToast("请输入真实有效的11位手机号");
      if (!password || password.length < 6) return showToast("密码至少6位");

      const isAdmin = phone === OFFICIAL_ADMIN_PHONE;
      const permanentUid = await ensurePermanentUid(db, ensureCloudAnonymousAuth, phone, isAdmin, null);

      const result = await apiRequest('/register', 'POST', {
        phone,
        password,
        uid: permanentUid,
        name: locationAgreed && locationInfo?.city ? `用户_${phone.slice(-4)}` : undefined,
        location: locationInfo?.location || UNKNOWN_LOCATION,
        city: locationInfo?.city || '',
        province: locationInfo?.province || '',
        locationData: locationInfo?.locationData || null,
        locationSource: locationInfo?.locationSource || '',
        locationAgreed: !!locationAgreed
      });

      if (!result.success) {
        return showToast(result.message);
      }

      const cachedProfile = getCachedProfileByPhone(phone) || {};
      let newProfile = await resolveLoginProfile({
        phone,
        serverUser: result.user,
        locationInfo,
        locationAgreed,
        cachedProfile,
        applySession: false
      });
      if (await rejectIfMaintenanceBlocksProfile(newProfile)) {
        setUserProfile(null);
        setIsLoggedIn(false);
        localStorage.removeItem('yugui_user');
        return;
      }

      applySessionProfile(phone, newProfile);
      setIsLoggedIn(true);
      await syncUserDataFromCloud(phone, newProfile);
      await writeAuditLog(db, ensureCloudAnonymousAuth, {
        action: AUDIT_ACTIONS.REGISTER,
        actorId: newProfile.id || newProfile.uid,
        actorPhone: phone,
        actorName: newProfile.name,
        summary: '用户注册'
      });
      await syncProfileToCloudDb(newProfile);
      setShowAuthModal(false);
      beginAuthenticatedSession(newProfile.phone);
      showToast(isAdmin ? "欢迎回来，超级管理员！" : "注册成功！");

    } else if (mode === 'login') {
      if (!isValidPhone(phone)) return showToast('请输入真实有效的11位手机号');
      if (!password) return showToast('请输入密码');

      const result = await apiRequest('/login', 'POST', { phone, password });
      if (!result.success) return showToast(result.message);

      const isAdmin = phone === OFFICIAL_ADMIN_PHONE;
      let loggedInProfile = await resolveLoginProfile({
        phone,
        serverUser: result.user,
        locationInfo,
        locationAgreed,
        applySession: false
      });
      if (await rejectIfMaintenanceBlocksProfile(loggedInProfile)) {
        setUserProfile(null);
        setIsLoggedIn(false);
        localStorage.removeItem('yugui_user');
        return;
      }

      applySessionProfile(phone, loggedInProfile);
      setIsLoggedIn(true);
      await syncUserDataFromCloud(phone, loggedInProfile);
      await writeAuditLog(db, ensureCloudAnonymousAuth, {
        action: AUDIT_ACTIONS.LOGIN,
        actorId: loggedInProfile.id || loggedInProfile.uid,
        actorPhone: phone,
        actorName: loggedInProfile.name,
        summary: '密码登录'
      });
      await syncProfileToCloudDb(loggedInProfile);
      setShowAuthModal(false);
      beginAuthenticatedSession(loggedInProfile.phone);
      showToast(isAdmin ? '欢迎回来，超级管理员！' : '登录成功！');

    } else if (mode === 'forgot') {
      if (isMaintenanceActive(activeMaintenanceCfg)) {
        return showToast('网站维护中，暂不支持重置密码');
      }
      if (!isValidPhone(phone)) return showToast('请输入真实有效的11位手机号');
      if (!code || String(code).trim() !== DEV_SMS_CODE) return showToast('验证码错误');
      if (!password || password.length < 6) return showToast('新密码至少6位');

      const result = await apiRequest('/reset-password', 'POST', { phone, code, password });
      if (!result?.success) {
        return showToast(result?.message || '密码重置失败');
      }
      showToast(result.message || '密码重置成功！请使用新密码登录');
      if (setModeFunc) setModeFunc('login');
    }
  };

  const markReportsHandled = async (matchFn, handledAction) => {
    const patch = {
      status: 'handled',
      handledAction,
      handledAt: Date.now(),
      handledBy: String(userProfile?.id || userProfile?.uid || userProfile?.phone || 'admin')
    };
    const pending = reports.filter(
      (r) => (!r.status || r.status === 'pending') && matchFn(r)
    );
    if (!pending.length) return 0;

    if (db) {
      try {
        await ensureCloudAnonymousAuth();
        await Promise.all(
          pending.map(async (report) => {
            const docId = report.id || report._id;
            if (!docId) return;
            try {
              await db.collection('reports').doc(String(docId)).update(patch);
            } catch (error) {
              console.warn('举报状态更新失败:', docId, error);
            }
          })
        );
      } catch (error) {
        console.warn('批量更新举报状态失败:', error);
      }
    }

    const handledIdSet = new Set(
      pending.map((r) => String(r.id || r._id)).filter(Boolean)
    );
    setReports((prev) =>
      prev.map((r) => (handledIdSet.has(String(r.id)) ? { ...r, ...patch } : r))
    );
    return pending.length;
  };

  const isProtectedAdminUser = (row) =>
    row?.role === 'super_admin' || String(row?.phone || '') === OFFICIAL_ADMIN_PHONE;

  const executeUserBan = async (data, auditExtra = {}) => {
    const days = data.days ?? 7;
    const isPermanent = days === -1;
    const userRow = matchUserRow(data) || { id: data.userId, userId: data.userId };
    if (isProtectedAdminUser(userRow)) {
      showToast('无法封禁超级管理员');
      return false;
    }
    const actor = {
      phone: userProfile?.phone || '',
      name: userProfile?.name || '',
      role: isSuperAdminProfile(userProfile) ? 'super_admin' : 'admin'
    };
    const result = await apiRequest('/admin/ban', 'POST', {
      userId: data.userId,
      days: isPermanent ? -1 : days,
      permanent: isPermanent
    });
    let cloudOk = false;
    if (userRow) {
      cloudOk = await updateUserBanInCloud(userRow, { banned: true, days }, actor);
    }
    const banPatch = {
      isBanned: true,
      banDays: days,
      banUntil: isPermanent ? null : Date.now() + days * 86400000,
      bannedAt: Date.now(),
      bannedBy: actor.phone,
      bannedByName: actor.name,
      bannedByRole: actor.role
    };
    if (result?.success || cloudOk) {
      applyBanPatchToUsersList(data.userId, {
        ...banPatch,
        phone: userRow?.phone
      });
    }
    let handledCount = 0;
    if (data.targetId) {
      handledCount = await markReportsHandled(
        (r) => String(r.targetId) === String(data.targetId),
        'ban_user'
      );
    } else if (data.id && (data.reason || data.targetType)) {
      handledCount = await markReportsHandled(
        (r) => String(r.id) === String(data.id),
        'ban_user'
      );
    }
    const durationLabel = getBanDurationLabel(days);
    const { skipAudit, ...auditMeta } = auditExtra;
    if (!skipAudit) {
      await recordAdminAudit({
        action: AUDIT_ACTIONS.ADMIN_BAN,
        targetType: 'user',
        targetId: String(data.userId || userRow?.id || ''),
        summary: `封禁用户 ${userRow?.name || userRow?.phone || data.userId}（${durationLabel}）`,
        meta: {
          banDays: days,
          banUntil: banPatch.banUntil,
          targetName: userRow?.name || data.label || '',
          targetPhone: userRow?.phone || '',
          ...auditMeta
        }
      });
    }
    const baseMsg =
      result?.success || cloudOk
        ? skipAudit
          ? `封禁时长已调整为 ${durationLabel}`
          : `用户已被封禁（${durationLabel}）`
        : result?.message || '封禁操作失败';
    if (!skipAudit) {
      showToast(handledCount > 0 ? `${baseMsg}，${handledCount} 条举报已办结` : baseMsg);
    }
    return result?.success || cloudOk;
  };

  const executeContentTakedown = async (contentId, contentType, auditExtra = {}) => {
    if (!contentId) return 0;
    const collection = contentType === 'adopt' ? 'pets' : 'posts';
    const list = contentType === 'adopt' ? adoptList : posts;
    const target =
      list.find((p) => String(p.id) === String(contentId)) ||
      adminAllContent.find((p) => String(p.id) === String(contentId));
    const snapshot = buildContentSnapshot(target || { id: contentId, type: contentType });

    await recordAdminAudit({
      action: AUDIT_ACTIONS.ADMIN_TAKEDOWN,
      targetType: contentType,
      targetId: contentId,
      summary: `下架${contentType === 'adopt' ? '领养' : '作品'}：${target?.title || target?.name || contentId}`,
      snapshot,
      meta: {
        contentTitle: target?.title || target?.name || '',
        contentOwner: target?.user || '',
        ...auditExtra
      }
    });

    if (db) {
      await softDeleteContentInCloud(
        db,
        ensureCloudAnonymousAuth,
        collection,
        contentId,
        userProfile?.phone || userProfile?.id || userProfile?.uid || 'admin'
      );
    }

    setPosts((prev) => prev.filter((p) => String(p.id) !== String(contentId)));
    setAdoptList((prev) => prev.filter((p) => String(p.id) !== String(contentId)));
    setAdminAllContent((prev) =>
      prev.map((item) =>
        String(item.id) === String(contentId)
          ? {
              ...item,
              deletedAt: Date.now(),
              status: 'deleted',
              archived: true,
              deletedBy: userProfile?.phone || userProfile?.name || 'admin'
            }
          : item
      )
    );

    return markReportsHandled((r) => String(r.targetId) === String(contentId), 'takedown');
  };

  const handleAdminAction = async (action, data) => {
    const superAdmin = isSuperAdminProfile(userProfile);
    const admin = isAdminProfile(userProfile);
    if (!admin) {
      showToast('无管理权限');
      return false;
    }

    const superOnlyActions = new Set([
      'publishAnn',
      'deleteAnn',
      'withdrawAnn',
      'pinAnn',
      'setAdmin',
      'removeAdmin',
      'banUser',
      'unbanUser',
      'approveBanRequest',
      'rejectBanRequest',
      'overrideRevokeBan',
      'overrideRestoreContent',
      'modifyBanDuration',
      'updateMaintenance'
    ]);
    if (superOnlyActions.has(action) && !superAdmin) {
      showToast('该操作需要超级管理员权限');
      return false;
    }

    const adminOnlyActions = new Set(['requestBanUser']);
    if (adminOnlyActions.has(action) && superAdmin) {
      // super admin uses banUser directly
    } else if (adminOnlyActions.has(action) && !admin) {
      showToast('无管理权限');
      return false;
    }
    if (action === 'publishAnn') {
      const title = (data.title || '').trim();
      const content = (data.content || '').trim();
      if (!title || !content) {
        showToast('请填写公告标题和正文');
        return false;
      }

      const annDoc = {
        title,
        content,
        createdAt: Date.now(),
        pinned: false
      };

      const result = await apiRequest('/announcements', 'POST', { title, content });
      let cloudOk = false;
      let newId = `local_${Date.now()}`;

      if (db) {
        try {
          await ensureCloudAnonymousAuth();
          const addRes = await db.collection('announcements').add(annDoc);
          newId = addRes.id || addRes._id || newId;
          cloudOk = true;
        } catch (error) {
          console.warn('公告写入云库失败:', error);
        }
      }

      if (result?.success || cloudOk) {
        setAnnouncements((prev) =>
          sortAnnouncements([{ ...annDoc, id: newId }, ...prev.filter((a) => String(a.id) !== String(newId))])
        );
        showToast('全站公告发布成功！');
        return true;
      }
      showToast(result?.message || '发布失败');
      return false;
    } else if (action === 'deleteAnn' || action === 'withdrawAnn') {
      const annId = data.annId || data.id;
      if (!annId) {
        showToast('公告 ID 无效');
        return false;
      }

      const patch = {
        withdrawn: true,
        withdrawnAt: Date.now(),
        withdrawnByPhone: userProfile?.phone || '',
        withdrawnByName: userProfile?.name || '超级管理员',
        pinned: false,
        pinnedAt: null,
        updatedAt: Date.now()
      };

      setAnnouncements((prev) =>
        sortAnnouncements(
          prev.map((a) => (String(a.id) === String(annId) ? { ...a, ...patch } : a))
        )
      );

      let cloudOk = false;
      if (db) {
        try {
          await ensureCloudAnonymousAuth();
          await db.collection('announcements').doc(String(annId)).update(patch);
          cloudOk = true;
        } catch (error) {
          console.warn('公告撤回失败:', error);
        }
      }
      await apiRequest(`/announcements/${annId}`, 'DELETE').catch(() => null);

      if (cloudOk) {
        showToast('公告已撤回，全站用户将不再看到');
        await recordAdminAudit({
          action: AUDIT_ACTIONS.ADMIN_WITHDRAW_ANN,
          actorId: userProfile?.id || userProfile?.uid,
          actorPhone: userProfile?.phone,
          actorName: userProfile?.name,
          targetType: 'announcement',
          targetId: String(annId),
          summary: `撤回全站公告：${data.title || annId}`,
          meta: { annId, title: data.title || '' }
        }).catch(() => null);
        return true;
      }
      setAnnouncements((prev) =>
        sortAnnouncements(
          prev.map((a) =>
            String(a.id) === String(annId)
              ? { ...a, withdrawn: false, withdrawnAt: null, withdrawnByPhone: '', withdrawnByName: '' }
              : a
          )
        )
      );
      showToast('公告撤回失败，请检查云库写权限');
      return false;
    } else if (action === 'pinAnn') {
      const annId = data.annId || data.id;
      const pin = data.pin !== false;
      if (!annId) {
        showToast('公告 ID 无效');
        return false;
      }
      const targetAnn = announcements.find((a) => String(a.id) === String(annId));
      if (targetAnn?.withdrawn) {
        showToast('已撤回的公告无法置顶');
        return false;
      }

      const patch = {
        pinned: pin,
        pinnedAt: pin ? Date.now() : null,
        updatedAt: Date.now()
      };

      setAnnouncements((prev) =>
        sortAnnouncements(
          prev.map((a) => (String(a.id) === String(annId) ? { ...a, ...patch } : a))
        )
      );

      if (db) {
        try {
          await ensureCloudAnonymousAuth();
          await db.collection('announcements').doc(String(annId)).update(patch);
        } catch (error) {
          console.warn('公告置顶更新失败:', error);
        }
      }

      setAnnouncements((prev) =>
        sortAnnouncements(
          prev.map((a) => (String(a.id) === String(annId) ? { ...a, ...patch } : a))
        )
      );
      showToast(pin ? '公告已置顶' : '已取消置顶');
      return true;
    } else if (action === 'updateMaintenance') {
      const prevEnabled = !!siteMaintenance?.enabled;
      const immediate = !!data.immediate;
      const payload = {
        enabled: !!data.enabled,
        title: (data.title || '网站维护中').trim(),
        message: (data.message || '').trim(),
        startAt: data.enabled && immediate ? null : (data.startAt ?? null),
        endAt: data.endAt ?? null,
        updatedByPhone: userProfile?.phone || '',
        updatedByName: userProfile?.name || '超级管理员'
      };

      let cloudOk = false;
      if (db) {
        cloudOk = await saveCloudMaintenanceConfig(db, ensureCloudAnonymousAuth, payload);
      }
      const serverRes = await saveServerMaintenanceConfig(apiRequest, {
        ...payload,
        adminPhone: userProfile?.phone,
        immediate
      });
      const serverOk = !(serverRes && serverRes.success === false);

      if (!cloudOk && db) {
        showToast('云端维护设置保存失败，请检查 site_settings 写权限');
        return false;
      }
      if (!serverOk) {
        showToast('服务端维护设置保存失败，请稍后重试');
        return false;
      }

      const merged = pickMaintenanceConfig(
        normalizeMaintenanceConfig({ ...payload, updatedAt: Date.now() }),
        serverRes?.enabled !== undefined ? serverRes : null
      );
      setSiteMaintenance(merged);

      if (merged.enabled && isMaintenanceBypassProfile(userProfile)) {
        markMaintenanceAdminSession(userProfile.phone);
      }
      if (merged.enabled) {
        enforceMaintenanceLockout(merged, userProfile, { toast: false });
      } else {
        clearMaintenanceAdminSession();
      }

      await writeAuditLog(db, ensureCloudAnonymousAuth, {
        action: payload.enabled
          ? (prevEnabled ? AUDIT_ACTIONS.SITE_MAINTENANCE_UPDATE : AUDIT_ACTIONS.SITE_MAINTENANCE_ON)
          : AUDIT_ACTIONS.SITE_MAINTENANCE_OFF,
        actorId: userProfile?.id || userProfile?.uid,
        actorPhone: userProfile?.phone,
        actorName: userProfile?.name,
        summary: payload.enabled ? '开启/更新网站维护模式' : '关闭网站维护模式',
        snapshot: {
          startAt: payload.startAt,
          endAt: payload.endAt,
          title: payload.title
        }
      });
      return true;
    } else if (action === 'deleteContent') {
      const contentId = data.contentId || data.targetId || data.id;
      const contentType = data.contentType || data.targetType || data.type || 'post';
      const handledCount = await executeContentTakedown(contentId, contentType);
      showToast(
        handledCount > 0
          ? `内容已下架，${handledCount} 条相关举报已办结`
          : '内容已下架（已停止公开展示）'
      );
    } else if (action === 'batchIgnoreReports') {
      const reportIds = (data.reportIds || []).map(String).filter(Boolean);
      if (!reportIds.length) {
        showToast('请先选择举报');
        return false;
      }
      const idSet = new Set(reportIds);
      const handledCount = await markReportsHandled((r) => idSet.has(String(r.id)), 'ignored');
      await Promise.all(
        reportIds.map((reportId) =>
          apiRequest('/admin/handle-report', 'POST', {
            reportId,
            action: 'ignore'
          }).catch(() => null)
        )
      );
      showToast(handledCount > 0 ? `已忽略 ${handledCount} 条举报` : '所选举报已处理');
      return true;
    } else if (action === 'batchTakedownReports') {
      const items = data.items || [];
      const reportIds = (data.reportIds || []).map(String).filter(Boolean);
      if (!items.length) {
        showToast('没有可下架的内容');
        return false;
      }
      for (const item of items) {
        await executeContentTakedown(item.contentId, item.contentType);
      }
      if (reportIds.length) {
        const idSet = new Set(reportIds);
        await markReportsHandled((r) => idSet.has(String(r.id)), 'takedown');
      }
      showToast(
        reportIds.length
          ? `已下架 ${items.length} 条内容，办结 ${reportIds.length} 条举报`
          : `已下架 ${items.length} 条内容`
      );
      return true;
    } else if (action === 'restoreContent') {
      const contentId = data.contentId || data.id;
      const contentType = data.contentType || data.type || 'post';
      const collection = contentType === 'adopt' ? 'pets' : 'posts';
      const sourceItem =
        adminAllContent.find((p) => String(p.id) === String(contentId)) ||
        [...posts, ...adoptList].find((p) => String(p.id) === String(contentId)) ||
        { id: contentId, type: contentType };

      const restorePatch = {
        deletedAt: null,
        deletedBy: '',
        status: 'active',
        archived: false,
        restoredAt: Date.now()
      };
      const restoredItem = {
        ...sourceItem,
        ...restorePatch,
        type: contentType === 'adopt' ? 'adopt' : 'post'
      };

      let cloudOk = false;
      if (contentId && db) {
        const result = await restoreContentInCloud(
          db,
          ensureCloudAnonymousAuth,
          collection,
          contentId,
          userProfile?.id || userProfile?.uid || 'admin'
        );
        cloudOk = result.success;
      }

      await recordAdminAudit({
        action: AUDIT_ACTIONS.ADMIN_RESTORE,
        targetType: contentType,
        targetId: contentId,
        summary: `恢复${contentType === 'adopt' ? '领养' : '作品'}：${restoredItem.title || restoredItem.name || contentId}`,
        snapshot: buildContentSnapshot(restoredItem),
        meta: data.overrideOf ? { overrideOf: data.overrideOf } : null
      });

      const upsertContentList = (prev) => {
        const id = String(contentId);
        if (prev.some((p) => String(p.id) === id)) {
          return prev.map((p) => (String(p.id) === id ? { ...p, ...restorePatch } : p));
        }
        return [restoredItem, ...prev].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      };

      if (contentType === 'adopt') {
        setAdoptList(upsertContentList);
      } else {
        setPosts(upsertContentList);
      }
      setAdminAllContent((prev) =>
        prev.map((item) =>
          String(item.id) === String(contentId) ? { ...item, ...restorePatch } : item
        )
      );

      showToast(cloudOk || !db ? '内容已恢复，重新在前台展示' : '已恢复；云同步失败请稍后刷新');
    } else if (action === 'banUser') {
      await executeUserBan(data);
    } else if (action === 'requestBanUser') {
      const days = data.days ?? 7;
      const userRow = matchUserRow(data) || { id: data.userId, userId: data.userId };
      if (isProtectedAdminUser(userRow)) {
        showToast('无法封禁超级管理员');
        return false;
      }
      const targetUserId = data.userId || userRow?.id || userRow?.userId;
      if (!targetUserId) {
        showToast('用户 ID 无效');
        return false;
      }

      const requestDoc = {
        targetUserId: String(targetUserId),
        targetPhone: userRow?.phone || data.phone || '',
        targetName: data.label || userRow?.name || '',
        days,
        reason: data.reason || data.details || '',
        reportId: data.id || data.targetId || '',
        status: 'pending',
        requestedBy: userProfile?.id || userProfile?.uid || '',
        requestedByName: userProfile?.name || '',
        requestedByPhone: userProfile?.phone || '',
        createdAt: Date.now()
      };

      let cloudOk = false;
      let requestId = `local_${Date.now()}`;
      if (db) {
        try {
          await ensureCloudAnonymousAuth();
          const addRes = await db.collection('ban_requests').add(requestDoc);
          requestId = addRes.id || addRes._id || requestId;
          cloudOk = true;
        } catch (error) {
          console.warn('封禁申请写入失败:', error);
        }
      }

      if (cloudOk) {
        setBanRequests((prev) => [{ ...requestDoc, id: requestId }, ...prev]);
        await recordAdminAudit({
          action: AUDIT_ACTIONS.ADMIN_BAN_REQUEST,
          targetType: 'user',
          targetId: String(targetUserId),
          summary: `申请封禁 ${requestDoc.targetName || requestDoc.targetPhone || targetUserId}（${getBanDurationLabel(days)}）`,
          meta: {
            banDays: days,
            targetName: requestDoc.targetName,
            targetPhone: requestDoc.targetPhone,
            requestId
          }
        });
        await syncNotificationToCloud({
          type: 'ban_request',
          targetPhone: OFFICIAL_ADMIN_PHONE,
          targetUserId: OFFICIAL_ADMIN_PHONE,
          title: '新的封禁申请',
          content: `${userProfile?.name || '管理员'} 申请封禁 ${requestDoc.targetName || requestDoc.targetPhone || targetUserId}（${getBanDurationLabel(days)}）`,
          read: false,
          createdAt: Date.now(),
          meta: { requestId }
        });
        showToast('封禁申请已提交，等待超级管理员审批');
        return true;
      }
      showToast('提交失败，请稍后重试');
      return false;
    } else if (action === 'approveBanRequest') {
      const req = data;
      const ok = await executeUserBan(
        {
          userId: req.targetUserId,
          days: req.days,
          targetId: req.reportId
        },
        {
          approvedFromRequest: true,
          requestedByName: req.requestedByName,
          requestedByPhone: req.requestedByPhone,
          requestId: req.id
        }
      );
      if (!ok) return false;

      await recordAdminAudit({
        action: AUDIT_ACTIONS.ADMIN_BAN_APPROVE,
        targetType: 'user',
        targetId: String(req.targetUserId),
        summary: `批准封禁 ${req.targetName || req.targetPhone || req.targetUserId}（${getBanDurationLabel(req.days)}），申请人：${req.requestedByName || req.requestedByPhone || '—'}`,
        meta: {
          banDays: req.days,
          targetName: req.targetName,
          requestedByName: req.requestedByName,
          requestId: req.id
        }
      });

      const patch = { status: 'approved', handledAt: Date.now(), handledBy: userProfile?.phone || '' };
      if (db && req.id) {
        try {
          await ensureCloudAnonymousAuth();
          await db.collection('ban_requests').doc(String(req.id)).update(patch);
        } catch (error) {
          console.warn('封禁申请状态更新失败:', error);
        }
      }
      setBanRequests((prev) => prev.filter((r) => String(r.id) !== String(req.id)));
      if (req.requestedByPhone) {
        await syncNotificationToCloud({
          type: 'ban_request_result',
          targetPhone: req.requestedByPhone,
          targetUserId: req.requestedBy,
          title: '封禁申请已通过',
          content: `您申请封禁的 ${req.targetName || req.targetPhone || req.targetUserId} 已被超级管理员批准（${getBanDurationLabel(req.days)}）`,
          read: false,
          createdAt: Date.now()
        });
      }
      return true;
    } else if (action === 'rejectBanRequest') {
      const req = data;
      await recordAdminAudit({
        action: AUDIT_ACTIONS.ADMIN_BAN_REJECT,
        targetType: 'user',
        targetId: String(req.targetUserId),
        summary: `驳回封禁申请：${req.targetName || req.targetPhone || req.targetUserId}，申请人：${req.requestedByName || '—'}`,
        meta: { requestId: req.id, requestedByName: req.requestedByName }
      });
      const patch = { status: 'rejected', handledAt: Date.now(), handledBy: userProfile?.phone || '' };
      if (db && req.id) {
        try {
          await ensureCloudAnonymousAuth();
          await db.collection('ban_requests').doc(String(req.id)).update(patch);
        } catch (error) {
          console.warn('封禁申请驳回失败:', error);
        }
      }
      setBanRequests((prev) => prev.filter((r) => String(r.id) !== String(req.id)));
      if (req.requestedByPhone) {
        await syncNotificationToCloud({
          type: 'ban_request_result',
          targetPhone: req.requestedByPhone,
          targetUserId: req.requestedBy,
          title: '封禁申请已驳回',
          content: `您申请封禁的 ${req.targetName || req.targetPhone || req.targetUserId} 未被批准`,
          read: false,
          createdAt: Date.now()
        });
      }
      showToast('已驳回封禁申请');
      return true;
    } else if (action === 'unbanUser') {
      const userRow = matchUserRow(data) || { id: data.userId, userId: data.userId };
      const actor = {
        phone: userProfile?.phone || '',
        name: userProfile?.name || '',
        role: isSuperAdminProfile(userProfile) ? 'super_admin' : 'admin'
      };
      const result = await apiRequest('/admin/ban', 'POST', {
        userId: data.userId,
        days: 0
      });
      let cloudOk = false;
      if (userRow) {
        cloudOk = await updateUserBanInCloud(userRow, { banned: false, days: 0 }, actor);
      }
      if (result?.success || cloudOk) {
        applyBanPatchToUsersList(data.userId, {
          isBanned: false,
          banDays: 0,
          banUntil: null,
          bannedAt: null,
          bannedBy: '',
          bannedByName: '',
          bannedByRole: '',
          phone: userRow?.phone || data.phone
        });
        if (userRow?.phone) {
          await syncNotificationToCloud({
            type: 'account_unban',
            targetPhone: userRow.phone,
            targetUserId: userRow.uid || userRow.id || data.userId,
            title: '账号已解封',
            content: '您的账号限制已解除，可以正常使用予归平台。',
            read: false,
            createdAt: Date.now()
          }).catch(() => null);
        }
        await recordAdminAudit({
          action: AUDIT_ACTIONS.ADMIN_UNBAN,
          targetType: 'user',
          targetId: String(data.userId),
          summary: `解封用户 ${userRow?.name || userRow?.phone || data.userId}`,
          meta: {
            targetName: userRow?.name,
            targetPhone: userRow?.phone,
            overrideOf: data.overrideOf || null
          }
        });
      }
      showToast(result?.message || (cloudOk ? '用户已解封' : '解封操作失败'));
    } else if (action === 'overrideRevokeBan') {
      const log = data.log || data;
      const userId = log.targetId || log.meta?.targetUserId;
      if (!userId) {
        showToast('无法识别被封禁用户');
        return false;
      }
      const userRow = matchUserRow({ userId }) || { userId };
      const actor = {
        phone: userProfile?.phone || '',
        name: userProfile?.name || '',
        role: 'super_admin'
      };
      const result = await apiRequest('/admin/ban', 'POST', { userId, days: 0 });
      let cloudOk = false;
      if (userRow) {
        cloudOk = await updateUserBanInCloud(userRow, { banned: false, days: 0 }, actor);
      }
      if (result?.success || cloudOk) {
        applyBanPatchToUsersList(userId, {
          isBanned: false,
          banDays: 0,
          banUntil: null,
          bannedAt: null,
          phone: userRow?.phone
        });
        if (userRow?.phone) {
          await syncNotificationToCloud({
            type: 'account_unban',
            targetPhone: userRow.phone,
            targetUserId: userRow.uid || userRow.id || userId,
            title: '账号已解封',
            content: '您的账号限制已解除，可以正常使用予归平台。',
            read: false,
            createdAt: Date.now()
          }).catch(() => null);
        }
        await recordAdminAudit({
          action: AUDIT_ACTIONS.ADMIN_REVOKE_BAN,
          targetType: 'user',
          targetId: String(userId),
          summary: `超级管理员撤销封禁（原操作者：${log.actorName || log.actorPhone || '—'}）`,
          meta: {
            overridesLogId: log.id,
            originalActor: log.actorName || log.actorPhone,
            targetName: log.meta?.targetName || userRow?.name
          }
        });
        showToast('已撤销该封禁，用户已解封');
        return true;
      }
      showToast('撤销封禁失败');
      return false;
    } else if (action === 'overrideRestoreContent') {
      const log = data.log || data;
      const contentId = log.targetId;
      const contentType = log.targetType || log.snapshot?.type || 'post';
      if (!contentId) {
        showToast('无法识别被下架内容');
        return false;
      }
      const collection = contentType === 'adopt' ? 'pets' : 'posts';
      const sourceItem =
        adminAllContent.find((p) => String(p.id) === String(contentId)) ||
        [...posts, ...adoptList].find((p) => String(p.id) === String(contentId)) ||
        { id: contentId, type: contentType, ...(log.snapshot || {}) };
      const restorePatch = {
        deletedAt: null,
        deletedBy: '',
        status: 'active',
        archived: false,
        restoredAt: Date.now()
      };
      if (db) {
        await restoreContentInCloud(
          db,
          ensureCloudAnonymousAuth,
          collection,
          contentId,
          userProfile?.phone || 'super_admin'
        );
      }
      const upsert = (prev) => {
        const id = String(contentId);
        if (prev.some((p) => String(p.id) === id)) {
          return prev.map((p) => (String(p.id) === id ? { ...p, ...restorePatch } : p));
        }
        return [{ ...sourceItem, ...restorePatch, type: contentType === 'adopt' ? 'adopt' : 'post' }, ...prev];
      };
      if (contentType === 'adopt') setAdoptList(upsert);
      else setPosts(upsert);
      setAdminAllContent((prev) =>
        prev.map((item) => (String(item.id) === String(contentId) ? { ...item, ...restorePatch } : item))
      );
      await recordAdminAudit({
        action: AUDIT_ACTIONS.ADMIN_REVOKE_TAKEDOWN,
        targetType: contentType,
        targetId: String(contentId),
        summary: `超级管理员恢复作品（原操作者：${log.actorName || log.actorPhone || '—'}）`,
        meta: {
          overridesLogId: log.id,
          originalActor: log.actorName || log.actorPhone,
          contentTitle: log.meta?.contentTitle || sourceItem.title || sourceItem.name
        }
      });
      showToast('已恢复该作品展示');
      return true;
    } else if (action === 'modifyBanDuration') {
      const userId = data.userId;
      const days = data.days ?? 7;
      const userRow = matchUserRow({ userId }) || { userId };
      const ok = await executeUserBan({ userId, days }, { skipAudit: true });
      if (ok) {
        await recordAdminAudit({
          action: AUDIT_ACTIONS.ADMIN_MODIFY_BAN,
          targetType: 'user',
          targetId: String(userId),
          summary: `超级管理员调整封禁为 ${getBanDurationLabel(days)}：${userRow?.name || userId}`,
          meta: { banDays: days, overridesLogId: data.overrideOf || null, targetName: userRow?.name }
        });
        showToast(`封禁时长已调整为 ${getBanDurationLabel(days)}`);
      }
      return ok;
    } else if (action === 'setAdmin') {
      const row = data;
      if (isProtectedAdminUser(row)) {
        showToast('无法修改超级管理员权限');
        return false;
      }
      const adminDays = data.adminDays ?? 30;
      const isPermanent = adminDays === -1;
      const adminUntil = isPermanent ? null : Date.now() + adminDays * 86400000;
      const targetApiId = row.userId || row.id || row._id;
      const result = await apiRequest('/admin/setadmin', 'POST', {
        userId: targetApiId,
        phone: row.phone,
        isAdmin: true
      });
      let cloudOk = false;
      if (!result?.success && row) {
        cloudOk = await updateUserRoleInCloud(row, 'admin', {
          adminDays,
          adminUntil,
          adminGrantedBy: userProfile?.phone || '',
          adminGrantedByName: userProfile?.name || ''
        });
      }
      const ok = result?.success || cloudOk;
      if (ok) {
        setUsersList((prev) =>
          prev.map((u) => {
            const match =
              String(u.id) === String(row.id) ||
              String(u.uid) === String(row.uid) ||
              (row.phone && String(u.phone) === String(row.phone)) ||
              (targetApiId &&
                (String(u.id) === String(targetApiId) || String(u.userId) === String(targetApiId)));
            return match
              ? { ...u, role: 'admin', adminDays, adminUntil, adminGrantedBy: userProfile?.phone, adminGrantedByName: userProfile?.name }
              : u;
          })
        );
        await recordAdminAudit({
          action: AUDIT_ACTIONS.ADMIN_GRANT_ROLE,
          targetType: 'user',
          targetId: String(targetApiId || row.id),
          summary: `授予 ${row.name || row.phone} 管理员权限（${getBanDurationLabel(adminDays === -1 ? -1 : adminDays).replace('永久', '永久有效')}）`,
          meta: {
            adminDays,
            adminUntil,
            targetName: row.name,
            targetPhone: row.phone
          }
        });
      }
      showToast(
        ok
          ? `已设为管理员（${isPermanent ? '永久直至收回' : `${adminDays} 天`}）`
          : result?.message || '操作失败'
      );
    } else if (action === 'removeAdmin') {
      const row = data;
      if (isProtectedAdminUser(row)) {
        showToast('无法修改超级管理员权限');
        return false;
      }
      const targetApiId = row.userId || row.id || row._id;
      const result = await apiRequest('/admin/setadmin', 'POST', {
        userId: targetApiId,
        phone: row.phone,
        isAdmin: false
      });
      let cloudOk = false;
      if (!result?.success && row) {
        cloudOk = await updateUserRoleInCloud(row, 'user');
      }
      const ok = result?.success || cloudOk;
      if (ok) {
        setUsersList((prev) =>
          prev.map((u) => {
            const match =
              String(u.id) === String(row.id) ||
              String(u.uid) === String(row.uid) ||
              (row.phone && String(u.phone) === String(row.phone)) ||
              (targetApiId &&
                (String(u.id) === String(targetApiId) || String(u.userId) === String(targetApiId)));
            return match ? { ...u, role: 'user', adminUntil: null, adminDays: null } : u;
          })
        );
        await recordAdminAudit({
          action: AUDIT_ACTIONS.ADMIN_REVOKE_ROLE,
          targetType: 'user',
          targetId: String(targetApiId || row.id),
          summary: `取消 ${row.name || row.phone} 的管理员权限`,
          meta: { targetName: row.name, targetPhone: row.phone }
        });
      }
      showToast(
        ok
          ? result?.message || (cloudOk && !result?.success ? '已通过云库取消管理员' : '已取消管理员权限')
          : result?.message || '操作失败'
      );
    } else if (action === 'ignoreReport') {
      const reportId = data.id || data.reportId;
      const report = reports.find((r) => String(r.id) === String(reportId));
      const handledCount = reportId
        ? await markReportsHandled((r) => String(r.id) === String(reportId), 'ignored')
        : 0;
      if (reportId) {
        await apiRequest('/admin/handle-report', 'POST', {
          reportId,
          action: 'ignore'
        });
        await recordAdminAudit({
          action: AUDIT_ACTIONS.ADMIN_IGNORE_REPORT,
          targetType: report?.targetType || 'report',
          targetId: String(report?.targetId || reportId),
          summary: `忽略举报：${report?.reason || reportId}`,
          meta: { reportId, reportReason: report?.reason }
        });
      }
      showToast(handledCount > 0 ? '举报已忽略，已从待处理列表移除' : '举报已忽略');
    } else if (action === 'handleFeedback') {
      const feedbackId = data.feedbackId;
      if (!feedbackId) {
        showToast('反馈 ID 无效');
        return;
      }

      const reply = (data.reply || '').trim();
      const patch = {
        status: 'handled',
        handledAt: Date.now(),
        handledBy: userProfile?.name || userProfile?.phone || 'admin',
        adminReply: reply
      };

      const targetFeedback = feedbackList.find((f) => String(f.id) === String(feedbackId));

      if (db) {
        try {
          await ensureCloudAnonymousAuth();
          await db.collection('feedbacks').doc(String(feedbackId)).update(patch);
        } catch (error) {
          console.warn('反馈状态更新失败:', error);
        }
      }

      setFeedbackList((prev) =>
        prev.map((f) => (String(f.id) === String(feedbackId) ? { ...f, ...patch } : f))
      );

      if (reply && targetFeedback) {
        const notification = {
          type: 'feedback_reply',
          title: '官方已回复您的反馈',
          content: reply,
          targetUserId: targetFeedback.userId || targetFeedback.uid || targetFeedback.phone,
          targetPhone: targetFeedback.phone,
          feedbackId: String(feedbackId),
          createdAt: Date.now(),
          read: false
        };
        const notifySynced = await syncNotificationToCloud(notification);
        const recipientIds = [
          targetFeedback.userId,
          targetFeedback.uid,
          targetFeedback.phone
        ]
          .filter(Boolean)
          .map(String);
        const isSelf =
          recipientIds.length > 0 &&
          recipientIds.some((id) =>
            [userProfile?.id, userProfile?.uid, userProfile?.phone]
              .filter(Boolean)
              .map(String)
              .includes(id)
          );
        if (isSelf) {
          setNotifications((prev) => [notification, ...prev]);
        }
        if (!notifySynced) {
          pushOfflineTask({ type: 'notification', notification });
        }
      }

      showToast(reply ? '已回复用户并办结' : '已标记为已处理');
    }
  };

  const handleReportSubmit = async (reason, details) => {
    if (!reportTarget || !userProfile) {
      showToast('请先登录后再举报');
      return;
    }

    const detailText = (details || '').trim();
    if (detailText) {
      const compliance = scanReportCompliance(detailText);
      if (!compliance.ok) {
        showToast(complianceBlockMessage(compliance), 'warning');
        await recordContentBlocked(compliance, '拦截举报说明');
        return;
      }
    }

    const matchedContent = [...posts, ...adoptList].find(
      (p) => String(p.id) === String(reportTarget.id)
    );
    const reportData = {
      reason,
      details: details || '',
      targetId: reportTarget.id,
      targetType: reportTarget.type || 'post',
      reporterId: userProfile.id || userProfile.uid,
      reporterPhone: userProfile.phone,
      reporterName: userProfile.name,
      reporterUid: userProfile.uid || userProfile.id,
      status: 'pending',
      createdAt: Date.now(),
      contentPreview: matchedContent
        ? {
            type: matchedContent.type || reportTarget.type || 'post',
            title: matchedContent.title || matchedContent.name || '',
            text: (matchedContent.content || matchedContent.description || '').slice(0, 500),
            user: matchedContent.user || '',
            userId: matchedContent.userId || '',
            img: matchedContent.img || '',
            isVideo: isPostVideoMedia(matchedContent)
          }
        : null
    };

    if (db) {
      try {
        await ensureCloudAnonymousAuth();
        await db.collection('reports').add(reportData);
      } catch (error) {
        console.warn('举报写入云库失败:', error);
      }
    }

    await recordAudit({
      action: AUDIT_ACTIONS.REPORT,
      targetType: reportTarget.type || 'post',
      targetId: reportTarget.id,
      summary: `举报：${reason}`,
      snapshot: { details: (details || '').slice(0, 1000), reason }
    });

    showToast("举报已提交至官方云端审核");
    setShowReportModal(false);
    setReportTarget(null);
  };

  const handleFeedbackSubmit = async (text, type) => {
    if (!userProfile) {
      showToast('请先登录后再提交反馈');
      return;
    }

    const compliance = scanFeedbackCompliance(text);
    if (!compliance.ok) {
      showToast(complianceBlockMessage(compliance), 'warning');
      await recordContentBlocked(compliance, '拦截反馈');
      return;
    }

    const feedbackData = {
      type: type || '建议',
      content: (text || '').trim(),
      uid: userProfile.uid || userProfile.id,
      userId: userProfile.id || userProfile.uid,
      userName: userProfile.name,
      phone: userProfile.phone,
      status: 'pending',
      createdAt: Date.now()
    };

    if (db) {
      try {
        await ensureCloudAnonymousAuth();
        await db.collection('feedbacks').add(feedbackData);
      } catch (error) {
        console.warn('反馈写入云库失败:', error);
      }
    }

    setFeedbackList((prev) =>
      [{ ...feedbackData, id: `local_${Date.now()}` }, ...prev].sort(
        (a, b) => (b.createdAt || 0) - (a.createdAt || 0)
      )
    );

    showToast('反馈已成功提交！');
    setShowFeedbackModal(false);
  };

  // 我的作品/收藏/点赞/关注筛选
  const currentUserIds = [
    userProfile?.id,
    userProfile?.uid,
    userProfile?.phone,
    ...(Array.isArray(userProfile?.legacyUids) ? userProfile.legacyUids : [])
  ]
    .filter(Boolean)
    .map((v) => String(v));
  const isMine = (ownerId) => ownerId !== undefined && currentUserIds.includes(String(ownerId));
  const listIncludesMe = (arr) =>
    Array.isArray(arr) && currentUserIds.length > 0 && arr.some((id) => currentUserIds.includes(String(id)));
  const viewReportedContent = (content) => {
    if (!content?.id || content.missing) return;
    if (content.type === 'adopt') {
      navigateTo('adopt');
      setTimeout(() => {
        document.getElementById(`adopt-card-${content.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 250);
    } else {
      openPostInDiscover({ id: content.id, type: 'post' });
    }
  };

  const openPostInDiscover = (post, returnView = null) => {
    const id = post?.id ?? post?._id;
    if (!id) return;
    if (returnView) setFeedReturnView(returnView);
    setDiscoverFocusPostId(String(id));
    setVideoSoundPostId(null);
    navigateTo('home', { preserveViewingUser: returnView === 'user' });
  };

  const openAdoptInHall = (pet, returnView = 'profile') => {
    if (!pet?.id) return;
    setFeedReturnView(returnView);
    navigateTo('adopt', { preserveViewingUser: returnView === 'user' });
    const petId = String(pet.id);
    const scrollToCard = () => {
      document.getElementById(`adopt-card-${petId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    requestAnimationFrame(scrollToCard);
    setTimeout(scrollToCard, 200);
    setTimeout(scrollToCard, 600);
  };

  const returnFromDiscoverFeed = () => {
    const back = feedReturnView || 'profile';
    setFeedReturnView(null);
    setDiscoverFocusPostId(null);
    setVideoSoundPostId(null);
    navigateTo(back, { preserveViewingUser: back === 'user' });
  };

  const commitDeletePost = async (post, keepDraft, opts = {}) => {
    const silent = !!opts.silent;
    if (!post?.id) return;
    if (!isMine(post.userId)) {
      if (!silent) showToast('仅能删除自己发布的作品或领养信息');
      setPendingDeletePost(null);
      return;
    }
    const col = post.type === 'adopt' ? 'pets' : 'posts';
    const id = String(post.id);
    const snapshot = buildContentSnapshot(post);

    let draftMeta = { droppedMedia: false, error: false };
    if (keepDraft) {
      draftMeta = savePublishDraftFromPost(post);
      await refreshPublishDraftsFromCloud(userProfile?.phone);
    }

    await recordAudit({
      action: AUDIT_ACTIONS.DELETE,
      targetType: post.type === 'adopt' ? 'adopt' : 'post',
      targetId: id,
      summary: keepDraft ? '用户删除并保留草稿' : '用户删除内容',
      snapshot
    });

    let softDeleted = false;
    try {
      if (db) {
        const { success } = await softDeleteContentInCloud(
          db,
          ensureCloudAnonymousAuth,
          col,
          id,
          userProfile?.id || userProfile?.uid || userProfile?.phone
        );
        softDeleted = success;
        if (!success) {
          await db.collection(col).doc(id).update({
            deletedAt: Date.now(),
            status: 'deleted',
            archived: true
          });
          softDeleted = true;
        }
      } else {
        const base = col === 'pets' ? '/pets' : '/posts';
        const patchResult = await apiRequest(`${base}/${encodeURIComponent(id)}`, 'PATCH', {
          deletedAt: Date.now(),
          status: 'deleted',
          archived: true
        });
        softDeleted = !!patchResult?.success;
        if (!softDeleted) {
          await apiRequest(`${base}/${encodeURIComponent(id)}`, 'DELETE');
        }
      }
    } catch (e) {
      console.error('删除作品失败:', e);
      if (!silent) showToast('删除失败，请稍后重试');
      setPendingDeletePost(null);
      return;
    }

    setPosts((prev) => prev.filter((p) => String(p.id) !== id));
    setAdoptList((prev) => prev.filter((p) => String(p.id) !== id));
    if (selectedPost && String(selectedPost.id) === id) {
      setShowCommentModal(false);
      setSelectedPost(null);
    }
    if (!silent) {
      if (keepDraft) {
        if (draftMeta?.error) showToast('删除成功，但草稿写入失败');
        else if (draftMeta?.droppedMedia) showToast('已删除。草稿已存入草稿箱；视频/大图请重新选择后再发');
        else showToast('已删除，内容已存入草稿箱；可在「我的 → 草稿箱」查看');
      } else {
        showToast(softDeleted ? '已删除，前台不再展示' : '已删除');
      }
    }
    setPendingDeletePost(null);
  };

  // 发现页：公开作品全员可见；「仅自己可见」只在本人主页出现
  const discoverFeedPosts = posts.filter(
    (p) =>
      (p.type || 'post') !== 'adopt' &&
      ((p.visibility || 'public') === 'public' || isMine(p.userId))
  );

  const discoverFeedPostsForView = (() => {
    if (!discoverFocusPostId) return discoverFeedPosts;
    const focusId = String(discoverFocusPostId);
    const focused =
      discoverFeedPosts.find((p) => String(p.id) === focusId) ||
      posts.find(
        (p) =>
          String(p.id) === focusId &&
          (p.type || 'post') !== 'adopt' &&
          ((p.visibility || 'public') === 'public' || isMine(p.userId))
      );
    if (!focused) return discoverFeedPosts;
    return [focused, ...discoverFeedPosts.filter((p) => String(p.id) !== focusId)];
  })();

  useEffect(() => {
    if (currentView !== 'home' || !discoverFocusPostId || isSearching) return undefined;
    const scrollToFocus = () => {
      const viewport = document.querySelector('.feed-viewport');
      const slide = document.getElementById(`discover-feed-${discoverFocusPostId}`);
      if (viewport && slide) {
        viewport.scrollTo({ top: slide.offsetTop, behavior: 'smooth' });
      } else if (viewport) {
        viewport.scrollTo({ top: 0, behavior: 'auto' });
      }
    };
    const raf = requestAnimationFrame(scrollToFocus);
    const t1 = setTimeout(scrollToFocus, 120);
    const t2 = setTimeout(scrollToFocus, 450);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [currentView, discoverFocusPostId, isSearching, discoverFeedPostsForView.length]);

  const resolveAdoptPublisher = (pet) =>
    usersList.find(
      (u) =>
        String(u.id) === String(pet.userId) ||
        String(u.uid) === String(pet.userId) ||
        String(u.uid) === String(pet.uid) ||
        (pet.phone && String(u.phone) === String(pet.phone))
    ) || null;

  const filteredAdoptList = (() => {
    let list = adoptList.filter((pet) => {
      if (adoptFilterType !== 'all') {
        const pt = (pet.petType || '').toLowerCase();
        if (adoptFilterType === '猫' && !/猫|喵/.test(pt)) return false;
        if (adoptFilterType === '狗' && !/狗|犬/.test(pt)) return false;
        if (adoptFilterType === '其他' && (/猫|喵|狗|犬/.test(pt))) return false;
      }
      if (
        adoptFilterLocation === 'sameCity' &&
        userProfile?.city &&
        userProfile.city !== UNKNOWN_LOCATION
      ) {
        return isNearbyAdoptMatch(userProfile, pet, resolveAdoptPublisher(pet));
      }
      return true;
    });

    if (
      adoptFilterLocation === 'sameCity' &&
      userProfile?.city &&
      userProfile.city !== UNKNOWN_LOCATION
    ) {
      list = sortAdoptsByLocation(list, userProfile, resolveAdoptPublisher);
    }
    return list;
  })();
  const myPosts = posts.filter(p => isMine(p.userId));
  const myAdopts = adoptList.filter(p => isMine(p.userId));
  const myWorks = [...myPosts, ...myAdopts];
  const likedKeySet = new Set(
    [
      ...[...posts, ...adoptList]
        .filter((p) => listIncludesMe(p.likedBy))
        .map((p) => buildContentKey(p)),
      ...(Array.isArray(userProfile?.likedContentKeys) ? userProfile.likedContentKeys : [])
    ].filter(Boolean)
  );
  const myLikes = mergeFeedItemsByContentKeys(
    [...posts, ...adoptList].filter((p) => listIncludesMe(p.likedBy)),
    [...likedKeySet]
  );
  const bookmarkKeySet = new Set(
    [
      ...[...posts, ...adoptList]
        .filter((p) => listIncludesMe(p.bookmarkedBy))
        .map((p) => buildContentKey(p)),
      ...(Array.isArray(userProfile?.bookmarkedContentKeys) ? userProfile.bookmarkedContentKeys : [])
    ].filter(Boolean)
  );
  const myBookmarks = mergeFeedItemsByContentKeys(
    [...posts, ...adoptList].filter((p) => listIncludesMe(p.bookmarkedBy)),
    [...bookmarkKeySet]
  );
  const myFollowing = usersList.filter(u => userProfile?.followingList?.includes(u.id) || userProfile?.followingList?.includes(u.uid));

  const contentBelongsToProfile = (item, profile) => {
    if (!item || !profile) return false;
    if (profileMatchesUserKey(profile, item.userId)) return true;
    if (item.uid && profileMatchesUserKey(profile, item.uid)) return true;
    return false;
  };

  const viewingUserPublicPosts = viewingUser
    ? posts.filter(
        (p) =>
          contentBelongsToProfile(p, viewingUser) &&
          (p.type || 'post') !== 'adopt' &&
          (p.visibility || 'public') === 'public'
      )
    : [];

  const viewingUserPublicAdopts = viewingUser
    ? adoptList.filter((p) => contentBelongsToProfile(p, viewingUser))
    : [];

  const isFollowingViewingUser =
    !!viewingUser &&
    isLoggedIn &&
    (userProfile?.followingList || []).some((id) => profileMatchesUserKey(viewingUser, id));

  const toggleProfileBatchId = (rawId) => {
    const id = String(rawId);
    setProfileBatchIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleProfileBatchSelectAll = () => {
    const list = profileTab === 'works' ? myPosts : myAdopts;
    const ids = list.map((p) => String(p.id));
    const allSelected = ids.length > 0 && ids.every((i) => profileBatchIds.includes(i));
    setProfileBatchIds(allSelected ? [] : ids);
  };

  const openPublishModalFresh = () => {
    closeAllOverlays();
    setPublishEditingDraftId(null);
    setShowPublishModal(true);
  };

  const openPublishForDraft = (draft) => {
    closeAllOverlays();
    setPublishEditingDraftId(draft.id);
    setShowPublishModal(true);
  };

  const toggleDraftBatchId = (id) => {
    const sid = String(id);
    setDraftBatchIds((prev) => (prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]));
  };

  const toggleDraftSelectAll = () => {
    const ids = publishDrafts.map((d) => String(d.id));
    const allSelected = ids.length > 0 && ids.every((id) => draftBatchIds.includes(id));
    setDraftBatchIds(allSelected ? [] : ids);
  };

  const handleRenameDraft = async (draft) => {
    const name = window.prompt('重命名草稿', draft.name || suggestDraftName(draft.formData));
    if (name === null) return;
    if (!name.trim()) {
      showToast('名称不能为空');
      return;
    }
    await renamePublishDraftEntry(draft.id, name.trim());
    showToast('草稿已重命名');
  };

  const handleDeleteDrafts = async (ids) => {
    if (!ids?.length) return;
    const n = await removePublishDrafts(ids);
    setDraftBatchIds((prev) => prev.filter((id) => !ids.includes(String(id))));
    if (n) showToast(`已删除 ${n} 条草稿`);
  };

  const runBatchDraftDelete = () => {
    if (!draftBatchIds.length) {
      showToast('请先选择草稿');
      return;
    }
    if (!window.confirm(`确定删除已选 ${draftBatchIds.length} 条草稿？此操作不可恢复。`)) return;
    handleDeleteDrafts([...draftBatchIds]);
    setDraftBatchMode(false);
  };

  const runBatchDraftPublish = async () => {
    if (!draftBatchIds.length) {
      showToast('请先选择草稿');
      return;
    }
    const selected = publishDrafts.filter((d) => draftBatchIds.includes(String(d.id)));
    let ok = 0;
    let fail = 0;
    const failReasons = [];
    for (const draft of selected) {
      const err = validateDraftForPublish(draft.formData);
      if (err) {
        fail += 1;
        failReasons.push(`「${draft.name}」：${err}`);
        continue;
      }
      const success = await handlePublish(
        draft.formData,
        draft.formData.type === 'adopt' ? 'adopt' : 'post',
        { draftId: draft.id, silent: true }
      );
      if (success) ok += 1;
      else fail += 1;
    }
    refreshPublishDraftsFromCloud(userProfile?.phone);
    setDraftBatchIds([]);
    setDraftBatchMode(false);
    if (failReasons.length) {
      showToast(`批量发布：成功 ${ok} 条，失败 ${fail} 条。${failReasons.slice(0, 2).join('；')}`, 'warning');
    } else {
      showToast(`批量发布完成：成功 ${ok} 条${fail ? `，失败 ${fail} 条` : ''}`);
    }
  };

  const runBatchProfileDelete = () => {
    if (!profileBatchIds.length) {
      showToast('请先选择内容');
      return;
    }
    const list = profileTab === 'works' ? myPosts : myAdopts;
    const toDelete = list.filter((p) => profileBatchIds.includes(String(p.id)));
    if (!toDelete.length) {
      showToast('没有可删除的项');
      return;
    }
    setConfirmAction({
      title: `批量删除（${toDelete.length} 项）`,
      desc: '删除后其他用户将无法看到；平台可在合理范围内保留必要记录。此操作不可在前台恢复，确定继续？',
      isDanger: true,
      confirmText: '删除',
      onConfirm: async () => {
        for (const post of toDelete) {
          await commitDeletePost(post, false, { silent: true });
        }
        setProfileBatchIds([]);
        setProfileBatchMode(false);
        showToast(`已删除 ${toDelete.length} 项`);
      }
    });
  };

  const runBatchProfileVisibility = async (visibility) => {
    if (profileTab !== 'works') {
      showToast('仅「我的作品」支持批量改可见范围');
      return;
    }
    const ids = profileBatchIds.filter((id) => myPosts.some((p) => String(p.id) === String(id)));
    if (!ids.length) {
      showToast('请先选择作品');
      return;
    }
    let ok = 0;
    for (const id of ids) {
      const post = myPosts.find((p) => String(p.id) === id);
      if (!post) continue;
      const synced = await syncPostPatch(post, { visibility }, 'post');
      if (synced) {
        ok += 1;
        setPosts((prev) => prev.map((p) => (String(p.id) === id ? { ...p, visibility } : p)));
      }
    }
    setProfileBatchIds([]);
    setProfileBatchMode(false);
    const label = visibility === 'public' ? '所有人可见' : '仅自己可见';
    showToast(
      ok === ids.length
        ? `已将 ${ok} 条设为${label}`
        : `部分成功：${ok}/${ids.length}，请检查网络后重试未成功的条目`
    );
  };

  const closeAdoptChat = () => {
    setAdoptChatPet(null);
    setAdoptChatThreadId(null);
    setAdoptChatMessages([]);
    setAdoptChatThreadChoices([]);
  };

  const reloadAdoptChat = async (tid) => {
    if (!db || !tid) return;
    try {
      const r = await db.collection('adopt_messages').where({ threadId: tid }).limit(200).get();
      const rows = (r.data || [])
        .map((d) => ({ ...d, id: d._id }))
        .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
      setAdoptChatMessages(rows);
    } catch (e) {
      console.warn('加载领养私信失败', e);
      showToast('加载消息失败，请确认云库已创建集合 adopt_messages');
    }
  };

  const loadOwnerAdoptThreads = async (pet) => {
    if (!db || !pet?.id) return;
    setAdoptChatThreadChoices([]);
    try {
      const r = await db.collection('adopt_messages').where({ petId: String(pet.id) }).limit(200).get();
      const rows = (r.data || []).map((d) => ({ ...d, id: d._id }));
      const map = new Map();
      rows.forEach((m) => {
        if (!m.threadId) return;
        const cur = map.get(m.threadId) || { threadId: m.threadId, lastAt: 0, snippet: '' };
        const t = m.createdAt || 0;
        if (t >= cur.lastAt) {
          cur.lastAt = t;
          cur.snippet = m.text || '';
        }
        map.set(m.threadId, cur);
      });
      const list = Array.from(map.values()).sort((a, b) => b.lastAt - a.lastAt);
      setAdoptChatThreadChoices(list);
      if (list.length === 1) {
        setAdoptChatThreadId(list[0].threadId);
        await reloadAdoptChat(list[0].threadId);
      }
    } catch (e) {
      console.warn('加载领养会话列表失败', e);
      showToast('加载会话失败，请确认云库集合 adopt_messages 可读');
    }
  };

  const openAdoptChat = (pet) => {
    requireAuth(() => {
      if (!pet) return;
      const owner = publisherKey(pet);
      const self = viewerKey(userProfile);
      if (!self) return;
      setAdoptChatPet(pet);
      if (owner === self) {
        setAdoptChatThreadId(null);
        setAdoptChatMessages([]);
        loadOwnerAdoptThreads(pet);
      } else {
        const tid = makeAdoptThreadId(pet, userProfile);
        setAdoptChatThreadId(tid);
        setAdoptChatThreadChoices([]);
        reloadAdoptChat(tid);
      }
    });
  };

  const handlePickAdoptThread = async (tid) => {
    setAdoptChatThreadId(tid);
    await reloadAdoptChat(tid);
  };

  const handleSendAdoptMessage = async (text) => {
    if (!adoptChatPet || !userProfile || !text?.trim()) return;

    const compliance = scanPrivateMessageCompliance(text);
    if (!compliance.ok) {
      showToast(complianceBlockMessage(compliance), 'warning');
      await recordContentBlocked(compliance, '拦截私信');
      return;
    }

    const tid = adoptChatThreadId || makeAdoptThreadId(adoptChatPet, userProfile);
    if (!tid) {
      showToast('无法建立会话');
      return;
    }
    const selfId = viewerKey(userProfile);
    const toId = otherPartyFromThreadId(tid, selfId);
    if (!toId) {
      showToast('无法识别对方账号，请稍后重试');
      return;
    }
    try {
      if (db) {
        await ensureCloudAnonymousAuth();
        await db.collection('adopt_messages').add({
          threadId: tid,
          petId: String(adoptChatPet.id),
          fromUserId: selfId,
          fromName: userProfile.name || '用户',
          toUserId: toId,
          text: text.trim(),
          createdAt: Date.now()
        });
        await reloadAdoptChat(tid);
        await recordAudit({
          action: AUDIT_ACTIONS.ADOPT_MESSAGE,
          targetType: 'adopt',
          targetId: String(adoptChatPet.id),
          summary: '领养私信发送',
          snapshot: {
            threadId: tid,
            petId: String(adoptChatPet.id),
            textLength: text.trim().length
          }
        });
      } else {
        showToast('当前未连接云数据库，无法发送私信');
      }
    } catch (e) {
      console.error(e);
      showToast('发送失败，请检查 adopt_messages 写权限');
    }
  };

  // 启动页（维护期间跳过，直接展示维护页）
  if (!maintenanceReady) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <Loader2 className="animate-spin text-amber-500" size={32} />
      </div>
    );
  }

  const maintenanceActive = isMaintenanceActive(siteMaintenance);
  const hasMaintenanceAccess =
    !!userProfile &&
    isMaintenanceBypassProfile(userProfile) &&
    hasMaintenanceAdminSession(userProfile.phone);
  const mustShowMaintenanceScreen = maintenanceActive && !hasMaintenanceAccess;

  if (showSplash && !maintenanceActive) {
    return (
      <div
        className="bg-black min-h-screen flex flex-col"
        style={{ minHeight: '100vh', background: '#000', color: '#fff' }}
      >
        <div className="flex-1 flex flex-col items-center justify-center p-6 cursor-pointer" onClick={() => setShowSplash(false)}>
          <h1
            className="text-7xl font-black text-amber-200 tracking-[1em] animate-pulse pl-8 mb-8"
            style={{ fontSize: '3rem', fontWeight: 900, color: '#fde68a', letterSpacing: '0.5em', marginBottom: '2rem' }}
          >
            予归
          </h1>
          <p
            className="text-white/40 tracking-[0.4em] text-xs font-black uppercase italic"
            style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', letterSpacing: '0.2em' }}
          >
            如果不爱了，请帮它找个家
          </p>
          <p className="text-white/25 text-[10px] mt-6" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', marginTop: '1.5rem' }}>
            点击进入
          </p>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openPlatformConvention(); }}
            className="mt-10 flex items-center gap-2 px-5 py-2.5 rounded-full border border-amber-500/40 text-amber-500 text-xs font-bold hover:bg-amber-500/10 transition-colors"
          >
            <ShieldCheck size={14} /> 阅读《予归平台公约》
          </button>
        </div>
        <SiteLegalFooter />
        {showPlatformConvention && (
          <LegalDocument
            title="予归平台公约"
            content={platformConventionContent}
            onClose={() => setShowPlatformConvention(false)}
          />
        )}
      </div>
    );
  }

  if (mustShowMaintenanceScreen) {
    return (
      <>
        <div className="min-h-screen flex flex-col bg-stone-950">
          <div className="flex-1">
            <SiteMaintenanceScreen
              config={siteMaintenance}
              isLoggedIn={isLoggedIn}
              onAdminLogin={openAuthModal}
              onLogout={() => performLogout('')}
            />
          </div>
          <SiteLegalFooter />
        </div>
        {showAuthModal && (
          <AuthModal
            onAuth={handleCloudAuth}
            onClose={() => setShowAuthModal(false)}
            onShowPrivacy={openPrivacyPolicy}
            onShowAgreement={openUserAgreement}
            onShowConvention={openPlatformConvention}
            showToast={showToast}
            adminLoginOnly
          />
        )}
        {toastMsg && (
          <div
            className={`fixed top-20 left-1/2 -translate-x-1/2 z-[200] px-5 py-3 rounded-2xl shadow-2xl flex items-center max-w-[90vw] text-sm font-bold border animate-slide-up ${
              toastType === 'error'
                ? 'bg-red-500 text-white border-red-900/20'
                : toastType === 'warning'
                  ? 'bg-zinc-800 text-amber-400 border-amber-500/30'
                  : 'bg-amber-500 text-black border-black/10'
            }`}
          >
            <CheckCircle2 size={18} className="mr-2 flex-shrink-0" />
            <span className="truncate">{toastMsg}</span>
          </div>
        )}
      </>
    );
  }

  // 主界面
  return (
    <div className="bg-stone-950 text-white min-h-screen font-sans flex flex-col relative overflow-hidden">
      {currentView === 'admin' && isLoggedIn && isAdminProfile(userProfile) ? (
        <AdminModal
          userProfile={userProfile}
          reports={reports}
          feedbackList={feedbackList}
          usersList={usersList}
          posts={posts}
          pets={adoptList}
          adminAllContent={adminAllContent}
          auditLogs={auditLogs}
          banRequests={banRequests}
          announcements={announcements}
          siteMaintenance={siteMaintenance}
          onClose={() => navigateTo('home')}
          onAction={handleAdminAction}
          onViewReportedContent={viewReportedContent}
          showToast={showToast}
        />
      ) : (
      <>
      {/* 顶部导航栏 — 桌面端保持原样；移动端精简 */}
      <header className="yugui-shell-header sticky top-0 h-14 md:h-16 bg-stone-950/80 backdrop-blur-xl border-b border-white/5 flex items-center justify-between px-3 md:px-6 lg:px-12 safe-area-top">
        <div className="flex items-center space-x-10 min-w-0">
          <div className="flex items-center space-x-2 cursor-pointer shrink-0" onClick={() => navigateTo('home')}>
            <Sparkles className="text-amber-500" size={22} />
            <span className="text-lg md:text-2xl font-black italic tracking-tighter uppercase">Yu Gui</span>
            <span className="hidden sm:inline text-[9px] font-bold text-amber-500/60 tracking-wider ml-0.5 shrink-0">v3.7.1</span>
          </div>
          <nav className="hidden md:flex space-x-8 text-sm font-black uppercase tracking-widest">
            <button onClick={() => navigateTo('home')} className={currentView === 'home' ? 'text-amber-500' : 'text-white/40 hover:text-white transition-colors'}>发现</button>
            <button onClick={() => navigateTo('adopt')} className={currentView === 'adopt' ? 'text-amber-500' : 'text-white/40 hover:text-white transition-colors'}>领养</button>
          </nav>
        </div>
        <div className="flex items-center space-x-2 md:space-x-6 shrink-0">
          <button
            type="button"
            onClick={openSearchBar}
            className={`p-2 rounded-full transition-colors ${showSearch ? 'text-amber-500 bg-amber-500/10' : 'text-white/40 hover:text-white hover:bg-white/5'}`}
            aria-label="搜索"
            aria-expanded={showSearch}
          >
            <Search size={20} />
          </button>
          <button type="button" onClick={() => requireAuth(openEmailModal)} className="hidden md:flex p-2 text-white/40 hover:text-amber-500 hover:bg-amber-500/10 rounded-full transition-colors relative" aria-label="消息通知">
            <MessageSquare size={20} />
            {notifications.length > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full" />}
          </button>
          <button type="button" onClick={() => requireAuth(openPublishModalFresh)} className="hidden md:flex px-6 py-2.5 bg-amber-500 text-black font-black text-xs rounded-full shadow-lg shadow-amber-500/20 items-center active:scale-95 transition-all">
            <Plus size={14} className="mr-1 stroke-[3px]" /> 发布
          </button>
          {isLoggedIn ? (
            <>
              <button type="button" className="yugui-mobile-only-inline md:hidden" onClick={openMobileUserMenu} aria-label="打开用户菜单">
                <div className="w-9 h-9 rounded-full border-2 border-amber-500/30 p-0.5">
                  <img src={userProfile.avatar} className="w-full h-full rounded-full object-cover" alt="" />
                </div>
              </button>
              <div className="hidden md:block relative group cursor-pointer" onClick={() => navigateTo('profile')}>
                <div className="w-10 h-10 rounded-full border-2 border-amber-500/30 p-0.5">
                  <img src={userProfile.avatar} className="w-full h-full rounded-full object-cover" alt="avatar" />
                </div>
                <div className="absolute top-full right-0 pt-2 w-56 hidden group-hover:block z-50">
                  <div className="bg-zinc-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
                    <button onClick={(e)=>{e.stopPropagation(); resetModalsForNav(); navigateTo('profile');}} className="w-full px-5 py-4 text-left text-xs font-bold hover:bg-white/5 border-b border-white/5 flex items-center">
                      <User size={14} className="mr-2"/>个人主页
                    </button>
                    <button onClick={(e)=>{e.stopPropagation(); openFeedbackModal();}} className="w-full px-5 py-4 text-left text-xs font-bold hover:bg-white/5 border-b border-white/5 flex items-center">
                      <MessageSquare size={14} className="mr-2"/>官方反馈
                    </button>
                    {isAdminProfile(userProfile) && (
                      <button onClick={(e)=>{e.stopPropagation(); openAdminConsole();}} className="w-full px-5 py-4 text-left text-xs font-black text-amber-500 hover:bg-amber-500/10 border-b border-white/5 flex items-center">
                        <LayoutDashboard size={14} className="mr-2"/>{isSuperAdminProfile(userProfile) ? '超级管理后台' : '管理后台'}
                      </button>
                    )}
                    <button onClick={(e)=>{e.stopPropagation(); performLogout();}} className="w-full px-5 py-4 text-left text-xs font-black text-red-500 hover:bg-red-500/10 flex items-center">
                      <LogOut size={14} className="mr-2"/>安全退出
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <button type="button" onClick={openAuthModal} className="text-xs md:text-sm font-black uppercase tracking-widest text-white/40 hover:text-white border border-white/10 px-3 md:px-4 py-1.5 md:py-2 rounded-full hover:border-white/30 transition-all">
              登录
            </button>
          )}
          <button
            type="button"
            onClick={openPlatformConvention}
            className="hidden md:flex items-center gap-1.5 text-[10px] font-bold text-amber-500/80 hover:text-amber-500 border border-amber-500/20 px-2.5 sm:px-3 py-1.5 rounded-full hover:border-amber-500/40 transition-colors shrink-0"
            title="予归平台公约"
          >
            <ShieldCheck size={12} /> 平台公约
          </button>
        </div>
      </header>

      {showSearch && (
        <div className="fixed inset-x-0 top-[calc(3.5rem+env(safe-area-inset-top,0px))] md:top-16 bottom-[calc(3.5rem+env(safe-area-inset-bottom,0px))] md:bottom-0 z-[8500] bg-stone-950 flex flex-col animate-slide-up">
          <div className="px-4 md:px-8 py-4 border-b border-white/10 shrink-0">
            <div className="flex items-center gap-3">
              <button type="button" onClick={handleCloseSearch} className="p-2 -ml-2 text-white/40 hover:text-white hover:bg-white/10 rounded-full transition-colors shrink-0" aria-label="关闭搜索">
                <ChevronLeft size={24} />
              </button>
              <div className="flex-1 flex items-center bg-white/5 border border-white/10 rounded-full px-3 md:px-4 py-2.5 min-w-0">
                <Search size={18} className="text-white/40 mr-2 shrink-0" />
                <input
                  autoFocus
                  type="search"
                  placeholder={searchTabMeta[searchTab].placeholder}
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                  className="flex-1 bg-transparent text-white text-sm outline-none min-w-0"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setIsSearching(false);
                      setSearchResults({ works: [], adopts: [], users: [], documents: [] });
                    }}
                    className="p-1.5 text-white/40 hover:text-white rounded-full shrink-0"
                    aria-label="清空"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              <button type="button" onClick={handleSearch} className="px-4 md:px-5 py-2.5 bg-amber-500 text-black text-xs font-black rounded-full shrink-0 hover:bg-amber-400 transition-colors">
                搜索
              </button>
            </div>

            <div className="flex gap-2 mt-4">
              {['works', 'adopt', 'users'].map((tab) => {
                const count =
                  isSearching
                    ? tab === 'works'
                      ? searchResults.works.length
                      : tab === 'adopt'
                        ? searchResults.adopts.length
                        : searchResults.users.length
                    : 0;
                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setSearchTab(tab)}
                    className={`px-4 py-2 rounded-full text-xs font-bold transition-colors ${
                      searchTab === tab
                        ? 'bg-amber-500 text-black'
                        : 'bg-white/5 text-white/50 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {searchTabMeta[tab].label}
                    {isSearching && count > 0 ? ` (${count})` : ''}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto no-scrollbar px-4 md:px-8 py-6">
            {!isSearching ? (
              <div className="flex flex-col items-center justify-center py-20 md:py-32 text-white/30">
                <Search size={56} className="mb-6 text-white/15" />
                <p className="font-black text-lg mb-2 text-white/70">选择分类并输入关键词</p>
                <p className="text-sm text-white/40 text-center px-6 max-w-md leading-relaxed">
                  当前分类：{searchTabMeta[searchTab].label}。{searchTabMeta[searchTab].placeholder}
                </p>
              </div>
            ) : (
              <>
            {searchResults.documents?.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-black uppercase tracking-widest text-white/40 mb-4">平台文档</h3>
                <div className="space-y-3">
                  {searchResults.documents.map((doc) => (
                    <button
                      key={doc.id}
                      type="button"
                      onClick={() => openSearchDocument(doc.id)}
                      className="w-full flex items-center gap-4 p-4 bg-white/5 rounded-2xl border border-white/5 hover:bg-amber-500/10 hover:border-amber-500/30 transition-colors text-left"
                    >
                      <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                        <FileText size={20} className="text-amber-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-sm text-white">{doc.title}</p>
                        <p className="text-xs text-white/40 mt-1">{doc.hint}</p>
                      </div>
                      <ChevronRight size={18} className="text-white/20 shrink-0 ml-auto" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {searchTab === 'works' && (
              searchResults.works.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {searchResults.works.map((post) => (
                    <div key={post.id} className="aspect-[3/4] rounded-[2rem] overflow-hidden relative cursor-pointer group shadow-2xl border border-white/5">
                      <img src={post.img} className="w-full h-full object-cover" alt="post" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent pointer-events-none" />
                      <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
                        {post.title && <p className="text-xs font-bold text-white line-clamp-2 mb-1">{post.title}</p>}
                        <p className="text-xs font-black flex items-center"><Heart size={14} className="mr-1.5 fill-white"/>{post.likes || 0}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 md:py-24 text-white/30">
                  <Search size={56} className="mb-6 text-white/15" />
                  <p className="font-black text-xl mb-3 text-white/70">未找到相关作品</p>
                  <p className="text-sm text-white/40 text-center px-6 max-w-md leading-relaxed">
                    未找到与「{searchQuery}」匹配的作品。{getSearchEmptyHint('works')}
                  </p>
                </div>
              )
            )}

            {searchTab === 'adopt' && (
              searchResults.adopts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {searchResults.adopts.map((pet) => (
                    <button
                      key={pet.id}
                      type="button"
                      onClick={() => { handleCloseSearch(); openAdoptChat(pet); }}
                      className="bg-zinc-900 rounded-[1.5rem] overflow-hidden border border-white/5 shadow-xl text-left hover:border-amber-500/30 transition-colors"
                    >
                      <div className="h-40 relative overflow-hidden bg-black">
                        {isPostVideoMedia(pet) ? (
                          <video src={pet.img} className="w-full h-full object-cover" muted playsInline loop autoPlay />
                        ) : (
                          <img src={pet.img} className="w-full h-full object-cover" alt={pet.name || 'pet'} />
                        )}
                      </div>
                      <div className="p-4">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <h3 className="font-black text-base">{pet.name || '未命名'}</h3>
                          {pet.petType && (
                            <span className="text-[10px] font-black bg-white/5 px-2 py-1 rounded shrink-0">{pet.petType}</span>
                          )}
                        </div>
                        <p className="text-xs text-white/50 line-clamp-2">{pet.content || pet.description || ''}</p>
                        {(pet.city || pet.location || pet.userLocation) && (
                          <p className="text-[10px] text-amber-400/80 mt-3 flex items-center gap-1">
                            <MapPin size={12} /> {pet.city || pet.location || pet.userLocation}
                          </p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 md:py-24 text-white/30">
                  <Heart size={56} className="mb-6 text-red-500/30" />
                  <p className="font-black text-xl mb-3 text-white/70">未找到相关领养</p>
                  <p className="text-sm text-white/40 text-center px-6 max-w-md leading-relaxed">
                    未找到与「{searchQuery}」匹配的领养信息。{getSearchEmptyHint('adopt')}
                  </p>
                </div>
              )
            )}

            {searchTab === 'users' && (
              searchResults.users.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {searchResults.users.map((user) => (
                    <div key={user.id || user.uid} className="flex flex-col items-center p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors">
                      <button type="button" onClick={() => openUserByProfile(user)} className="flex flex-col items-center w-full">
                        <img src={user.avatar || DEFAULT_AVATAR} className="w-16 h-16 rounded-full object-cover mb-3" alt="avatar" />
                        <p className="font-bold text-sm mb-1">{user.name}</p>
                        <p className="text-xs text-amber-400/80 font-mono mb-3">ID: {user.uid}</p>
                      </button>
                      <button onClick={() => requireAuth(() => handleFollow(user.id || user.uid))} className="px-4 py-1.5 bg-amber-500 text-black text-xs font-bold rounded-full hover:bg-amber-400 transition-colors">
                        关注
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 md:py-24 text-white/30">
                  <User size={56} className="mb-6 text-white/15" />
                  <p className="font-black text-xl mb-3 text-white/70">未找到相关用户</p>
                  <p className="text-sm text-white/40 text-center px-6 max-w-md leading-relaxed">
                    未找到与「{searchQuery}」匹配的用户。{getSearchEmptyHint('users')}
                  </p>
                </div>
              )
            )}
              </>
            )}
          </div>
        </div>
      )}

      {/* 主内容区 */}
      <main className="yugui-main-with-tabbar flex-1 min-h-0 overflow-hidden relative flex flex-col md:pb-0">
        <SyncStatusBanner queueLength={syncQueue.length} isSyncing={isSyncingQueue} />
        
        {/* 发现页 - 全屏滑动（每条固定一屏高度，竖滑切换，接近抖音单列） */}
        {currentView === 'home' && !isSearching && (
          <div className="feed-viewport flex-1 min-h-0 w-full overflow-y-auto overscroll-y-contain snap-y snap-mandatory no-scrollbar bg-black">
            {feedsLoading && discoverFeedPostsForView.length === 0 ? (
              <FeedLoadingState error={feedsLoadError} onRetry={retryFeedsLoad} />
            ) : discoverFeedPostsForView.length > 0 ? discoverFeedPostsForView.map((post) => {
              const isPostVideo = isPostVideoMedia(post);
              const mediaUrl = resolvePostMediaUrl(post);
              const authorAvatar = resolveAuthorAvatar(post);
              return (
              <div
                key={post.id}
                id={`discover-feed-${post.id}`}
                className="feed-slide w-full sm:max-w-[500px] mx-auto snap-start snap-always shrink-0 relative bg-black border-x border-white/5 overflow-hidden flex items-center justify-center"
              >
                {isPostVideo && mediaUrl ? (
                  <>
                  <FeedVideo
                    src={mediaUrl}
                    soundOn={videoSoundPostId === post.id}
                    className="absolute inset-0 w-full h-full object-cover opacity-95"
                  />
                  <button
                    type="button"
                    aria-label={videoSoundPostId === post.id ? '关闭声音' : '开启声音'}
                    onClick={(e) => {
                      e.stopPropagation();
                      setVideoSoundPostId((cur) => (cur === post.id ? null : post.id));
                    }}
                    className="absolute left-3 md:left-4 bottom-32 md:bottom-36 z-20 flex items-center gap-2 px-3 py-2 rounded-full bg-black/50 border border-white/20 text-xs font-black text-white hover:bg-white/10 pointer-events-auto"
                  >
                    {videoSoundPostId === post.id ? (
                      <>
                        <Volume2 size={18} className="text-amber-400" /> 声音开
                      </>
                    ) : (
                      <>
                        <VolumeX size={18} /> 开声音
                      </>
                    )}
                  </button>
                  </>
                ) : mediaUrl ? (
                  <img src={mediaUrl} className="absolute inset-0 w-full h-full object-cover opacity-90" alt="post" />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white/40 gap-2">
                    <Sparkles size={48} className="text-amber-500/50" />
                    <p className="text-sm font-bold">媒体加载失败，请重新发布</p>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/20 pointer-events-none" />
                
                <div className="absolute right-3 md:right-5 bottom-24 md:bottom-28 flex flex-col items-center space-y-5 md:space-y-7 z-10">
                  <button type="button" onClick={() => openAuthorProfile(post)} aria-label={`查看 ${post.user} 的主页`}>
                    <img src={authorAvatar} alt="" className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover border-2 border-white/30" />
                  </button>
                  {isMine(post.userId) && (
                    <button type="button" onClick={(e) => { e.stopPropagation(); setPendingDeletePost(post); }} className="flex flex-col items-center group/btn" aria-label="删除作品">
                      <Trash2 size={28} className="text-white/80 group-hover:text-red-500" />
                      <span className="text-[10px] font-black mt-1 text-white/50">删除</span>
                    </button>
                  )}
                  <button type="button" aria-label="点赞" onClick={(e) => handleLike(post, e)} className="flex flex-col items-center group/btn">
                    <Heart size={32} className={`md:w-9 md:h-9 transition-all text-white group-hover:text-red-500 ${listIncludesMe(post.likedBy) ? 'text-red-500 fill-red-500' : ''}`} />
                    <span className="text-[11px] font-black mt-1 drop-shadow-lg">{post.likes || 0}</span>
                  </button>
                  <button type="button" aria-label="收藏" onClick={(e) => handleBookmark(post, e)} className="flex flex-col items-center group/btn">
                    <Bookmark size={32} className={`md:w-9 md:h-9 transition-all text-white group-hover:text-amber-500 ${listIncludesMe(post.bookmarkedBy) ? 'text-amber-500 fill-amber-500' : ''}`} />
                  </button>
                  <button type="button" aria-label="评论" onClick={(e) => { e.stopPropagation(); openCommentModal(post); }} className="flex flex-col items-center group/btn">
                    <MessageCircle size={32} className="md:w-9 md:h-9 transition-all text-white group-hover:text-blue-500" />
                    <span className="text-[11px] font-black mt-1 drop-shadow-lg">{post.commentsCount || 0}</span>
                  </button>
                  <button type="button" aria-label="举报违规内容" onClick={(e) => { e.stopPropagation(); requireAuth(() => openReportModal({ id: post.id, type: post.type })); }} className="flex flex-col items-center group/btn mt-1 md:mt-2">
                    <Flag size={26} className="md:w-7 md:h-7 text-white/70 group-hover/btn:text-red-500 transition-colors drop-shadow-lg" />
                    <span className="text-[10px] font-black mt-1 text-white/55 group-hover/btn:text-red-400">举报</span>
                  </button>
                </div>

                <FeedCaption
                  post={post}
                  authorLabel={formatFeedAuthorLabel(post)}
                  authorAvatar={authorAvatar}
                  expanded={expandedFeedPostId === post.id}
                  onToggleExpand={() => setExpandedFeedPostId((cur) => (cur === post.id ? null : post.id))}
                  onAuthorClick={() => openAuthorProfile(post)}
                />
              </div>
            );
            }) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-white/30">
                <Sparkles size={64} className="mb-6 text-amber-500/50" />
                <p className="font-black text-xl mb-4">期待你的发布</p>
                <p className="text-sm text-white/40 mb-6">分享你的宠物故事，帮助更多毛孩子找到家</p>
                <button onClick={() => requireAuth(openPublishModalFresh)} className="px-6 py-3 border border-amber-500/30 rounded-full text-sm font-bold hover:bg-amber-500/10 hover:border-amber-500/50 transition-all">
                  发布第一条动态
                </button>
              </div>
            )}
          </div>
        )}

        {/* 领养大厅页 */}
        {currentView === 'adopt' && !isSearching && (
          <div className="yugui-scroll-panel max-w-7xl mx-auto w-full px-4 md:px-6 py-6 md:py-8 no-scrollbar">
            <div className="flex items-center justify-between mb-4 md:mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-black text-amber-500">领养大厅</h2>
                <p className="text-white/40 mt-1 md:mt-2 text-sm">给流浪的生命一个家</p>
              </div>
            </div>
            <AdoptFilterBar
              filterType={adoptFilterType}
              filterLocation={adoptFilterLocation}
              onTypeChange={setAdoptFilterType}
              onLocationChange={setAdoptFilterLocation}
            />
            {feedsLoading && adoptList.length === 0 ? (
              <FeedLoadingState error={feedsLoadError} onRetry={retryFeedsLoad} />
            ) : filteredAdoptList.length === 0 ? (
              <div className="py-20 md:py-32 flex flex-col items-center justify-center opacity-30">
                <Heart size={64} className="mb-6 text-red-500/50"/>
                <p className="font-black tracking-widest uppercase mb-4">{adoptList.length === 0 ? '暂无领养信息' : '没有符合筛选条件的领养'}</p>
                <p className="text-sm text-white/40 mb-6 text-center px-4">帮助流浪的毛孩子找到温暖的家</p>
                <button onClick={() => requireAuth(openPublishModalFresh)} className="px-6 py-3 border border-amber-500/30 rounded-full text-sm font-bold hover:bg-amber-500/10 hover:border-amber-500/50 transition-all">
                  发布领养信息
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {filteredAdoptList.map((pet) => (
                  <div
                    key={pet.id}
                    id={`adopt-card-${pet.id}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => openAdoptChat(pet)}
                    onKeyDown={(e) => e.key === 'Enter' && openAdoptChat(pet)}
                    className="bg-zinc-900 rounded-[2rem] overflow-hidden border border-white/5 shadow-xl group cursor-pointer hover:-translate-y-1 transition-all relative text-left"
                  >
                    {isMine(pet.userId) && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingDeletePost(pet);
                        }}
                        className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/60 border border-white/20 text-white/80 hover:text-red-400 hover:border-red-500/50"
                        title="删除本条领养（仅本人）"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                    <div className="h-48 relative overflow-hidden bg-black">
                      {isPostVideoMedia(pet) ? (
                        <video
                          src={resolvePostMediaUrl(pet)}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          muted
                          playsInline
                          loop
                          autoPlay
                        />
                      ) : (
                        <img src={resolvePostMediaUrl(pet) || pet.img} className="w-full h-full object-cover group-hover:scale-105 transition-transform" alt="pet" />
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-lg font-black">{pet.name}</h3>
                        <span className="text-[10px] font-black bg-white/5 px-2 py-1 rounded">{pet.petType}</span>
                      </div>
                      <p className="text-xs text-white/50 line-clamp-2">{pet.content || pet.description || ''}</p>
                      <div className="mt-4 flex gap-2">
                        <span className="flex-1 text-center text-[10px] font-black text-amber-500 border border-amber-500/30 py-2 rounded-full">
                          点卡片私聊发布者
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 他人主页 */}
        {currentView === 'user' && viewingUser && !isSearching && (
          <div className="yugui-scroll-panel bg-stone-950 pb-28 md:pb-12 animate-fade-in no-scrollbar">
            <div className="max-w-5xl mx-auto px-4 md:px-10 py-6">
              <button type="button" onClick={() => navigateTo('home')} className="mb-6 text-sm font-bold text-white/50 hover:text-white flex items-center gap-1">
                <ChevronLeft size={18} /> 返回
              </button>
              <div className="flex flex-col md:flex-row items-center md:items-end gap-6 mb-6 md:mb-8">
                <img src={viewingUser.avatar || DEFAULT_AVATAR} alt="" className="w-24 h-24 md:w-36 md:h-36 rounded-[2rem] border-4 border-stone-800 object-cover shadow-2xl shrink-0" />
                <div className="flex-1 text-center md:text-left min-w-0">
                  <div className="flex items-center justify-center md:justify-start gap-2 flex-wrap">
                    <h2 className="text-xl md:text-3xl font-black break-words">{viewingUser.name}</h2>
                    {viewingUser.isVerified && <BadgeCheck size={20} className="text-amber-500 shrink-0" />}
                    {viewingUser.role === 'super_admin' && (
                      <span className="text-[10px] font-black bg-amber-500 text-black px-2 py-0.5 rounded-full shrink-0">官方账号</span>
                    )}
                  </div>
                  <p className="text-xs text-white/40 font-mono mt-2 break-all">ID: {viewingUser.uid || '—'}</p>
                  {formatProfileLocationDisplay(viewingUser) !== UNKNOWN_LOCATION && (
                    <p className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-400 mt-2">
                      <MapPin size={14} className="shrink-0" />
                      {formatProfileLocationDisplay(viewingUser)}
                    </p>
                  )}
                  <p className="text-sm text-white/60 mt-3 italic max-w-lg mx-auto md:mx-0 break-words">
                    “{viewingUser.bio || '暂无简介'}”
                  </p>
                </div>
                {!isMine(viewingUser.id) && !isMine(viewingUser.uid) && (
                  <button
                    type="button"
                    onClick={() => requireAuth(() => handleFollow(viewingUser.id || viewingUser.uid))}
                    className={`px-6 md:px-8 py-2.5 md:py-3 font-black rounded-full text-sm shadow-lg shrink-0 transition-colors ${
                      isFollowingViewingUser
                        ? 'bg-white/10 text-white/60 border border-white/10'
                        : 'bg-amber-500 text-black shadow-amber-500/20 hover:bg-amber-400'
                    }`}
                  >
                    {isFollowingViewingUser ? '已关注' : '关注'}
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6 px-1 sm:px-0 sm:flex sm:gap-8">
                <div className="text-center">
                  <p className="text-lg sm:text-xl font-black">{viewingUserPublicPosts.length}</p>
                  <p className="text-[10px] text-white/30 font-black uppercase tracking-widest mt-1">作品</p>
                </div>
                <div className="text-center">
                  <p className="text-lg sm:text-xl font-black">{viewingUserPublicAdopts.length}</p>
                  <p className="text-[10px] text-white/30 font-black uppercase tracking-widest mt-1">领养</p>
                </div>
                <div className="text-center col-span-2 sm:col-span-1">
                  <p className="text-lg sm:text-xl font-black">{viewingUser.followers || 0}</p>
                  <p className="text-[10px] text-white/30 font-black uppercase tracking-widest mt-1">粉丝</p>
                </div>
              </div>

              <div className="flex border-b border-white/10 mb-4 sm:mb-6 overflow-x-auto no-scrollbar -mx-1 px-1">
                {[
                  { id: 'works', label: '作品', count: viewingUserPublicPosts.length },
                  { id: 'adopts', label: '领养', count: viewingUserPublicAdopts.length }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setViewingUserTab(tab.id)}
                    className={`pb-3 sm:pb-4 px-4 sm:px-6 text-xs sm:text-sm font-bold whitespace-nowrap transition-colors relative shrink-0 ${
                      viewingUserTab === tab.id ? 'text-amber-500' : 'text-white/40 hover:text-white'
                    }`}
                  >
                    {tab.label}{' '}
                    <span className="ml-1 text-[10px] bg-white/5 px-1.5 sm:px-2 py-0.5 rounded-full">{tab.count}</span>
                    {viewingUserTab === tab.id && (
                      <div className="absolute bottom-0 left-2 right-2 sm:left-4 sm:right-4 h-1 bg-amber-500 rounded-full shadow-[0_0_10px_rgba(245,158,11,0.5)]" />
                    )}
                  </button>
                ))}
              </div>

              {viewingUserTab === 'works' && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                  {viewingUserPublicPosts.map((p) => (
                    <ProfileWorkTile
                      key={p.id}
                      mediaUrl={resolvePostMediaUrl(p)}
                      isVideo={isPostVideoMedia(p)}
                      onClick={() => openPostInDiscover(p, 'user')}
                      footer={
                        <div className="absolute bottom-3 left-3 right-3 pointer-events-none">
                          {p.title && <p className="text-[10px] font-bold text-white line-clamp-2 mb-1">{p.title}</p>}
                          <p className="text-[10px] font-black flex items-center text-white/90">
                            <Heart size={12} className="mr-1 fill-white" />
                            {p.likes || 0}
                          </p>
                        </div>
                      }
                    />
                  ))}
                  {viewingUserPublicPosts.length === 0 && (
                    <div className="col-span-full py-16 flex flex-col items-center justify-center opacity-30">
                      <PlaySquare size={48} />
                      <p className="text-xs font-black mt-4 uppercase">暂无公开作品</p>
                    </div>
                  )}
                </div>
              )}

              {viewingUserTab === 'adopts' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {viewingUserPublicAdopts.map((pet) => (
                    <div
                      key={pet.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => openAdoptChat(pet)}
                      onKeyDown={(e) => e.key === 'Enter' && openAdoptChat(pet)}
                      className="bg-zinc-900 rounded-[1.5rem] md:rounded-[2rem] overflow-hidden border border-white/5 shadow-xl cursor-pointer hover:-translate-y-0.5 transition-all text-left"
                    >
                      <div className="h-40 md:h-48 relative overflow-hidden bg-black">
                        {isPostVideoMedia(pet) ? (
                          <video src={pet.img} className="w-full h-full object-cover" muted playsInline loop autoPlay />
                        ) : (
                          <img src={pet.img} className="w-full h-full object-cover" alt="" />
                        )}
                      </div>
                      <div className="p-4 md:p-5">
                        <div className="flex justify-between items-center mb-2 gap-2">
                          <h3 className="text-base md:text-lg font-black truncate">{pet.name || '待领养'}</h3>
                          <span className="text-[10px] font-black bg-white/5 px-2 py-1 rounded shrink-0">{pet.petType || '宠物'}</span>
                        </div>
                        <p className="text-xs text-white/50 line-clamp-2">{pet.content || pet.description || ''}</p>
                      </div>
                    </div>
                  ))}
                  {viewingUserPublicAdopts.length === 0 && (
                    <div className="col-span-full py-16 flex flex-col items-center justify-center opacity-30">
                      <Heart size={48} />
                      <p className="text-xs font-black mt-4 uppercase">暂无领养信息</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 个人主页 */}
        {currentView === 'profile' && userProfile && !isSearching && (
          <div className="yugui-scroll-panel bg-stone-950 pb-28 md:pb-20 relative animate-fade-in no-scrollbar">
             <div className="w-full h-36 sm:h-44 md:h-80 relative bg-zinc-900 rounded-b-[1.5rem] md:rounded-b-[4rem] overflow-hidden shadow-2xl mb-4 md:mb-8">
                <img src={userProfile.coverImg || DEFAULT_COVER} className="w-full h-full object-cover opacity-60" alt="cover"/>
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/20 to-transparent"></div>
             </div>
             <div className="max-w-5xl mx-auto px-4 md:px-10 -mt-12 sm:-mt-14 md:-mt-32 relative z-10">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5 md:mb-8">
                   <div className="flex items-end gap-3 min-w-0">
                     <img src={userProfile.avatar} className="w-20 h-20 sm:w-28 sm:h-28 md:w-40 md:h-40 rounded-[1.25rem] sm:rounded-[2rem] md:rounded-[3.5rem] border-4 md:border-8 border-stone-950 shadow-2xl bg-zinc-800 object-cover shrink-0" alt="avatar"/>
                     <button onClick={openSettingsModal} className="sm:hidden px-4 py-2 bg-white/5 border border-white/10 rounded-full text-xs font-black hover:bg-white/10 transition-colors flex items-center shadow-xl shrink-0">
                       <Settings size={14} className="mr-1.5"/>设置
                     </button>
                   </div>
                   <div className="hidden sm:flex space-x-3 mb-2">
                      <button onClick={openSettingsModal} className="px-4 md:px-8 py-2 md:py-3 bg-white/5 border border-white/10 rounded-full text-xs font-black hover:bg-white/10 transition-colors flex items-center shadow-xl">
                        <Settings size={14} className="mr-2"/>账号设置
                      </button>
                   </div>
                </div>
                
                <div className="space-y-2 mb-4 md:mb-6">
                  <div className="flex items-center gap-2 flex-wrap">
                     <h2 className="text-xl sm:text-2xl md:text-4xl font-black break-words">{userProfile.name}</h2>
                     {userProfile.isVerified && <BadgeCheck size={22} className="text-amber-500 shrink-0" />}
                     {userProfile.role === 'super_admin' && <span className="text-[10px] sm:text-xs font-black bg-amber-500 text-black px-2.5 py-1 rounded-full shrink-0">官方账号</span>}
                  </div>
                  <p className="text-[10px] text-white/40 font-mono tracking-widest uppercase break-all">
                    用户ID: <span className="text-amber-400/90">{userProfile.uid || '未分配'}</span>
                  </p>
                  {formatProfileLocationDisplay(userProfile) !== UNKNOWN_LOCATION ? (
                    <p className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-400">
                      <MapPin size={14} className="shrink-0" />
                      {formatProfileLocationDisplay(userProfile)}
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={openSettingsModal}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-white/40 hover:text-amber-400 transition-colors"
                    >
                      <MapPin size={14} className="shrink-0" />
                      未设置城市 · 前往账号设置
                    </button>
                  )}
                </div>

                <p className="text-sm text-white/80 leading-relaxed bg-white/5 p-4 sm:p-6 rounded-[1.25rem] sm:rounded-[2rem] border border-white/5 italic break-words">
                  “{userProfile.bio}”
                </p>

                <div className="mt-4 grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:gap-3">
                  <button
                    type="button"
                    onClick={openFeedbackModal}
                    className="flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-1 sm:gap-2 px-2 sm:px-5 py-2.5 sm:py-3 rounded-xl bg-white/5 border border-white/10 text-[11px] sm:text-xs font-bold text-white/80 hover:bg-white/10 hover:text-white transition-colors min-h-[72px] sm:min-h-0"
                  >
                    <MessageSquare size={18} className="text-amber-500 shrink-0" />
                    <span className="text-center leading-tight">官方反馈</span>
                  </button>
                  <button
                    type="button"
                    onClick={openPlatformConvention}
                    className="flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-1 sm:gap-2 px-2 sm:px-5 py-2.5 sm:py-3 rounded-xl bg-white/5 border border-white/10 text-[11px] sm:text-xs font-bold text-white/80 hover:bg-white/10 hover:text-white transition-colors min-h-[72px] sm:min-h-0"
                  >
                    <ShieldCheck size={18} className="text-amber-500 shrink-0" />
                    <span className="text-center leading-tight">平台公约</span>
                  </button>
                  <button
                    type="button"
                    onClick={openSettingsModal}
                    className="flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-1 sm:gap-2 px-2 sm:px-5 py-2.5 sm:py-3 rounded-xl bg-white/5 border border-white/10 text-[11px] sm:text-xs font-bold text-white/80 hover:bg-white/10 hover:text-white transition-colors min-h-[72px] sm:min-h-0"
                  >
                    <Settings size={18} className="text-amber-500 shrink-0" />
                    <span className="text-center leading-tight">账号设置</span>
                  </button>
                </div>
                
                <div className="grid grid-cols-3 gap-3 mt-6 sm:mt-8 px-1 sm:px-4 sm:flex sm:space-x-12">
                   <div className="text-center cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setProfileTab('following')}>
                     <p className="text-xl sm:text-2xl font-black">{userProfile.followingList?.length || 0}</p>
                     <p className="text-[10px] text-white/30 font-black uppercase tracking-widest mt-1">关注</p>
                   </div>
                   <div className="text-center cursor-pointer hover:opacity-80 transition-opacity">
                     <p className="text-xl sm:text-2xl font-black">{userProfile.followers || 0}</p>
                     <p className="text-[10px] text-white/30 font-black uppercase tracking-widest mt-1">粉丝</p>
                   </div>
                   <div className="text-center cursor-pointer hover:opacity-80 transition-opacity">
                     <p className="text-xl sm:text-2xl font-black">{myWorks.reduce((a,c)=>a+(c.likes||0),0)}</p>
                     <p className="text-[10px] text-white/30 font-black uppercase tracking-widest mt-1">获赞</p>
                   </div>
                </div>

                <div className="mt-8 sm:mt-12">
                  <ProfileTabGroups
                    activeTab={profileTab}
                    counts={{
                      works: myPosts.length,
                      adopts: myAdopts.length,
                      drafts: publishDrafts.length,
                      likes: myLikes.length,
                      bookmarks: myBookmarks.length,
                      following: myFollowing.length
                    }}
                    onTabChange={setProfileTab}
                  />

                  <div className="mt-5 sm:mt-6">
                  {(profileTab === 'works' || profileTab === 'adopts') && (
                    <div className="mb-5">
                      {showProfileBatchHint && (
                        <div className="mb-4 p-4 rounded-xl bg-blue-500/10 border border-blue-500/25 flex flex-col sm:flex-row sm:items-center gap-3">
                          <p className="text-sm text-white/80 leading-relaxed flex-1">
                            点击「批量管理」可一次删除多条内容{profileTab === 'works' ? '，或将作品设为仅自己可见' : ''}。
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              markUxHintSeen(UX_HINT_KEYS.profileBatch);
                              setShowProfileBatchHint(false);
                            }}
                            className="shrink-0 px-4 py-2 rounded-lg bg-blue-500/20 text-blue-300 text-xs font-black hover:bg-blue-500/30"
                          >
                            知道了
                          </button>
                        </div>
                      )}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setProfileBatchMode((m) => {
                            if (m) setProfileBatchIds([]);
                            return !m;
                          });
                        }}
                        className={`px-4 py-2 rounded-full text-xs font-black border transition-colors ${
                          profileBatchMode
                            ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                            : 'bg-white/5 border-white/10 text-white/70 hover:text-white'
                        }`}
                      >
                        {profileBatchMode ? '退出批量' : '批量管理'}
                      </button>
                      {profileBatchMode && (
                        <>
                          <button
                            type="button"
                            onClick={toggleProfileBatchSelectAll}
                            className="px-4 py-2 rounded-full text-xs font-bold bg-white/5 border border-white/10 text-white/80 hover:bg-white/10"
                          >
                            全选 / 取消全选
                          </button>
                          <span className="text-[10px] text-white/40 font-mono">
                            已选 {profileBatchIds.length} 项
                          </span>
                        </>
                      )}
                      {profileBatchMode && profileBatchIds.length > 0 && (
                        <>
                          <button
                            type="button"
                            onClick={runBatchProfileDelete}
                            className="px-4 py-2 rounded-full text-xs font-black bg-red-500/15 text-red-400 border border-red-500/40 hover:bg-red-500 hover:text-white transition-colors"
                          >
                            批量删除
                          </button>
                          {profileTab === 'works' && (
                            <>
                              <button
                                type="button"
                                onClick={() => runBatchProfileVisibility('private')}
                                className="px-4 py-2 rounded-full text-xs font-black bg-white/5 border border-white/15 text-white/80 hover:bg-white/10"
                              >
                                仅自己可见
                              </button>
                              <button
                                type="button"
                                onClick={() => runBatchProfileVisibility('public')}
                                className="px-4 py-2 rounded-full text-xs font-black bg-amber-500/20 text-amber-400 border border-amber-500/40 hover:bg-amber-500 hover:text-black transition-colors"
                              >
                                所有人可见
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                    </div>
                  )}

                  {/* 我的作品 */}
                  {profileTab === 'works' && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 animate-fade-in">
                       {myPosts.map((p) => {
                         const mediaUrl = resolvePostMediaUrl(p);
                         const isVideo = isPostVideoMedia(p);
                         return (
                          <div key={p.id} className="relative">
                             {profileBatchMode && (
                               <button
                                 type="button"
                                 aria-label={profileBatchIds.includes(String(p.id)) ? '取消选择' : '选择'}
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   requireAuth(() => toggleProfileBatchId(p.id));
                                 }}
                                 className="absolute top-3 left-3 z-20 p-1.5 rounded-xl bg-black/60 border border-white/20 text-amber-400 hover:bg-black/80"
                               >
                                 {profileBatchIds.includes(String(p.id)) ? (
                                   <CheckSquare size={20} strokeWidth={2.5} />
                                 ) : (
                                   <Square size={20} className="text-white/50" strokeWidth={2} />
                                 )}
                               </button>
                             )}
                             <ProfileWorkTile
                               mediaUrl={mediaUrl}
                               isVideo={isVideo}
                               onClick={() => {
                                 if (profileBatchMode) {
                                   requireAuth(() => toggleProfileBatchId(p.id));
                                   return;
                                 }
                                 requireAuth(() => openPostInDiscover(p, 'profile'));
                               }}
                               footer={
                                 <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-2 pointer-events-none">
                                   <div className="text-[10px] font-black flex items-center gap-2 text-white/90">
                                     <span className="inline-flex items-center"><Heart size={12} className="mr-1 fill-white"/>{getWorkInteractionCounts(p).likes}</span>
                                     <span className="inline-flex items-center"><MessageCircle size={12} className="mr-1"/>{getWorkInteractionCounts(p).comments}</span>
                                   </div>
                                   {!profileBatchMode ? (
                                     <span className="text-[10px] font-bold text-amber-400/90 truncate max-w-[40%]">{p.title || '作品'}</span>
                                   ) : null}
                                 </div>
                               }
                             />
                             {!profileBatchMode && (
                               <button
                                 type="button"
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   setWorkInteractionPost(p);
                                 }}
                                 className="absolute top-3 right-3 z-20 px-2.5 py-1 rounded-lg bg-black/55 border border-white/10 text-[10px] font-bold text-amber-400 hover:bg-black/75"
                               >
                                 互动
                               </button>
                             )}
                          </div>
                       );
                       })}
                       {myPosts.length === 0 && <div className="col-span-full py-20 flex flex-col items-center justify-center opacity-30"><PlaySquare size={48}/><p className="text-xs font-black mt-4 uppercase">暂无作品</p></div>}
                       <div
                         onClick={() => {
                           if (profileBatchMode) return;
                           requireAuth(openPublishModalFresh);
                         }}
                         className={`aspect-[3/4] border-2 border-dashed border-white/10 rounded-[2.5rem] flex flex-col items-center justify-center text-white/20 transition-all ${
                           profileBatchMode ? 'opacity-30 pointer-events-none' : 'hover:text-amber-500 hover:border-amber-500/50 cursor-pointer'
                         }`}
                       >
                         <Plus size={40} />
                         <span className="text-[10px] font-black uppercase mt-4 tracking-widest">发布新动态</span>
                       </div>
                    </div>
                  )}

                  {/* 我的领养 */}
                  {profileTab === 'adopts' && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 animate-fade-in">
                       {myAdopts.map(p => (
                          <div
                            key={p.id}
                            role={profileBatchMode ? 'button' : undefined}
                            tabIndex={profileBatchMode ? 0 : undefined}
                            onClick={() => {
                              if (profileBatchMode) {
                                requireAuth(() => toggleProfileBatchId(p.id));
                                return;
                              }
                              requireAuth(() => openAdoptInHall(p, 'profile'));
                            }}
                            onKeyDown={(e) => {
                              if (!profileBatchMode || e.key !== 'Enter') return;
                              requireAuth(() => toggleProfileBatchId(p.id));
                            }}
                            className={`aspect-[3/4] rounded-[2rem] overflow-hidden relative group shadow-2xl border border-white/5 ${profileBatchMode ? 'cursor-pointer' : 'cursor-pointer hover:border-amber-500/30'}`}
                          >
                             {profileBatchMode && (
                               <button
                                 type="button"
                                 aria-label={profileBatchIds.includes(String(p.id)) ? '取消选择' : '选择'}
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   requireAuth(() => toggleProfileBatchId(p.id));
                                 }}
                                 className="absolute top-3 left-3 z-20 p-1.5 rounded-xl bg-black/60 border border-white/20 text-amber-400 hover:bg-black/80"
                               >
                                 {profileBatchIds.includes(String(p.id)) ? (
                                   <CheckSquare size={20} strokeWidth={2.5} />
                                 ) : (
                                   <Square size={20} className="text-white/50" strokeWidth={2} />
                                 )}
                               </button>
                             )}
                             {isPostVideoMedia(p) ? (
                               <video src={p.img} className="w-full h-full object-cover" muted playsInline loop autoPlay />
                             ) : (
                               <img src={p.img} className="w-full h-full object-cover" alt="adopt" />
                             )}
                             <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent pointer-events-none" />
                             <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-2">
                               <div className="text-[10px] font-black flex items-center gap-2 text-white/90">
                                 <span className="inline-flex items-center"><Heart size={12} className="mr-1 fill-white"/>{getWorkInteractionCounts(p).likes}</span>
                                 <span className="inline-flex items-center"><MessageCircle size={12} className="mr-1"/>{getWorkInteractionCounts(p).comments}</span>
                               </div>
                               {!profileBatchMode && (
                                 <button
                                   type="button"
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     setWorkInteractionPost(p);
                                   }}
                                   className="px-2.5 py-1 rounded-lg bg-black/55 border border-white/10 text-[10px] font-bold text-amber-400 hover:bg-black/75"
                                 >
                                   互动
                                 </button>
                               )}
                             </div>
                          </div>
                       ))}
                       {myAdopts.length === 0 && <div className="col-span-full py-20 flex flex-col items-center justify-center opacity-30"><Heart size={48}/><p className="text-xs font-black mt-4 uppercase">暂无领养内容</p></div>}
                    </div>
                  )}

                  {profileTab === 'drafts' && (
                    <DraftBoxPanel
                      drafts={publishDrafts}
                      batchMode={draftBatchMode}
                      selectedIds={draftBatchIds}
                      onToggleBatchMode={() => {
                        setDraftBatchMode((m) => {
                          if (m) setDraftBatchIds([]);
                          return !m;
                        });
                      }}
                      onToggleSelect={toggleDraftBatchId}
                      onToggleSelectAll={toggleDraftSelectAll}
                      onEdit={(draft) => requireAuth(() => openPublishForDraft(draft))}
                      onRename={handleRenameDraft}
                      onDeleteDraft={(id) => handleDeleteDrafts([id])}
                      onBatchDelete={runBatchDraftDelete}
                      onBatchPublish={() => requireAuth(runBatchDraftPublish)}
                      onCreateDraft={() => requireAuth(openPublishModalFresh)}
                      showToast={showToast}
                    />
                  )}

                  {/* 我喜欢的 */}
                  {profileTab === 'likes' && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 animate-fade-in">
                       {myLikes.map((p) => (
                          <ProfileWorkTile
                            key={p.id}
                            mediaUrl={resolvePostMediaUrl(p)}
                            isVideo={isPostVideoMedia(p)}
                            onClick={() => requireAuth(() => openPostInDiscover(p, 'profile'))}
                            footer={
                              <div className="absolute bottom-3 left-3 right-3 pointer-events-none">
                                <p className="text-[10px] font-bold text-white line-clamp-2">{p.title || p.content || '作品'}</p>
                              </div>
                            }
                          />
                       ))}
                       {myLikes.length === 0 && <div className="col-span-full py-20 flex flex-col items-center justify-center opacity-30"><Heart size={48}/><p className="text-xs font-black mt-4 uppercase">空空如也</p></div>}
                    </div>
                  )}

                  {/* 我的收藏 */}
                  {profileTab === 'bookmarks' && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 animate-fade-in">
                       {myBookmarks.map((p) => (
                          <ProfileWorkTile
                            key={p.id}
                            mediaUrl={resolvePostMediaUrl(p)}
                            isVideo={isPostVideoMedia(p)}
                            onClick={() => requireAuth(() => openPostInDiscover(p, 'profile'))}
                            footer={
                              <div className="absolute bottom-3 left-3 pointer-events-none">
                                <Bookmark size={14} className="fill-amber-500 text-amber-500" />
                              </div>
                            }
                          />
                       ))}
                       {myBookmarks.length === 0 && <div className="col-span-full py-20 flex flex-col items-center justify-center opacity-30"><Bookmark size={48}/><p className="text-xs font-black mt-4 uppercase">暂无收藏</p></div>}
                    </div>
                  )}

                  {/* 关注的人 */}
                  {profileTab === 'following' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-in">
                       {myFollowing.map(u => (
                          <div key={u.id} className="flex items-center justify-between bg-white/5 p-4 rounded-2xl border border-white/5 hover:bg-white/10 transition-colors">
                             <div className="flex items-center space-x-4">
                                <img src={u.avatar || DEFAULT_AVATAR} className="w-12 h-12 rounded-full border-2 border-white/10 object-cover shadow-xl" alt="avatar"/>
                                <div><p className="font-black text-sm flex items-center">{u.name} {u.role === 'super_admin' && <BadgeCheck size={14} className="text-amber-500 ml-1"/>}</p><p className="text-[10px] text-white/40 font-mono mt-1 bg-black/30 px-2 py-0.5 rounded-full w-fit">UID: {u.uid}</p></div>
                             </div>
                             <button onClick={(e) => handleFollow(u.id, e)} className="px-5 py-2.5 text-xs font-black rounded-full transition-all shadow-lg bg-white/10 text-white/50 border border-white/5">
                               已关注
                             </button>
                          </div>
                       ))}
                       {myFollowing.length === 0 && <div className="col-span-full py-20 flex flex-col items-center justify-center opacity-30"><User size={48}/><p className="text-xs font-black mt-4 uppercase tracking-widest">暂无关注</p></div>}
                    </div>
                  )}
                </div>
                </div>
             </div>
          </div>
        )}
      </main>

      {(currentView === 'home' || currentView === 'adopt') && feedReturnView && !isSearching && (
        <button
          type="button"
          onClick={returnFromDiscoverFeed}
          className="fixed top-[4.5rem] md:top-20 left-3 md:left-6 z-[5500] flex items-center gap-1.5 px-4 py-2 rounded-full bg-black/60 border border-white/20 text-sm font-black text-white hover:bg-black/80 backdrop-blur-md shadow-lg"
        >
          <ChevronLeft size={18} /> 返回
        </button>
      )}

      <SiteLegalFooter className="fixed bottom-14 left-0 right-0 z-[25] md:static md:z-auto" />

      <MobileBottomNav
        currentView={currentView}
        isLoggedIn={isLoggedIn}
        notificationCount={notifications.length}
        onNavigate={navigateTo}
        onPublish={() => requireAuth(openPublishModalFresh)}
        onMessages={() => requireAuth(openEmailModal)}
        onProfile={() => {
          if (!isLoggedIn) {
            openAuthModal();
            return;
          }
          setViewingUser(null);
          navigateTo('profile');
        }}
      />
      </>
      )}

      {showMobileUserMenu && isLoggedIn && userProfile && (
        <MobileUserSheet
          userProfile={userProfile}
          onClose={() => setShowMobileUserMenu(false)}
          onProfile={() => { setViewingUser(null); navigateTo('profile'); }}
          onFeedback={openFeedbackModal}
          onAdmin={() => { openAdminConsole(); }}
          onConvention={openPlatformConvention}
          onLogout={() => {
            setShowMobileUserMenu(false);
            performLogout();
          }}
        />
      )}

      {/* 弹窗组件挂载 */}
      {adoptChatPet && (
        <AdoptChatModal
          pet={adoptChatPet}
          userProfile={userProfile}
          messages={adoptChatMessages}
          threadId={adoptChatThreadId}
          threadChoices={adoptChatThreadChoices}
          onClose={closeAdoptChat}
          onSend={handleSendAdoptMessage}
          onPickThread={handlePickAdoptThread}
        />
      )}
      {pendingDeletePost && (
        <div className="fixed inset-0 z-[4200] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-zinc-900 border border-white/10 rounded-[2rem] w-full max-w-sm p-8 shadow-2xl text-center">
            <h3 className="text-xl font-black text-red-500 mb-2">删除作品</h3>
            <p className="text-sm text-white/60 mb-6 leading-relaxed">
              删除后其他用户将无法看到。可选择将内容存入草稿箱（可命名、批量管理），下次在「我的 → 草稿箱」继续编辑或发布。
            </p>
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={() => commitDeletePost(pendingDeletePost, true)}
                className="w-full py-3 rounded-xl font-black bg-amber-500 text-black hover:bg-amber-400 transition-colors"
              >
                删除并存入草稿箱
              </button>
              <button
                type="button"
                onClick={() => commitDeletePost(pendingDeletePost, false)}
                className="w-full py-3 rounded-xl font-black bg-red-500/15 text-red-400 border border-red-500/40 hover:bg-red-500 hover:text-white transition-colors"
              >
                删除且不留草稿
              </button>
              <button
                type="button"
                onClick={() => setPendingDeletePost(null)}
                className="w-full py-3 rounded-xl font-bold text-white/50 hover:text-white bg-white/5 transition-colors"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
      {showAuthModal && (
        <AuthModal 
          onAuth={handleCloudAuth} 
          onClose={() => setShowAuthModal(false)} 
          onShowPrivacy={openPrivacyPolicy} 
          onShowAgreement={openUserAgreement} 
          onShowConvention={openPlatformConvention}
          showToast={showToast}
          adminLoginOnly={maintenanceActive}
        />
      )}
      {showPublishModal && (
        <PublishModal 
          onPublish={handlePublish} 
          onClose={() => {
            setShowPublishModal(false);
            setPublishEditingDraftId(null);
          }}
          showToast={showToast} 
          userProfile={userProfile}
          editingDraftId={publishEditingDraftId}
          onSaveDraft={persistPublishDraft}
          onDeleteDraft={async (id) => removePublishDrafts([id])}
          onDraftSaved={() => refreshPublishDraftsFromCloud(userProfile?.phone)}
          onDraftDeleted={() => refreshPublishDraftsFromCloud(userProfile?.phone)}
        />
      )}
      {showReportModal && (
        <ReportModal 
          targetInfo={reportTarget} 
          onSubmit={handleReportSubmit} 
          onClose={() => setShowReportModal(false)} 
        />
      )}
      {showFeedbackModal && (
        <FeedbackModal 
          onSubmit={handleFeedbackSubmit} 
          onClose={() => setShowFeedbackModal(false)} 
        />
      )}

      {showEmailModal && (
        <EmailNotificationModal 
          notifications={notifications} 
          announcements={publicAnnouncements}
          userProfile={userProfile}
          onClose={() => setShowEmailModal(false)} 
          showToast={showToast}
        />
      )}
      {showSettingsModal && (
        <SettingsModal 
          userProfile={userProfile} 
          onClose={() => setShowSettingsModal(false)} 
          onUpdateProfile={handleUpdateProfile}
          onChangePassword={handleChangePassword}
          onSendSmsCode={handleSendSmsCode}
          onToggleLocationAutoDetect={handleToggleLocationAutoDetect}
          onShowPrivacy={openPrivacyPolicy}
          onShowAgreement={openUserAgreement}
          onShowConvention={openPlatformConvention}
          showToast={showToast}
        />
      )}
      {workInteractionPost && (
        <WorkInteractionsModal
          post={workInteractionPost}
          usersList={usersList}
          onClose={() => setWorkInteractionPost(null)}
          onViewUser={openUserByProfile}
        />
      )}
      {showCommentModal && selectedPost && (
        <CommentModal 
          post={selectedPost} 
          onClose={() => setShowCommentModal(false)}
          onAddComment={handleAddComment}
          showToast={showToast}
          userProfile={userProfile}
        />
      )}
      {showPrivacyPolicy && (
        <LegalDocument 
          title="予归隐私保护政策" 
          content={privacyPolicyContent} 
          onClose={() => setShowPrivacyPolicy(false)} 
        />
      )}
      {showUserAgreement && (
        <LegalDocument 
          title="予归用户服务与免责协议" 
          content={userAgreementContent} 
          onClose={() => setShowUserAgreement(false)} 
        />
      )}
      {showPlatformConvention && !showSplash && (
        <LegalDocument 
          title="予归平台公约" 
          content={platformConventionContent} 
          onClose={() => setShowPlatformConvention(false)} 
        />
      )}
      {showConventionAcceptModal && isLoggedIn && (
        <PlatformConventionAcceptModal onAccept={handleConventionAccept} />
      )}
      {confirmAction && (
        <ConfirmModal 
          {...confirmAction} 
          onCancel={() => setConfirmAction(null)} 
        />
      )}

      {/* Toast提示 */}
      {toastMsg && (
        <div
          role="alert"
          className={`fixed top-16 md:top-10 left-1/2 -translate-x-1/2 z-[9000] px-6 md:px-10 py-3 md:py-4 rounded-full shadow-2xl font-black text-sm animate-slide-up flex items-center border-4 max-w-[92vw] ${
            toastType === 'error'
              ? 'bg-red-500 text-white border-red-900/20'
              : toastType === 'warning'
                ? 'bg-zinc-800 text-amber-400 border-amber-500/30'
                : 'bg-amber-500 text-black border-black/10'
          }`}
        >
          <CheckCircle2 size={18} className="mr-2 md:mr-3 flex-shrink-0" />
          <span className="truncate">{toastMsg}</span>
        </div>
      )}
      {/* 全局样式 */}
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.4s ease-out forwards; }
        @keyframes slide-up { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        .animate-pulse { animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
        .feed-viewport { height: calc(100dvh - 4rem); overflow-y: auto; overscroll-behavior: contain; }
        .feed-slide { height: calc(100dvh - 4rem); min-height: calc(100dvh - 4rem); }
        @media (max-width: 767px) {
          .feed-viewport { height: calc(100dvh - 3.5rem - 3.75rem - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)); }
          .feed-slide { height: calc(100dvh - 3.5rem - 3.75rem - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)); min-height: calc(100dvh - 3.5rem - 3.75rem - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)); }
        }
      `}</style>
    </div>
  );
};

export default App;